
import React, { useEffect, useMemo, useState, useRef } from "react";
import { db, auth, storage } from "../../firebase";
import { validateRutUY } from "../../utils/validateRutUY";
import { formatCurrencyUY, formatCurrencyUSD } from "../../utils/formatters";
import { computeExpenseTotals, tieneDescuento } from "../../utils/expenseCalc";
import { colors, shadows, radius, spacing, typography, transitions, cardBase } from "../../utils/designTokens";
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  doc,
  deleteDoc,
  serverTimestamp,
  orderBy,
  writeBatch,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useBuilding } from "../../context/BuildingContext";
import { tolerantId } from "../../utils/firestoreHelpers";
import {
  CONTADOR_GASTOS,
  CONTADOR_PAGOS,
  reservarIdSecuencial,
  mensajeErrorNumeracion,
} from "../../utils/contadoresFinanzas";
import { getAuthHeaders } from "../../utils/habiApi";
import { devLog } from "../../utils/devLog";
import { isDemoUser, DEMO_BLOCK_MESSAGE } from "../../utils/demoMode";
import { useToast } from "../../components/Toast";
import { useAppDialog } from "../../components/AppDialog";
import { FaPlus, FaCreditCard, FaUpload, FaTimes, FaBuilding, FaCalendarAlt, FaChartBar, FaClock, FaCheckCircle, FaExclamationCircle, FaRobot, FaSpinner, FaFileInvoice, FaCloudUploadAlt, FaMoneyBillWave, FaEye, FaEllipsisV, FaSort, FaSortUp, FaSortDown, FaTrash } from "react-icons/fa";
import ScreenHeader from "../../components/ScreenHeader";
import AdminModal from "../../components/AdminModal";
import FormField from "../../components/FormField";
import { createSupplierForBuilding } from "../../utils/supplierCreate";
import StatusBadge from "../../components/StatusBadge";
import { getPreviousDayRate } from "../../utils/exchangeRates";
import { METODOS_PAGO_GASTO, formPagoDesde, validarPagoEditado, cambiosDelPago, actualizacionesDelPago } from "../../utils/pagoGastoEdicion";
import { MAX_DETALLE_CORTO, detalleCorto, detalleCortoParaGuardar } from "../../utils/detalleGasto";
import { filtrarGastosDelMes } from "../../utils/gastosDelMes";
import { useJsApiLoader } from "@react-google-maps/api";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
const libraries = ["places"];

// Devuelve los totales netos (subtotal/iva/amount con el descuento aplicado) para un
// form/expense que tenga subtotal (bruto), iva (bruto) y descuentoTipo/Valor. Si no hay
// descuento devuelve null. Se usa para MOSTRAR el neto en los campos Subtotal/IVA/Total.
const descNetos = (f) =>
  f && tieneDescuento(f.descuentoTipo, f.descuentoValor)
    ? computeExpenseTotals({
        subtotalBruto: f.subtotal,
        ivaBruto: f.iva,
        descuentoTipo: f.descuentoTipo,
        descuentoValor: f.descuentoValor,
      })
    : null;

// URL del backend PropIA para procesamiento con IA (facturas, proveedores)
// Desarrollo local: REACT_APP_PROPIA_API_URL=http://localhost:8000 en .env.local
const Habi_API_URL = (() => {
  if (process.env.REACT_APP_PROPIA_API_URL) {
    return process.env.REACT_APP_PROPIA_API_URL.replace(/\/$/, "");
  }
  const wsUrl = process.env.REACT_APP_PROPIA_WS_URL || "wss://propia-agent-241529496588.us-central1.run.app";
  return wsUrl.replace("wss://", "https://").replace("ws://", "http://").replace("/ws", "");
})();

const PagosEdificio = ({ setSelected }) => {
  const { buildings, selectedBuilding, setSelectedBuilding } = useBuilding();
  const { showToast } = useToast();
  const { confirmDialog } = useAppDialog();
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
  const [monthFilter, setMonthFilter] = useState(new Date().getMonth() + 1);
  
  // Cargar Google Maps Places para autocomplete de direcciones
  const { isLoaded: isGoogleMapsLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
  });
  
  // Usar el filtro global del contexto
  const buildingFilter = selectedBuilding?.buildingid || (buildings && buildings.length === 1 ? buildings[0].buildingid : null);
  const buildingId = selectedBuilding?.buildingid;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  // Alta rápida de proveedor desde los selectores ("+ Crear proveedor nuevo").
  // target: a qué formulario se asigna el proveedor creado.
  const [newSupplier, setNewSupplier] = useState(null); // { target, name, rut, type, telephone }
  const [creatingQuickSupplier, setCreatingQuickSupplier] = useState(false);
  const NEW_SUPPLIER = "__nuevo_proveedor__";
  const [form, setForm] = useState({
    // Gastos form fields
    supplierid: "",
    receiptNumber: "",
    description: "",
    maturity: "",
    invoiceDate: "", // Fecha Factura
    money: "peso", // peso | dolar
    subtotal: "",
    iva: "",
    amount: "",
    descuentoTipo: "", // "" | "porcentaje" | "monto"
    descuentoValor: "",
    bill: null, // file
    criteria: "", // Campo para criterio
    dividePorSuperficie: false, // Divide por superficie (legacy)
    documentType: "factura", // factura | recibo | ticket | otro
    expenseCategory: "gastos_comunes", // gastos_comunes | obra | incidencia
    projectId: "", // proyecto asociado (para categoría obra)
    incidentId: "", // incidencia asociada (para categoría incidencia)
    overrideDistribution: "", // "" = default edificio | partes_iguales | alicuotas | superficie
    sourceFund: "", // Fondo del que se debita el gasto (solo para obra/incidencia): "caja" | "reserva" | customFundId
  });
  const [projects, setProjects] = useState([]); // for obra category
  // customFunds del edificio actual. Cargado directo de Firestore (fresco) para no depender del cache del context.
  const [buildingCustomFunds, setBuildingCustomFunds] = useState([]);
  
  // Additional states for expenses form
  const [suppliers, setSuppliers] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [criteriaExpenses, setCriteriaExpenses] = useState([]);

  // Payment modal states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    method: "Transferencia", // Transferencia | Tarjeta | Efectivo
    proof: null, // file
    paymentDate: new Date().toISOString().split('T')[0], // YYYY-MM-DD, editable por el usuario
  });
  const [savingPayment, setSavingPayment] = useState(false);

  // Detail modal states
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedDetailExpense, setSelectedDetailExpense] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [paymentDetail, setPaymentDetail] = useState(null); // Pago asociado cuando el gasto está pagado
  const [paymentDetailEstado, setPaymentDetailEstado] = useState("idle"); // idle | cargando | listo | sin_registro | error
  const [pagoEdit, setPagoEdit] = useState(null); // Datos del pago editables (fecha, método, monto, comprobante nuevo)

  // Filtros de tabla
  const [viewMode, setViewMode] = useState("active"); // active | deleted
  const [statusFilter, setStatusFilter] = useState("all"); // all | paid | pending
  // Fondo que paga el gasto: "todos" | "caja" | "reserva" | id de fondo propio.
  const [fundFilter, setFundFilter] = useState("todos");
  const [columnFilters, setColumnFilters] = useState({}); // { [columnKey]: string }
  const [openMenuColumn, setOpenMenuColumn] = useState(null);
  const [openFilterColumn, setOpenFilterColumn] = useState(null);
  const [hoveredColumn, setHoveredColumn] = useState(null);
  const [sortKey, setSortKey] = useState("paidDate");
  const [sortDirection, setSortDirection] = useState("desc"); // asc | desc

  // IA Invoice Processing states
  const [aiProcessing, setAIProcessing] = useState(false);
  const [aiFile, setAIFile] = useState(null);
  const [aiPreviewUrl, setAIPreviewUrl] = useState(null);
  const [aiResult, setAIResult] = useState(null);
  const [aiError, setAIError] = useState(null);
  const [aiErrorType, setAIErrorType] = useState("error"); // "error" | "warning"
  const aiFileInputRef = useRef(null);
  const aiFileInputBatchRef = useRef(null);
  // Batch mode: varias facturas
  const [aiFilesBatch, setAIFilesBatch] = useState([]);
  const [aiResultsBatch, setAIResultsBatch] = useState([]); // { file, result, pendingData }[]
  const [currentBatchIndex, setCurrentBatchIndex] = useState(0);
  const [batchProcessingProgress, setBatchProcessingProgress] = useState(null); // { current, total }
  
  // Modal de decisión de proveedor
  const [showSupplierDecisionModal, setShowSupplierDecisionModal] = useState(false);
  const [pendingInvoiceData, setPendingInvoiceData] = useState(null);
  const [creatingSupplier, setCreatingSupplier] = useState(false);
  const [showSupplierToClient, setShowSupplierToClient] = useState(true);
  
  // Niveles de confianza de campos extraídos por IA
  const [fieldConfidence, setFieldConfidence] = useState({});
  
  // Ref para autocomplete de dirección del proveedor
  const supplierAddressInputRef = useRef(null);
  const supplierAddressAutocompleteRef = useRef(null);

  // Cerrar menú/filtro al hacer clic fuera
  useEffect(() => {
    const handleClick = (e) => {
      if (!openMenuColumn && !openFilterColumn) return;
      const inHeader = e.target.closest("[data-column-header]");
      const inMenu = e.target.closest("[data-column-menu]");
      const inPopover = e.target.closest("[data-filter-popover]");
      if (!inHeader && !inMenu && !inPopover) {
        setOpenMenuColumn(null);
        setOpenFilterColumn(null);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [openMenuColumn, openFilterColumn]);

  // Inicializar autocomplete de Google Places para dirección del proveedor
  useEffect(() => {
    let timeoutId;
    
    const initAutocomplete = () => {
      if (!supplierAddressInputRef.current || !window.google?.maps?.places) {
        devLog("Autocomplete no disponible aún:", { 
          hasRef: !!supplierAddressInputRef.current, 
          hasGoogle: !!window.google?.maps?.places 
        });
        return;
      }
      
      // Limpiar autocomplete anterior si existe
      if (supplierAddressAutocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(supplierAddressAutocompleteRef.current);
        supplierAddressAutocompleteRef.current = null;
      }
      
      try {
        const autocomplete = new window.google.maps.places.Autocomplete(supplierAddressInputRef.current, {
          types: ["address"],
          componentRestrictions: { country: ["uy", "ar", "br", "cl", "py"] },
        });
        
        autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();
          if (place.geometry) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            const formattedAddress = place.formatted_address || supplierAddressInputRef.current.value;
            
            setPendingInvoiceData(prev => prev ? {
              ...prev,
              invoiceData: {
                ...prev.invoiceData,
                supplier_address: formattedAddress,
                supplier_lat: lat,
                supplier_lng: lng
              }
            } : null);
          }
        });
        
        supplierAddressAutocompleteRef.current = autocomplete;
        console.log("Autocomplete de dirección inicializado correctamente");
      } catch (error) {
        console.error("Error inicializando autocomplete:", error);
      }
    };
    
    if (showSupplierDecisionModal && isGoogleMapsLoaded) {
      // Pequeño delay para asegurar que el DOM esté listo
      timeoutId = setTimeout(initAutocomplete, 300);
    }
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (supplierAddressAutocompleteRef.current) {
        try {
          window.google?.maps?.event?.clearInstanceListeners?.(supplierAddressAutocompleteRef.current);
        } catch (_) {}
        supplierAddressAutocompleteRef.current = null;
      }
    };
  }, [showSupplierDecisionModal, isGoogleMapsLoaded]);

  // Geocodificar dirección del proveedor cuando el modal se muestra con datos sin lat/lng
  useEffect(() => {
    if (!showSupplierDecisionModal || !pendingInvoiceData) return;
    const addr = pendingInvoiceData.invoiceData?.supplier_address?.trim();
    if (!addr || !window.google?.maps) return;
    if (pendingInvoiceData.invoiceData?.supplier_lat != null && pendingInvoiceData.invoiceData?.supplier_lng != null) return;

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: addr + ", Uruguay" }, (results, status) => {
      if (status === "OK" && results?.[0]) {
        const location = results[0].geometry.location;
        setPendingInvoiceData(prev => prev ? {
          ...prev,
          invoiceData: {
            ...prev.invoiceData,
            supplier_lat: location.lat(),
            supplier_lng: location.lng()
          }
        } : null);
      }
    });
  }, [showSupplierDecisionModal, pendingInvoiceData, isGoogleMapsLoaded]);
  
  // Función para obtener color según nivel de confianza
  const getConfidenceColor = (fieldName) => {
    const confidence = fieldConfidence[fieldName];
    if (confidence === undefined || confidence === null) return null;
    if (confidence >= 0.85) return colors.success; // Verde - alta confianza
    if (confidence >= 0.6) return colors.warning; // Amarillo - media confianza
    return colors.error; // Rojo - baja confianza
  };
  
  // Función para obtener estilo del input con confianza
  const getInputStyleWithConfidence = (fieldName, baseStyle = styles.input) => {
    const color = getConfidenceColor(fieldName);
    if (!color) return baseStyle;
    return {
      ...baseStyle,
      borderColor: color,
      borderWidth: '2px',
      boxShadow: `0 0 0 1px ${color}20`
    };
  };

  // BUG-61 fix: Agregar catch block al fetchPagos
  const fetchPagos = async () => {
    try {
      setLoading(true);
      const coll = viewMode === "deleted" ? "expenses_deleted" : "expenses";
      const fetchForBuilding = async (bId) => {
        const q = query(collection(db, coll), where("buildingid", "==", Number(bId)));
        const snap = await getDocs(q);
        return snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          type: "expense",
          isDeleted: viewMode === "deleted"
        }));
      };

      let gathered = [];
      if (buildingFilter) {
        gathered = await fetchForBuilding(buildingFilter);
      }

      // Gastos del mes por FECHA DE PAGO (es lo que define en qué gastos comunes
      // entran) y los pendientes siempre, hasta que se paguen. En eliminados,
      // por fecha de eliminación. Ver utils/gastosDelMes.js.
      const filteredItems = filtrarGastosDelMes(gathered, {
        year: yearFilter,
        month: monthFilter,
        diaCorte: selectedBuilding?.diaCorte ?? null,
        eliminados: viewMode === "deleted",
      });

      // Pendientes por vencimiento (el más urgente primero); pagados por fecha de pago.
      filteredItems.sort((a, b) => {
        if (!a.paid && !b.paid) return (toDateStr(a.maturity) || "9999").localeCompare(toDateStr(b.maturity) || "9999");
        return (toDateStr(b.paidDate) || "").localeCompare(toDateStr(a.paidDate) || "");
      });
      setItems(filteredItems);
    } catch (error) {
      console.error("Error al cargar gastos:", error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    if (viewMode === "deleted") {
      setSortKey("deletedAt");
      setSortDirection("desc");
    } else {
      setSortKey("paidDate");
      setSortDirection("desc");
    }
  }, [viewMode]);

  useEffect(() => {
    if (!buildings || buildings.length === 0) return;
    if (!buildingFilter) return;
    fetchPagos();
    fetchSuppliers();
    fetchIncidents();
    fetchProjects();
    fetchCriteriaExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBuilding, buildings, yearFilter, monthFilter, viewMode]);

  // Cargar customFunds frescos del edificio. El context a veces trae un snapshot cacheado,
  // así que leemos directo el doc cuando cambia el edificio seleccionado.
  useEffect(() => {
    const loadFunds = async () => {
      if (!selectedBuilding?.id) { setBuildingCustomFunds([]); return; }
      try {
        const snap = await getDoc(doc(db, "building", selectedBuilding.id));
        const data = snap.data() || {};
        setBuildingCustomFunds(Array.isArray(data.customFunds) ? data.customFunds : []);
      } catch (err) {
        console.error("Error cargando customFunds del edificio:", err);
        setBuildingCustomFunds(Array.isArray(selectedBuilding?.customFunds) ? selectedBuilding.customFunds : []);
      }
    };
    loadFunds();
  }, [selectedBuilding?.id]);

  // Obtener el pago asociado cuando se muestra el detalle de un gasto pagado.
  // La consulta fija el edificio del gasto (reglas 0D), y eso además separa los
  // expensesid repetidos entre edificios de antes del contador atómico.
  const detailExpenseBid = selectedDetailExpense?.buildingid ?? buildingFilter;
  useEffect(() => {
    if (!showDetailModal || !selectedDetailExpense?.paid || !selectedDetailExpense?.expensesid || detailExpenseBid == null) {
      setPaymentDetail(null);
      setPagoEdit(null);
      setPaymentDetailEstado("idle");
      return;
    }
    const fetchPayment = async () => {
      setPaymentDetailEstado("cargando");
      try {
        const q = query(
          collection(db, "payments"),
          where("expensesid", "==", selectedDetailExpense.expensesid),
          where("buildingid", "in", tolerantId(detailExpenseBid))
        );
        const snap = await getDocs(q);
        if (!snap.empty) {
          const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          docs.sort((a, b) => {
            const ta = a.createdAt?.toDate?.()?.getTime?.() || 0;
            const tb = b.createdAt?.toDate?.()?.getTime?.() || 0;
            return tb - ta;
          });
          setPaymentDetail(docs[0]);
          // El formulario del pago arranca con lo guardado.
          setPagoEdit(formPagoDesde(docs[0]));
          setPaymentDetailEstado("listo");
        } else {
          setPaymentDetail(null);
          setPagoEdit(null);
          setPaymentDetailEstado("sin_registro");
        }
      } catch (e) {
        console.error("Error fetching payment:", e);
        setPaymentDetail(null);
        setPagoEdit(null);
        setPaymentDetailEstado("error");
      }
    };
    fetchPayment();
  }, [showDetailModal, selectedDetailExpense?.paid, selectedDetailExpense?.expensesid, detailExpenseBid]);


  const handleCreateSupplier = async () => {
    if (!newSupplier) return;
    if (!newSupplier.name?.trim()) return showToast("Poné el nombre del proveedor.", "warning");
    setCreatingQuickSupplier(true);
    try {
      const created = await createSupplierForBuilding(newSupplier, buildingFilter);
      await fetchSuppliers();
      const id = String(created.supplierId);
      if (newSupplier.target === "form") setForm((f) => ({ ...f, supplierid: id }));
      else if (newSupplier.target === "edit") setEditingExpense((e) => ({ ...e, supplierid: id }));
      showToast(created.reused
        ? `Ese RUT ya existía: usamos "${created.name}" y quedó vinculado al edificio.`
        : `Proveedor "${created.name}" creado.`, "success");
      setNewSupplier(null);
    } catch (e) {
      showToast(e.message || "No se pudo crear el proveedor.", "error");
    } finally {
      setCreatingQuickSupplier(false);
    }
  };

  const fetchSuppliers = async () => {
    if (!buildingFilter) return;
    try {
      const q = query(
        collection(db, "supplierbuilding"),
        where("buildingId", "==", Number(buildingFilter))
      );
      const snap = await getDocs(q);
      const supplierIds = snap.docs.map(doc => doc.data().supplierId);
      
      if (supplierIds.length > 0) {
        const supplierQuery = query(
          collection(db, "supplier"),
          where("supplierId", "in", supplierIds)
        );
        const supplierSnap = await getDocs(supplierQuery);
        setSuppliers(supplierSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      }
    } catch (error) {
      console.error("Error fetching suppliers:", error);
    }
  };

  const fetchIncidents = async () => {
    if (!buildingFilter) return;
    try {
      const q = query(
        collection(db, "incidents"),
        where("buildingid", "==", Number(buildingFilter)),
        where("type", "==", "Edificio"),
        orderBy("date", "desc")
      );
      const snap = await getDocs(q);
      setIncidents(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error("Error fetching incidents:", error);
    }
  };

  const fetchProjects = async () => {
    if (!buildingFilter) return;
    try {
      const q = query(
        collection(db, "projects"),
        where("buildingid", "==", Number(buildingFilter))
      );
      const snap = await getDocs(q);
      setProjects(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(p => p.status !== "cancelado"));
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  };

  // expensesid y paymentid salen de los contadores atómicos counters/expenses_global
  // y counters/payments_global (contrato C1, utils/contadoresFinanzas): se reservan
  // al guardar, en la misma transacción que el alta. Antes se calculaban como el
  // máximo de TODA la colección, que las reglas 0D niegan (quedaban en 1).
  const crearGastoNumerado = async (payload) => {
    const nuevoRef = doc(collection(db, "expenses"));
    return reservarIdSecuencial(db, CONTADOR_GASTOS, (tx, expensesid) => {
      tx.set(nuevoRef, { ...payload, expensesid });
    });
  };

  const fetchCriteriaExpenses = async () => {
    // Cargar criterios del edificio (criteriabuilding) Y criterios globales (criteriaexpenses)
    try {
      const allCriteria = [];
      
      // Primero cargar criterios específicos del edificio (criteriabuilding)
      if (buildingFilter) {
        const criteriaBuildingQuery = query(
          collection(db, "criteriabuilding"),
          where("buildingid", "==", Number(buildingFilter))
        );
        const criteriaBuildingSnap = await getDocs(criteriaBuildingQuery);
        criteriaBuildingSnap.docs.forEach(doc => {
          allCriteria.push({ id: doc.id, ...doc.data() });
        });
      }
      
      // También cargar criterios globales de criteriaexpenses
      const criteriaExpensesQuery = query(collection(db, "criteriaexpenses"));
      const criteriaExpensesSnap = await getDocs(criteriaExpensesQuery);
      criteriaExpensesSnap.docs.forEach(doc => {
        // Solo agregar si no existe ya (evitar duplicados)
        if (!allCriteria.find(c => c.id === doc.id)) {
          allCriteria.push({ id: doc.id, ...doc.data() });
        }
      });
      
      setCriteriaExpenses(allCriteria);
      return allCriteria;
    } catch (error) {
      console.error("Error fetching criteria:", error);
      return [];
    }
  };

  // Fondo de un gasto: mismo criterio que la columna "Fondo" y que el cierre
  // (isCommonExpense): manda sourceFund; sin él, obra → reserva, resto → caja.
  const fundKeyOf = (item) =>
    item.sourceFund || ((item.expenseCategory || "gastos_comunes") === "obra" ? "reserva" : "caja");

  const fundTabs = useMemo(() => {
    const nonDup = items.filter((p) => !p.isDuplicate);
    const stat = (key) => {
      const its = key === "todos" ? nonDup : nonDup.filter((p) => fundKeyOf(p) === key);
      return {
        count: its.length,
        pending: its.filter((p) => !p.paid).reduce((a, p) => a + Number(p.amount || 0), 0),
      };
    };
    const tabs = [
      { key: "todos", label: "Todos" },
      { key: "caja", label: "Caja (gastos comunes)" },
      { key: "reserva", label: "Fondo de Reserva" },
      ...buildingCustomFunds.filter((f) => f.active !== false).map((f) => ({ key: f.id, label: f.name || f.id })),
    ];
    return tabs.map((t) => ({ ...t, ...stat(t.key) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, buildingCustomFunds]);

  const totals = useMemo(() => {
    // Excluir duplicados del cálculo de totales; respeta el fondo elegido.
    const nonDuplicate = items.filter((p) => !p.isDuplicate && (fundFilter === "todos" || fundKeyOf(p) === fundFilter));
    const paid = nonDuplicate.filter((p) => p.paid).reduce((acc, p) => acc + Number(p.amount || 0), 0);
    const pending = nonDuplicate.filter((p) => !p.paid).reduce((acc, p) => acc + Number(p.amount || 0), 0);
    return { paid, pending, total: paid + pending };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, fundFilter]);
  
  // Detalle corto con IA para gastos que no lo tienen (los cargados antes).
  const [resumiendoDetalles, setResumiendoDetalles] = useState(false);
  const gastosSinDetalleCorto = useMemo(
    () => items.filter((g) => !g.isDeleted && !String(g.shortDescription || "").trim() && String(g.description || "").trim()),
    [items]
  );
  const resumirDetallesConIA = async () => {
    const lista = gastosSinDetalleCorto;
    if (!lista.length || !buildingFilter) return;
    if (await isDemoUser()) return showToast(DEMO_BLOCK_MESSAGE, "warning");
    const ok = await confirmDialog(
      `La IA va a escribir el detalle corto (qué es y de qué período) de ${lista.length === 1 ? "1 gasto" : `${lista.length} gastos`}. La descripción completa no se toca, y después podés corregir el detalle en cada gasto.`,
      { title: "Resumir detalles con IA", confirmLabel: "Resumir" }
    );
    if (!ok) return;
    setResumiendoDetalles(true);
    try {
      const authHeaders = await getAuthHeaders();
      const nombreProveedor = (g) => suppliers.find((sp) => String(sp.supplierId) === String(g.supplierid))?.name || "";
      let guardados = 0;
      for (let i = 0; i < lista.length; i += 50) {
        const lote = lista.slice(i, i + 50);
        const resp = await fetch(`${Habi_API_URL}/api/expense-short-descriptions`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders },
          body: JSON.stringify({
            building_id: String(buildingFilter),
            items: lote.map((g) => ({ id: g.id, description: String(g.description || ""), supplier_name: nombreProveedor(g) })),
          }),
        });
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          throw new Error(err.detail || `Habi respondió ${resp.status}`);
        }
        const { items: resumenes = [] } = await resp.json();
        const validos = resumenes.filter((r) => lote.some((g) => g.id === r.id) && detalleCortoParaGuardar(r.short));
        if (validos.length) {
          const batch = writeBatch(db);
          validos.forEach((r) => batch.update(doc(db, "expenses", r.id), { shortDescription: detalleCortoParaGuardar(r.short), shortDescriptionOrigen: "ia" }));
          await batch.commit();
          guardados += validos.length;
        }
      }
      await addDoc(collection(db, "audit"), {
        action: "expense_short_descriptions_ai",
        buildingId: Number(buildingFilter),
        count: guardados,
        createdBy: auth.currentUser?.email || "desconocido",
        createdAt: serverTimestamp(),
      });
      fetchPagos();
      showToast(guardados === lista.length ? `Listo: ${guardados} gastos con detalle corto.` : `Se resumieron ${guardados} de ${lista.length} gastos.`, guardados ? "success" : "warning");
    } catch (e) {
      console.error("Error resumiendo detalles:", e);
      showToast(`No se pudieron resumir los detalles: ${e.message}`, "error");
    } finally {
      setResumiendoDetalles(false);
    }
  };

  // Definición de columnas (filtrado + ordenamiento) - títulos abreviados para caber
  const TABLE_COLUMNS = useMemo(() => [
    { key: "receiptNumber", label: "Nº Fact.", sortType: "alpha", getValue: (item) => String(item.receiptNumber || ""), getSortValue: (item) => String(item.receiptNumber || "").toLowerCase() },
    { key: "supplier", label: "Proveed.", sortType: "alpha", getValue: (item) => suppliers.find(s => String(s.supplierId) === String(item.supplierid))?.name || "", getSortValue: (item) => (suppliers.find(s => String(s.supplierId) === String(item.supplierid))?.name || "").toLowerCase() },
    { key: "money", label: "", sortType: "alpha", getValue: (item) => {
      if (item.convertedToUYU) return "$";
      return item.money === "dolar" ? "US$" : "$";
    }, getSortValue: (item) => (item.money === "peso" ? "$" : item.money === "dolar" ? "US$" : item.money || "").toLowerCase() },
    { key: "amount", label: "Monto", sortType: "numeric", getValue: (item) => {
      return Number(item.amount || 0).toLocaleString("es-UY", { maximumFractionDigits: 0 });
    }, getSortValue: (item) => Number(item.amount || 0) },
    { key: "invoiceDate", label: "F. factura", sortType: "date", getValue: (item) => toDateStr(item.invoiceDate) || "", getSortValue: (item) => toTimestamp(item.invoiceDate) },
    { key: "maturity", label: "Vencim.", sortType: "date", getValue: (item) => toDateStr(item.maturity) || "", getSortValue: (item) => toTimestamp(item.maturity) },
    // La fecha de pago define en qué mes de gastos comunes entra el gasto.
    { key: "paidDate", label: "F. pago", sortType: "date", getValue: (item) => (item.paid ? toDateStr(item.paidDate) || "" : "—"), getSortValue: (item) => (item.paid ? toTimestamp(item.paidDate) : 0) },
    { key: "fondo", label: "Fondo", sortType: "alpha", getValue: (item) => {
      // El fondo mostrado respeta sourceFund cuando aplica (obra o incidencia).
      // Si no hay sourceFund, se usa el default legacy: obra → Fondo Reserva, resto → Gastos Comunes.
      const src = item.sourceFund;
      if (src === "caja") return "Gastos Comunes";
      if (src === "reserva") return "Fondo Reserva";
      if (src) {
        const fund = buildingCustomFunds.find(f => f.id === src);
        return fund ? (fund.name || fund.id) : src;
      }
      const cat = item.expenseCategory || "gastos_comunes";
      if (cat === "obra") return "Fondo Reserva";
      return "Gastos Comunes";
    }, getSortValue: (item) => {
      const src = item.sourceFund;
      if (src === "caja") return "gastos comunes";
      if (src === "reserva") return "fondo reserva";
      if (src) {
        const fund = buildingCustomFunds.find(f => f.id === src);
        return (fund?.name || src).toLowerCase();
      }
      const cat = item.expenseCategory || "gastos_comunes";
      return cat === "obra" ? "fondo reserva" : "gastos comunes";
    } },
    { key: "status", label: "Estado", sortType: "alpha", getValue: (item) => item.isDeleted ? "Eliminado" : item.isDuplicate ? "Duplicado" : item.paid ? "Pagado" : "Pendiente", getSortValue: (item) => item.isDeleted ? 3 : item.isDuplicate ? 2 : item.paid ? 1 : 0 },
    { key: "incidents", label: "Incid.", sortType: "alpha", getValue: (item) => {
      const isIncidencia = item.expenseCategory === "incidencia" || item.hasIncident;
      if (!isIncidencia || !item.incidents) return "";
      const inc = incidents.find(i => i.id === item.incidents);
      return inc ? `#${inc.incidentid || inc.id}` : "";
    }, getSortValue: (item) => {
      const isIncidencia = item.expenseCategory === "incidencia" || item.hasIncident;
      if (!isIncidencia || !item.incidents) return "";
      const inc = incidents.find(i => i.id === item.incidents);
      return (inc ? `#${inc.incidentid || inc.id}` : "").toLowerCase();
    }},
    { key: "description", label: "Detalle", sortType: "alpha", getValue: (item) => detalleCorto(item), getFilterValue: (item) => `${item.shortDescription || ""} ${item.description || ""}`, getSortValue: (item) => detalleCorto(item).toLowerCase() },
  ], [suppliers, incidents, buildingCustomFunds]);

  const TABLE_COLUMNS_DELETED = useMemo(() => [
    { key: "receiptNumber", label: "Nº Fact.", sortType: "alpha", getValue: (item) => String(item.receiptNumber || ""), getSortValue: (item) => String(item.receiptNumber || "").toLowerCase() },
    { key: "supplier", label: "Proveed.", sortType: "alpha", getValue: (item) => suppliers.find(s => String(s.supplierId) === String(item.supplierid))?.name || "-", getSortValue: (item) => (suppliers.find(s => String(s.supplierId) === String(item.supplierid))?.name || "").toLowerCase() },
    { key: "amount", label: "Monto", sortType: "numeric", getValue: (item) => {
      return Number(item.amount || 0).toLocaleString("es-UY", { maximumFractionDigits: 0 });
    }, getSortValue: (item) => Number(item.amount || 0) },
    { key: "deletedBy", label: "Admin", sortType: "alpha", getValue: (item) => {
      const email = String(item.deletedBy || "-");
      if (email.length <= 24) return email;
      const at = email.indexOf("@");
      if (at > 0) return email.slice(0, 10) + "…" + email.slice(at);
      return email.slice(0, 21) + "…";
    }, getSortValue: (item) => String(item.deletedBy || "").toLowerCase() },
    { key: "deletedAt", label: "Fecha", sortType: "date", getValue: (item) => toDateStr(item.deletedAt) || "-", getSortValue: (item) => toTimestamp(item.deletedAt) },
    { key: "status", label: "Estado", sortType: "alpha", getValue: () => "Eliminado", getSortValue: () => "eliminado" },
    { key: "description", label: "Detalle", sortType: "alpha", getValue: (item) => detalleCorto(item), getFilterValue: (item) => `${item.shortDescription || ""} ${item.description || ""}`, getSortValue: (item) => detalleCorto(item).toLowerCase() },
  ], [suppliers]);

  const activeColumns = viewMode === "deleted" ? TABLE_COLUMNS_DELETED : TABLE_COLUMNS;

  const filteredItems = useMemo(() => {
    return items.filter(item => {
      if (fundFilter !== "todos" && fundKeyOf(item) !== fundFilter) return false;
      if (viewMode === "active") {
        if (statusFilter === "paid" && !item.paid) return false;
        if (statusFilter === "pending" && item.paid) return false;
      }
      for (const col of activeColumns) {
        const filterVal = columnFilters[col.key];
        if (!filterVal || !filterVal.trim()) continue;
        // El filtro de Detalle busca también en la descripción completa.
        const cellVal = (col.getFilterValue || col.getValue)(item);
        if (!String(cellVal).toLowerCase().includes(filterVal.trim().toLowerCase())) return false;
      }
      return true;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, statusFilter, fundFilter, columnFilters, activeColumns, viewMode]);

  const sortedFilteredItems = useMemo(() => {
    const col = activeColumns.find(c => c.key === sortKey);
    const dir = sortDirection === "asc" ? 1 : -1;
    return [...filteredItems].sort((a, b) => {
      if (viewMode === "active") {
        const pa = a.paid ? 1 : 0, pb = b.paid ? 1 : 0;
        if (pa !== pb) return pa - pb;
      }
      if (!col) return 0;
      const va = col.getSortValue(a);
      const vb = col.getSortValue(b);
      if (col.sortType === "numeric" || col.sortType === "date") {
        return (va - vb) * dir;
      }
      return String(va).localeCompare(String(vb), "es") * dir;
    });
  }, [filteredItems, sortKey, sortDirection, activeColumns, viewMode]);

  // ─── Helper: Detección de factura duplicada ───
  // Busca por Nº comprobante + RUT proveedor + edificio.
  // Retorna { isDuplicate: true } si existe y el usuario confirma cargarla,
  //         { isDuplicate: false } si no existe duplicada,
  //         null si el usuario cancela.
  const checkDuplicateInvoice = async (receiptNumber, supplierId, buildingId) => {
    if (!receiptNumber || !String(receiptNumber).trim()) return { isDuplicate: false };

    const trimmedReceipt = String(receiptNumber).trim();

    // Obtener el RUT del proveedor seleccionado
    const currentSupplier = suppliers.find(s => String(s.supplierId) === String(supplierId));
    const currentRut = currentSupplier?.rut ? String(currentSupplier.rut).replace(/[\s.-]/g, '').replace(/\D/g, '') : null;

    // Buscar todos los gastos del edificio con el mismo Nº comprobante
    const dupQuery = query(
      collection(db, "expenses"),
      where("buildingid", "==", Number(buildingId)),
      where("receiptNumber", "==", trimmedReceipt)
    );
    const dupSnap = await getDocs(dupQuery);
    const existingNonDeleted = dupSnap.docs.filter(d => !d.data().isDeleted);

    if (existingNonDeleted.length === 0) return { isDuplicate: false };

    // Verificar si alguna coincide por RUT del proveedor
    let matchByRut = false;
    for (const docSnap of existingNonDeleted) {
      const expData = docSnap.data();
      const expSupplier = suppliers.find(s => String(s.supplierId) === String(expData.supplierid));
      const expRut = expSupplier?.rut ? String(expSupplier.rut).replace(/[\s.-]/g, '').replace(/\D/g, '') : null;

      // Coincide si: mismo supplierid O mismo RUT (cuando ambos tienen RUT)
      if (String(expData.supplierid) === String(supplierId)) {
        matchByRut = true;
        break;
      }
      if (currentRut && expRut && currentRut === expRut) {
        matchByRut = true;
        break;
      }
    }

    if (!matchByRut) return { isDuplicate: false };

    const supplierName = currentSupplier?.name || "proveedor desconocido";
    const rutDisplay = currentSupplier?.rut || "sin RUT";
    const confirmar = await confirmDialog(
      `⚠️ FACTURA DUPLICADA DETECTADA\n\n` +
      `Ya existe una factura con el comprobante "${trimmedReceipt}" ` +
      `para ${supplierName} (RUT: ${rutDisplay}) en este edificio.\n\n` +
      `¿Deseas cargarla igualmente como DUPLICADA?\n` +
      `(No se contará en los totales de gastos ni en el cierre de mes)`,
      { title: "Factura duplicada", confirmLabel: "Cargar igualmente" }
    );

    if (!confirmar) return null; // Usuario canceló
    return { isDuplicate: true };
  };

  const saveExpense = async (forceDuplicate = false) => {
    // Validación básica
    if (await isDemoUser()) return showToast(DEMO_BLOCK_MESSAGE, "warning");
    if (!buildingFilter) return showToast("Selecciona un edificio primero.", "warning");
    // Proveedor y Nº comprobante son obligatorios solo para facturas
    if (form.documentType === "factura") {
      if (!form.supplierid) return showToast("Selecciona un proveedor.", "warning");
    }
    if (!form.description) return showToast("Completa la descripción.", "warning");
    if (!form.amount) return showToast("Completa el total.", "warning");
    // FIN-016: Validar monto positivo y finito
    const amountNum = Number(form.amount);
    if (amountNum <= 0 || !isFinite(amountNum)) return showToast("El monto debe ser un número positivo.", "warning");
    // El proyecto/obra es OPCIONAL: hay gastos de fondo de reserva que no son de una obra puntual.
    if (form.expenseCategory === "incidencia" && !form.incidentId) return showToast("Selecciona una incidencia.", "warning");
    if ((form.expenseCategory === "obra" || form.expenseCategory === "incidencia") && !form.sourceFund) {
      return showToast("Selecciona de qué fondo se paga este gasto.", "warning");
    }

    // Validar que Fecha Factura <= Fecha Vencimiento
    if (form.invoiceDate && form.maturity && form.invoiceDate > form.maturity) {
      return showToast("La Fecha Factura no puede ser posterior a la Fecha Vencimiento", "warning");
    }

    // Detección de factura duplicada por Nº comprobante + RUT proveedor + Edificio
    if (!forceDuplicate) {
      const dupResult = await checkDuplicateInvoice(form.receiptNumber, form.supplierid, buildingFilter);
      if (dupResult === null) return; // Usuario canceló
      if (dupResult.isDuplicate) {
        forceDuplicate = true;
      }
    }

    setSaving(true);
    try {
      let billUrl = null;

      // Upload bill file if provided
      if (form.bill) {
        const fileRef = ref(storage, `bills/${buildingFilter}/${Date.now()}_${form.bill.name}`);
        await uploadBytes(fileRef, form.bill);
        billUrl = await getDownloadURL(fileRef);
      }

      const _hasDesc = tieneDescuento(form.descuentoTipo, form.descuentoValor);
      const _tot = computeExpenseTotals({ subtotalBruto: form.subtotal, ivaBruto: form.iva, descuentoTipo: form.descuentoTipo, descuentoValor: form.descuentoValor });
      const payload = {
        buildingid: Number(buildingFilter),
        supplierid: form.supplierid,
        hasIncident: form.expenseCategory === "incidencia",
        incidents: form.expenseCategory === "incidencia" ? form.incidentId : null,
        receiptNumber: form.receiptNumber,
        description: form.description,
        maturity: form.maturity,
        invoiceDate: form.invoiceDate || null, // Fecha Factura
        money: form.money,
        // Con descuento: subtotal/iva/amount son los NETOS (post-descuento); se
        // guarda subtotalBruto/ivaTasa/descuentoMonto para auditoría.
        subtotal: _hasDesc ? _tot.subtotal : (Number(form.subtotal) || 0),
        iva: _hasDesc ? _tot.iva : (Number(form.iva) || 0),
        amount: _hasDesc ? _tot.amount : Number(form.amount),
        subtotalBruto: _hasDesc ? _tot.subtotalBruto : (Number(form.subtotal) || 0),
        ivaTasa: _hasDesc ? _tot.ivaTasa : null,
        descuentoTipo: _hasDesc ? form.descuentoTipo : null,
        descuentoValor: _hasDesc ? (Number(form.descuentoValor) || 0) : null,
        descuentoMonto: _hasDesc ? _tot.descuentoMonto : 0,
        paid: false, // Default false
        bill: billUrl,
        criteria: form.criteria || null, // Agregar criterio
        dividePorSuperficie: false, // legacy
        documentType: form.documentType || "factura",
        expenseCategory: form.expenseCategory || "gastos_comunes",
        projectId: form.expenseCategory === "obra" ? form.projectId : null,
        incidentId: form.expenseCategory === "incidencia" ? form.incidentId : null,
        overrideDistribution: form.expenseCategory !== "obra" ? (form.overrideDistribution || null) : null,
        // Fondo de origen: solo se persiste para obra/incidencia; para gastos comunes queda null
        sourceFund: (form.expenseCategory === "obra" || form.expenseCategory === "incidencia") ? (form.sourceFund || null) : null,
        isDuplicate: forceDuplicate || false, // Marcar como duplicado si corresponde
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.email || "desconocido",
      };

      const expensesid = await crearGastoNumerado(payload);

      // Audit
      await addDoc(collection(db, "audit"), {
        action: "create_expense_building",
        buildingId,
        expenseId: expensesid,
        createdBy: auth.currentUser?.email || "desconocido",
        createdAt: serverTimestamp(),
      });

      setShowNew(false);
      resetForm();
      fetchPagos();
    } catch (e) {
      console.error("Error guardando gasto:", e);
      showToast(mensajeErrorNumeracion(e, "gasto") || "Error al guardar el gasto: " + (e.message || e), "error");
    } finally {
      setSaving(false);
    }
  };

  const openPaymentModal = (item) => {
    // Solo manejamos expenses ahora (los payments no se muestran en la lista)
    if (item.type === "expense") {
      setSelectedExpense(item);
      setPaymentForm({
        method: "Transferencia",
        proof: null,
        paymentDate: new Date().toISOString().split('T')[0],
      });
      setShowPaymentModal(true);
    }
  };

  const openDetailModal = (item) => {
    if (item.type === "expense") {
      setPaymentDetail(null);
      setSelectedDetailExpense(item);
      setEditingExpense({
        documentType: item.documentType || "factura",
        supplierid: item.supplierid || "",
        receiptNumber: item.receiptNumber || "",
        description: item.description || "",
        shortDescription: item.shortDescription || "",
        maturity: toDateStr(item.maturity) || "",
        invoiceDate: toDateStr(item.invoiceDate) || "",
        money: item.money || "peso",
        expenseCategory: item.expenseCategory || "gastos_comunes",
        incidentId: item.incidents || "",
        projectId: item.projectId || "",
        // Si tenía descuento, los inputs muestran el BRUTO (para poder re-editar);
        // el neto/total se recalcula al guardar.
        subtotal: (item.descuentoTipo ? (item.subtotalBruto ?? item.subtotal) : item.subtotal) || "",
        iva: (item.descuentoTipo && item.ivaTasa != null
          ? Math.round((Number(item.subtotalBruto ?? item.subtotal) || 0) * (Number(item.ivaTasa) / 100) * 100) / 100
          : item.iva) || "",
        amount: item.amount || "",
        descuentoTipo: item.descuentoTipo || "",
        descuentoValor: item.descuentoValor || "",
        criteria: item.criteria || "",
        bill: null, // No se puede editar el archivo existente fácilmente
        billUrl: item.bill || null, // URL existente
        // Fondo de origen persistido (solo aplica a obra/incidencia); default reserva para categorías nuevas.
        sourceFund: item.sourceFund || ((item.expenseCategory === "obra" || item.expenseCategory === "incidencia") ? "reserva" : ""),
      });
      setShowDetailModal(true);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedDetailExpense || !editingExpense) return;
    if (await isDemoUser()) return showToast(DEMO_BLOCK_MESSAGE, "warning");

    // Validación básica
    if (editingExpense.documentType === "factura" && !editingExpense.supplierid) return showToast("Selecciona un proveedor.", "warning");
    if (!editingExpense.description) return showToast("Completa la descripción.", "warning");
    if (!editingExpense.amount) return showToast("Completa el total.", "warning");
    if ((editingExpense.expenseCategory === "obra" || editingExpense.expenseCategory === "incidencia") && !editingExpense.sourceFund) {
      return showToast("Selecciona de qué fondo se paga este gasto.", "warning");
    }

    // FIN-016: Validar monto positivo y finito
    const editAmount = Number(editingExpense.amount);
    if (editAmount <= 0 || !isFinite(editAmount)) {
      return showToast("El monto debe ser un número positivo.", "warning");
    }

    // Validar que Fecha Factura <= Fecha Vencimiento
    if (editingExpense.invoiceDate && editingExpense.maturity && editingExpense.invoiceDate > editingExpense.maturity) {
      return showToast("La Fecha Factura no puede ser posterior a la Fecha Vencimiento", "warning");
    }

    // Datos del pago (fecha, método, monto, comprobante): se corrigen con confirmación.
    const cambiosPago = selectedDetailExpense.paid && paymentDetail && pagoEdit
      ? cambiosDelPago(paymentDetail, pagoEdit, formatCurrencyUY)
      : [];
    if (cambiosPago.length) {
      const errorPago = validarPagoEditado(pagoEdit);
      if (errorPago) return showToast(errorPago, "warning");
    }
    // En un gasto pagado, de los datos del gasto solo se editan el criterio (afecta
    // el reparto) y el detalle corto (solo se ve en la tabla).
    const cambiaReparto = !selectedDetailExpense.paid
      || String(editingExpense.criteria || "") !== String(selectedDetailExpense.criteria || "");
    const cambiaDetalle = (detalleCortoParaGuardar(editingExpense.shortDescription) || "") !== (selectedDetailExpense.shortDescription || "");

    // FIN-015: Advertir si el gasto ya fue distribuido en un cierre. La consulta
    // fija el edificio del gasto: con las reglas 0D, solo por expenseId se niega
    // y el aviso se salteaba en silencio.
    let unidadesDistribuidas = 0;
    try {
      const eaSnap = await getDocs(query(
        collection(db, "expensesapartments"),
        where("expenseId", "==", selectedDetailExpense.id),
        where("buildingid", "in", tolerantId(selectedDetailExpense.buildingid ?? buildingFilter))
      ));
      unidadesDistribuidas = eaSnap.size;
    } catch (e) { /* si falla la verificación, continuar */ }
    if (unidadesDistribuidas > 0 && cambiaReparto) {
      const ok = await confirmDialog(
        `Este gasto ya fue distribuido en ${unidadesDistribuidas} unidad(es) en un cierre de mes.\n\nCambiar el monto o criterio NO actualiza la distribución existente.\n\n¿Desea continuar?`,
        { title: "Gasto ya distribuido", danger: true, confirmLabel: "Guardar de todas formas" }
      );
      if (!ok) return;
    }
    if (cambiosPago.length) {
      const ok = await confirmDialog("Revisá lo que cambia en el pago de este gasto.", {
        title: "¿Guardar los cambios del pago?",
        confirmLabel: "Guardar cambios",
        wide: true,
        content: <CambiosDelPago cambios={cambiosPago} yaRepartido={unidadesDistribuidas > 0} />,
      });
      if (!ok) return;
    }

    setSavingEdit(true);
    try {
      let billUrl = editingExpense.billUrl; // Mantener URL existente por defecto

      // Si se subió un nuevo archivo, reemplazar
      if (editingExpense.bill) {
        const fileRef = ref(storage, `bills/${buildingFilter}/${Date.now()}_${editingExpense.bill.name}`);
        await uploadBytes(fileRef, editingExpense.bill);
        billUrl = await getDownloadURL(fileRef);
      }

      // Comprobante de pago nuevo (reemplaza al anterior, que queda en el historial del pago).
      let proofUrl = null;
      if (cambiosPago.length && pagoEdit.proof) {
        const fileRef = ref(storage, `payment_proofs/${buildingFilter}/${Date.now()}_${pagoEdit.proof.name}`);
        await uploadBytes(fileRef, pagoEdit.proof);
        proofUrl = await getDownloadURL(fileRef);
      }

      const payload = {
        documentType: editingExpense.documentType || "factura",
        supplierid: editingExpense.supplierid,
        expenseCategory: editingExpense.expenseCategory || "gastos_comunes",
        hasIncident: editingExpense.expenseCategory === "incidencia",
        incidents: editingExpense.expenseCategory === "incidencia" ? editingExpense.incidentId : null,
        projectId: editingExpense.expenseCategory === "obra" ? editingExpense.projectId : null,
        incidentId: editingExpense.expenseCategory === "incidencia" ? editingExpense.incidentId : null,
        sourceFund: (editingExpense.expenseCategory === "obra" || editingExpense.expenseCategory === "incidencia") ? (editingExpense.sourceFund || null) : null,
        receiptNumber: editingExpense.receiptNumber,
        description: editingExpense.description,
        shortDescription: detalleCortoParaGuardar(editingExpense.shortDescription),
        maturity: editingExpense.maturity,
        invoiceDate: editingExpense.invoiceDate || null,
        money: editingExpense.money,
        ...(() => {
          const hd = tieneDescuento(editingExpense.descuentoTipo, editingExpense.descuentoValor);
          const t = computeExpenseTotals({ subtotalBruto: editingExpense.subtotal, ivaBruto: editingExpense.iva, descuentoTipo: editingExpense.descuentoTipo, descuentoValor: editingExpense.descuentoValor });
          return {
            subtotal: hd ? t.subtotal : (Number(editingExpense.subtotal) || 0),
            iva: hd ? t.iva : (Number(editingExpense.iva) || 0),
            amount: hd ? t.amount : Number(editingExpense.amount),
            subtotalBruto: hd ? t.subtotalBruto : (Number(editingExpense.subtotal) || 0),
            ivaTasa: hd ? t.ivaTasa : null,
            descuentoTipo: hd ? editingExpense.descuentoTipo : null,
            descuentoValor: hd ? (Number(editingExpense.descuentoValor) || 0) : null,
            descuentoMonto: hd ? t.descuentoMonto : 0,
          };
        })(),
        bill: billUrl,
        criteria: editingExpense.criteria || null,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.email || "desconocido",
      };

      const actor = auth.currentUser?.email || "desconocido";
      if (cambiosPago.length) {
        // Pago y gasto juntos: el estado de cuenta lee el pago y el cierre lee el gasto.
        const { pago, gasto } = actualizacionesDelPago({ pago: paymentDetail, form: pagoEdit, proofUrl, actor, ahora: serverTimestamp() });
        const batch = writeBatch(db);
        batch.update(doc(db, "expenses", selectedDetailExpense.id), { ...payload, ...gasto });
        batch.update(doc(db, "payments", paymentDetail.id), pago);
        await batch.commit();
        await addDoc(collection(db, "audit"), {
          action: "update_payment_expense",
          buildingId: Number(buildingFilter),
          expenseId: selectedDetailExpense.expensesid,
          paymentId: paymentDetail.paymentid ?? paymentDetail.id,
          campos: cambiosPago.map((c) => c.campo),
          antes: { paymentDate: paymentDetail.paymentDate ?? null, method: paymentDetail.method ?? null, amount: paymentDetail.amount ?? null, proof: paymentDetail.proof ?? null },
          despues: { paymentDate: pago.paymentDate, method: pago.method, amount: pago.amount, proof: pago.proof ?? paymentDetail.proof ?? null },
          updatedBy: actor,
          createdAt: serverTimestamp(),
        });
      } else {
        await updateDoc(doc(db, "expenses", selectedDetailExpense.id), payload);
      }

      // Audit
      if (cambiaReparto || cambiaDetalle || !cambiosPago.length) {
        await addDoc(collection(db, "audit"), {
          action: "update_expense_building",
          buildingId: Number(buildingFilter),
          expenseId: selectedDetailExpense.expensesid,
          updatedBy: actor,
          createdAt: serverTimestamp(),
        });
      }

      setShowDetailModal(false);
      setSelectedDetailExpense(null);
      setEditingExpense(null);
      setPaymentDetail(null);
      fetchPagos();
      showToast(cambiosPago.length && !cambiaReparto && !cambiaDetalle ? "Pago actualizado" : "Gasto actualizado exitosamente", "success");
    } catch (e) {
      console.error("Error actualizando gasto:", e);
      showToast("Error al actualizar el gasto.", "error");
    } finally {
      setSavingEdit(false);
    }
  };

  const [deletingExpense, setDeletingExpense] = useState(false);

  const handleDeleteExpense = async () => {
    if (!selectedDetailExpense || selectedDetailExpense.isDeleted) return;
    if (await isDemoUser()) return showToast(DEMO_BLOCK_MESSAGE, "warning");
    const ok = await confirmDialog(
      "¿Eliminar este gasto? Se moverá a eliminados y ya no aparecerá en la lista.",
      { title: "Eliminar gasto", danger: true, confirmLabel: "Eliminar" }
    );
    if (!ok) return;

    setDeletingExpense(true);
    try {
      const { id: _docId, type, isDeleted, ...expenseData } = selectedDetailExpense;
      await addDoc(collection(db, "expenses_deleted"), {
        ...expenseData,
        originalDocId: _docId,
        deletedAt: serverTimestamp(),
        deletedBy: auth.currentUser?.email || "desconocido",
      });
      await deleteDoc(doc(db, "expenses", _docId));
      await addDoc(collection(db, "audit"), {
        action: "delete_expense_to_deleted",
        buildingId: buildingFilter,
        expenseId: selectedDetailExpense.expensesid,
        originalDocId: _docId,
        createdBy: auth.currentUser?.email || "desconocido",
        createdAt: serverTimestamp(),
      });
      setShowDetailModal(false);
      setSelectedDetailExpense(null);
      setEditingExpense(null);
      setPaymentDetail(null);
      fetchPagos();
      showToast("Gasto eliminado correctamente", "success");
    } catch (e) {
      console.error("Error eliminando gasto:", e);
      showToast("Error al eliminar el gasto.", "error");
    } finally {
      setDeletingExpense(false);
    }
  };

  const markAsPaid = async (itemId, itemType) => {
    const ok = await confirmDialog("¿Confirmar que este ítem queda como pagado?", {
      title: "Confirmar pago",
      confirmLabel: "Confirmar",
    });
    if (!ok) return;

    try {
      if (itemType === "expense") {
        const paidDate = new Date().toISOString().split('T')[0];
        const updateData = { paid: true, paidDate };

        // Conversión automática USD → UYU según política del edificio
        const expense = items.find(i => i.id === itemId);
        const money = (expense?.money || "peso").toLowerCase();
        if (money === "dolar" && selectedBuilding?.id) {
          const buildingSnap = await getDoc(doc(db, "building", selectedBuilding.id));
          const bData = buildingSnap.data() || {};
          const policy = bData.usdExpensePolicy || "convert_to_uyu";

          if (policy === "convert_to_uyu") {
            // Obtener TC del día anterior
            let rate = await getPreviousDayRate();
            let exchangeRate = rate?.sell;

            // Fallback: TC manual del edificio
            if (!exchangeRate && bData.exchangeRateUSD) {
              exchangeRate = Number(bData.exchangeRateUSD);
            }

            if (exchangeRate && exchangeRate > 0) {
              const originalAmount = Number(expense.amount) || 0;
              const originalSubtotal = Number(expense.subtotal) || 0;
              const originalIva = Number(expense.iva) || 0;

              updateData.convertedToUYU = true;
              updateData.originalAmountUSD = originalAmount;
              updateData.originalSubtotalUSD = originalSubtotal;
              updateData.originalIvaUSD = originalIva;
              updateData.exchangeRateUsed = exchangeRate;
              updateData.exchangeRateDate = rate?.date || paidDate;
              updateData.amount = Math.round(originalAmount * exchangeRate * 100) / 100;
              updateData.subtotal = Math.round(originalSubtotal * exchangeRate * 100) / 100;
              updateData.iva = Math.round(originalIva * exchangeRate * 100) / 100;
              // Descuento: convertir el bruto y el monto de descuento (la tasa/% no cambian).
              if (expense.subtotalBruto != null) updateData.subtotalBruto = Math.round((Number(expense.subtotalBruto) || 0) * exchangeRate * 100) / 100;
              if (expense.descuentoMonto != null) updateData.descuentoMonto = Math.round((Number(expense.descuentoMonto) || 0) * exchangeRate * 100) / 100;
              updateData.money = "peso";
            } else {
              showToast("No hay tipo de cambio disponible (BCU ni manual). El gasto queda en dólares.", "warning");
            }
          }
        }

        await updateDoc(doc(db, "expenses", itemId), updateData);
      } else if (itemType === "payment") {
        await updateDoc(doc(db, "payments", itemId), { paid: true });
      }

      showToast("¡Pagado!", "success");
      fetchPagos(); // Recargar datos
    } catch (error) {
      console.error("Error al pagar:", error);
      showToast("Error al pagar: " + error.message, "error");
    }
  };

  const savePayment = async () => {
    if (!selectedExpense) return;
    if (await isDemoUser()) return showToast(DEMO_BLOCK_MESSAGE, "warning");
    // Sin número interno (expensesid del contador) el pago no queda ligado a su
    // gasto y Firestore rechaza el campo vacío. Pasa con gastos viejos cargados por
    // script; las reglas no dejan numerarlos desde la app.
    if (selectedExpense.expensesid == null || selectedExpense.expensesid === "") {
      return showToast("Este gasto no tiene número interno (se cargó por fuera de la app) y no se puede pagar. Cargalo de nuevo como gasto nuevo y pagá ese.", "error");
    }

    setSavingPayment(true);
    try {
      let proofUrl = null;
      
      // Upload proof file if provided
      if (paymentForm.proof) {
        const fileRef = ref(storage, `payment_proofs/${buildingFilter}/${Date.now()}_${paymentForm.proof.name}`);
        await uploadBytes(fileRef, paymentForm.proof);
        proofUrl = await getDownloadURL(fileRef);
      }

      // La fecha de pago se toma del formulario (editable por el usuario); si viene vacía, se usa hoy como fallback.
      const paymentDateStr = paymentForm.paymentDate || new Date().toISOString().split('T')[0];

      // Conversión automática USD → UYU según política del edificio. Se calcula ANTES de
      // crear el pago para que TANTO el doc de pago COMO el gasto queden en UYU. Antes el
      // pago se guardaba en USD (nominal) y fundMovements prefería `payment.amount` → el
      // extracto/saldo del fondo sub-debitaba (US$100 se debitaba como $100).
      let paidAmountUYU = Number(selectedExpense.amount) || 0;
      const expenseConversion = {};
      const money = (selectedExpense.money || "peso").toLowerCase();
      if (money === "dolar" && selectedBuilding?.id) {
        const buildingSnap = await getDoc(doc(db, "building", selectedBuilding.id));
        const bData = buildingSnap.data() || {};
        const policy = bData.usdExpensePolicy || "convert_to_uyu";

        if (policy === "convert_to_uyu") {
          let rate = await getPreviousDayRate();
          let exchangeRate = rate?.sell;
          if (!exchangeRate && bData.exchangeRateUSD) {
            exchangeRate = Number(bData.exchangeRateUSD);
          }
          if (exchangeRate && exchangeRate > 0) {
            const originalAmount = Number(selectedExpense.amount) || 0;
            const originalSubtotal = Number(selectedExpense.subtotal) || 0;
            const originalIva = Number(selectedExpense.iva) || 0;

            paidAmountUYU = Math.round(originalAmount * exchangeRate * 100) / 100;
            expenseConversion.convertedToUYU = true;
            expenseConversion.originalAmountUSD = originalAmount;
            expenseConversion.originalSubtotalUSD = originalSubtotal;
            expenseConversion.originalIvaUSD = originalIva;
            expenseConversion.exchangeRateUsed = exchangeRate;
            expenseConversion.exchangeRateDate = rate?.date || paymentDateStr;
            expenseConversion.amount = paidAmountUYU;
            expenseConversion.subtotal = Math.round(originalSubtotal * exchangeRate * 100) / 100;
            expenseConversion.iva = Math.round(originalIva * exchangeRate * 100) / 100;
            if (selectedExpense.subtotalBruto != null) expenseConversion.subtotalBruto = Math.round((Number(selectedExpense.subtotalBruto) || 0) * exchangeRate * 100) / 100;
            if (selectedExpense.descuentoMonto != null) expenseConversion.descuentoMonto = Math.round((Number(selectedExpense.descuentoMonto) || 0) * exchangeRate * 100) / 100;
            expenseConversion.money = "peso";
          } else {
            showToast("No hay tipo de cambio disponible (BCU ni manual). El gasto queda en dólares.", "warning");
          }
        }
      }

      const paymentPayload = {
        expensesid: selectedExpense.expensesid,
        description: selectedExpense.description,
        amount: paidAmountUYU, // en UYU (convertido si el gasto estaba en USD)
        paymentDate: paymentDateStr, // Formato YYYY-MM-DD, seleccionado en el modal
        paymentUser: auth.currentUser?.email || "desconocido",
        method: paymentForm.method,
        maturity: selectedExpense.maturity,
        proof: proofUrl,
        buildingid: Number(buildingFilter),
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.email || "desconocido",
      };

      // Actualizar el expense a pagado (+ campos de conversión USD→UYU si aplica).
      const expenseUpdate = {
        paid: true,
        method: paymentForm.method,
        paidDate: paymentDateStr,
        ...expenseConversion,
      };

      // Pago (paymentid del contador atómico, contrato C1) y gasto pagado en UNA
      // transacción: o quedan los dos o ninguno.
      devLog("[savePayment] payload:", { amount: paymentPayload.amount, buildingid: paymentPayload.buildingid, expenseUpdate });
      const nuevoPagoRef = doc(collection(db, "payments"));
      const paymentid = await reservarIdSecuencial(db, CONTADOR_PAGOS, (tx, id) => {
        tx.set(nuevoPagoRef, { ...paymentPayload, paymentid: id });
        tx.update(doc(db, "expenses", selectedExpense.id), expenseUpdate);
      });

      // Audit
      await addDoc(collection(db, "audit"), {
        action: "create_payment_expense",
        buildingId: Number(buildingFilter),
        expenseId: selectedExpense.expensesid,
        paymentId: paymentid,
        createdBy: auth.currentUser?.email || "desconocido",
        createdAt: serverTimestamp(),
      });

      setShowPaymentModal(false);
      setSelectedExpense(null);
      setPaymentForm({ method: "Transferencia", proof: null, paymentDate: new Date().toISOString().split('T')[0] });
      fetchPagos();
      showToast("Pago registrado exitosamente", "success");
    } catch (error) {
      console.error("Error guardando pago:", error);
      showToast(mensajeErrorNumeracion(error, "pago") || "Error al guardar el pago: " + error.message, "error");
    } finally {
      setSavingPayment(false);
    }
  };

  const resetForm = () => {
    setForm({
      supplierid: "",
      receiptNumber: "",
      description: "",
      maturity: "",
      invoiceDate: "",
      money: "peso",
      subtotal: "",
      iva: "",
      amount: "",
      descuentoTipo: "",
      descuentoValor: "",
      bill: null,
      criteria: "",
      dividePorSuperficie: false,
      documentType: "factura",
      expenseCategory: "gastos_comunes",
      projectId: "",
      incidentId: "",
      overrideDistribution: "",
      sourceFund: "",
    });
  };

  const calculateTotal = () => {
    const subtotal = Number(form.subtotal) || 0;
    const ivaRate = Number(form.iva) || 0; // IVA as percentage
    const ivaAmount = subtotal * (ivaRate / 100);
    return subtotal + ivaAmount;
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const MAX_SIZE = 10 * 1024 * 1024;
    const ALLOWED = ["application/pdf", "image/jpeg", "image/png"];
    if (!ALLOWED.includes(file.type)) {
      showToast("Formato no permitido. Solo se aceptan PDF, JPG y PNG.", "warning");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_SIZE) {
      showToast(`El archivo supera el tamaño máximo de 10MB (${(file.size / 1024 / 1024).toFixed(1)}MB).`, "warning");
      e.target.value = "";
      return;
    }
    setForm({ ...form, bill: file });
  };

  // ===== Funciones para procesamiento de factura con IA =====

  const MAX_UPLOAD_SIZE = 10 * 1024 * 1024; // 10MB
  const ALLOWED_UPLOAD_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/webp"];

  const validateUploadFile = (file) => {
    if (!ALLOWED_UPLOAD_TYPES.includes(file.type)) {
      showToast(`Formato no permitido: "${file.name}". Solo se aceptan PDF, JPG, PNG y WEBP.`, "warning");
      return false;
    }
    if (file.size > MAX_UPLOAD_SIZE) {
      showToast(`"${file.name}" supera el tamaño máximo de 10MB (${(file.size / 1024 / 1024).toFixed(1)}MB).`, "warning");
      return false;
    }
    return true;
  };

  const handleAIFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!validateUploadFile(file)) { e.target.value = ""; return; }
      setAIFile(file);
      setAIError(null);
      setAIResult(null);

      // Crear preview
      if (file.type.startsWith('image/')) {
        const url = URL.createObjectURL(file);
        setAIPreviewUrl(url);
      } else {
        setAIPreviewUrl(null);
      }
    }
    e.target.value = "";
  };

  const handleAIFileSelectBatch = (e) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(f => validateUploadFile(f));
    if (validFiles.length > 0) {
      setAIFilesBatch(validFiles);
      setAIResultsBatch([]);
      setCurrentBatchIndex(0);
      setAIError(null);
      setAIResult(null);
      setAIFile(null);
    }
    e.target.value = "";
  };

  const processInvoiceWithAI = async () => {
    if (!aiFile || !buildingId) return;
    
    setAIProcessing(true);
    setAIError(null);
    
    try {
      // Convertir archivo a base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(aiFile);
      });
      
      const authHeaders = await getAuthHeaders();
      const response = await fetch(`${Habi_API_URL}/api/process-invoice`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify({
          file: {
            name: aiFile.name,
            data: base64,
            type: aiFile.type
          },
          building_id: buildingId
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 402) {
          setAIErrorType("warning");
          throw new Error(errorData.detail || "Sin crédito en la API de IA.");
        }
        throw new Error(errorData.detail || "Error procesando factura");
      }

      const result = await response.json();
      setAIResult(result);

      // A1 — Auto-clasificación (Fase 1): pedir sugerencias en paralelo al
      // modal de decisión. Las sugerencias con confidence >= 0.8 se aplican
      // automáticamente al guardar; las de confidence menor solo se muestran
      // como hint y el staff puede editar el gasto después.
      let classifySuggestion = null;
      try {
        const classifyResp = await fetch(`${Habi_API_URL}/api/classify-expense`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders },
          body: JSON.stringify({
            building_id: String(buildingId),
            supplier_id: result.supplier_id != null ? String(result.supplier_id) : null,
            supplier_rut: result.invoice_data?.supplier_rut || null,
            supplier_name: result.invoice_data?.supplier_name || null,
            description: result.invoice_data?.description || null,
            amount: Number(result.invoice_data?.total) || null,
            currency: result.invoice_data?.currency || null,
          }),
        });
        if (classifyResp.ok) {
          classifySuggestion = await classifyResp.json();
        }
      } catch (clsErr) {
        // Falla silenciosa: si el clasificador no responde, seguimos con defaults.
        console.warn("classify-expense falló (no bloqueante):", clsErr);
      }

      // SIEMPRE mostrar modal de previsualización para que el admin valide los datos
      if (result.invoice_data) {
        const data = result.invoice_data;
        
        // Guardar niveles de confianza
        if (data.confidence) {
          setFieldConfidence({
            supplier_name: data.confidence.supplier_name,
            supplier_rut: data.confidence.supplier_rut,
            supplier_address: data.confidence.supplier_address,
            supplier_phone: data.confidence.supplier_phone,
            supplier_email: data.confidence.supplier_email,
            supplier_type: data.confidence.supplier_type,
            receiptNumber: data.confidence.receipt_number,
            invoiceDate: data.confidence.invoice_date,
            maturity: data.confidence.maturity_date,
            currency: data.confidence.currency,
            subtotal: data.confidence.subtotal,
            iva: data.confidence.iva_amount,
            amount: data.confidence.total,
            description: data.confidence.description,
          });
        }
        
        // Limpiar teléfono: solo números, sin espacios ni guiones
        if (data.supplier_phone) {
          data.supplier_phone = data.supplier_phone.replace(/[^0-9]/g, '');
        }
        
        // Preparar datos para el modal
        const invoiceDataWithGeo = { ...data };
        
        // Siempre mostrar modal de decisión para validación
        setPendingInvoiceData({
          invoiceData: invoiceDataWithGeo,
          suggestedSuppliers: result.suggested_suppliers || [],
          supplierExists: result.supplier_exists,
          supplierId: result.supplier_id,
          classifySuggestion, // A1 — sugerencias del clasificador
        });
        setShowNew(false);
        setShowSupplierDecisionModal(true);
      }

    } catch (error) {
      console.error("Error procesando factura con IA:", error);
      setAIError(error.message || "Error al procesar la factura");
    } finally {
      setAIProcessing(false);
    }
  };

  const processBatchInvoices = async () => {
    if (!aiFilesBatch.length || !buildingId) return;
    setBatchProcessingProgress({ current: 0, total: aiFilesBatch.length });
    setAIError(null);
    const results = [];
    try {
      const errors = [];
      for (let i = 0; i < aiFilesBatch.length; i++) {
        setBatchProcessingProgress({ current: i + 1, total: aiFilesBatch.length });
        const file = aiFilesBatch[i];
        try {
          const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
          const batchAuthHeaders = await getAuthHeaders();
          const response = await fetch(`${Habi_API_URL}/api/process-invoice`, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...batchAuthHeaders },
            body: JSON.stringify({
              file: { name: file.name, data: base64, type: file.type },
              building_id: buildingId
            })
          });
          if (!response.ok) {
            const errData = await response.json().catch(() => ({}));
            if (response.status === 402) {
              setAIErrorType("warning");
              throw new Error(errData.detail || "Sin crédito en la API de IA.");
            }
            errors.push(`${file.name}: ${errData.detail || "Error desconocido"}`);
            continue;
          }
          const result = await response.json();
          const data = result.invoice_data || {};
          if (data.supplier_phone) data.supplier_phone = data.supplier_phone.replace(/[^0-9]/g, "");

          // A1 — clasificación por histórico en batch (best-effort, no bloquea).
          let classifySuggestion = null;
          try {
            const classifyResp = await fetch(`${Habi_API_URL}/api/classify-expense`, {
              method: "POST",
              headers: { "Content-Type": "application/json", ...batchAuthHeaders },
              body: JSON.stringify({
                building_id: String(buildingId),
                supplier_id: result.supplier_id != null ? String(result.supplier_id) : null,
                supplier_rut: data.supplier_rut || null,
                supplier_name: data.supplier_name || null,
                description: data.description || null,
                amount: Number(data.total) || null,
                currency: data.currency || null,
              }),
            });
            if (classifyResp.ok) {
              classifySuggestion = await classifyResp.json();
            }
          } catch (clsErr) {
            console.warn(`classify-expense batch falló para ${file.name}:`, clsErr);
          }

          results.push({
            file,
            result,
            pendingData: {
              invoiceData: { ...data },
              suggestedSuppliers: result.suggested_suppliers || [],
              supplierExists: result.supplier_exists,
              supplierId: result.supplier_id,
              criteria: "",
              expenseCategory: "gastos_comunes",
              projectId: "",
              incidentId: "",
              classifySuggestion,
            }
          });
        } catch (err) {
          errors.push(`${file.name}: ${err.message || "Error inesperado"}`);
        }
      }
      if (errors.length > 0 && results.length > 0) {
        setAIError(`${results.length} procesadas OK, ${errors.length} con error: ${errors.join("; ")}`);
      } else if (errors.length > 0 && results.length === 0) {
        throw new Error(`Todas las facturas fallaron: ${errors.join("; ")}`);
      }
      setAIResultsBatch(results);
      setCurrentBatchIndex(0);
      const first = results[0];
      if (first) {
        const data = first.pendingData.invoiceData;
        if (data.confidence) {
          setFieldConfidence({
            supplier_name: data.confidence.supplier_name,
            supplier_rut: data.confidence.supplier_rut,
            supplier_address: data.confidence.supplier_address,
            supplier_phone: data.confidence.supplier_phone,
            supplier_email: data.confidence.supplier_email,
            supplier_type: data.confidence.supplier_type,
            receiptNumber: data.confidence.receipt_number,
            invoiceDate: data.confidence.invoice_date,
            maturity: data.confidence.maturity_date,
            currency: data.confidence.currency,
            subtotal: data.confidence.subtotal,
            iva: data.confidence.iva_amount,
            amount: data.confidence.total,
            description: data.confidence.description,
          });
        }
        setPendingInvoiceData(first.pendingData);
        setShowNew(false);
        setShowSupplierDecisionModal(true);
      }
      setAIFilesBatch([]);
      setBatchProcessingProgress(null);
    } catch (error) {
      console.error("Error procesando batch:", error);
      setAIError(error.message || "Error al procesar facturas");
      setBatchProcessingProgress(null);
    }
  };

  const saveExpenseFromBatch = async (pd, supplierId) => {
    if (!buildingFilter || !pd) return;
    const data = pd.invoiceData;
    if (!supplierId) return showToast("Selecciona o crea un proveedor.", "warning");
    if (!data.description) return showToast("Completa la descripción.", "warning");
    if (!data.total) return showToast("Completa el total.", "warning");
    let maturityDate = data.maturity_date || "";
    const invoiceDate = data.invoice_date || "";
    if (maturityDate && invoiceDate && maturityDate < invoiceDate) maturityDate = invoiceDate;
    const batchItem = aiResultsBatch[currentBatchIndex];
    if (!batchItem) return;

    // Detección de factura duplicada por Nº comprobante + RUT proveedor + Edificio
    const dupResult = await checkDuplicateInvoice(data.receipt_number, supplierId, buildingFilter);
    if (dupResult === null) return; // Usuario canceló
    const markAsDuplicate = dupResult.isDuplicate;

    setSaving(true);
    try {
      let billUrl = null;
      if (batchItem.file) {
        const fileRef = ref(storage, `bills/${buildingFilter}/${Date.now()}_${batchItem.file.name}`);
        await uploadBytes(fileRef, batchItem.file);
        billUrl = await getDownloadURL(fileRef);
      }

      // A1 — aplicar sugerencias del clasificador si confidence >= 0.8
      const cls = pd.classifySuggestion || {};
      const AUTO_APPLY_THRESHOLD = 0.8;
      const sgCat = cls.expense_category?.confidence >= AUTO_APPLY_THRESHOLD
        ? cls.expense_category.value : null;
      const sgCri = cls.criteria_expenses?.confidence >= AUTO_APPLY_THRESHOLD
        ? cls.criteria_expenses.value : null;
      const sgFnd = cls.source_fund?.confidence >= AUTO_APPLY_THRESHOLD
        ? cls.source_fund.value : null;
      const effCat = pd.expenseCategory || sgCat || "gastos_comunes";
      const effCri = pd.criteria || sgCri || null;
      // Obras/incidencias sin fondo elegido → Reserva (lo que muestra el selector por defecto).
      const effFnd = pd.sourceFund || sgFnd || "reserva";

      const payload = {
        buildingid: Number(buildingFilter),
        supplierid: supplierId,
        expenseCategory: effCat,
        hasIncident: effCat === "incidencia",
        incidents: effCat === "incidencia" ? pd.incidentId : null,
        projectId: effCat === "obra" ? pd.projectId : null,
        incidentId: effCat === "incidencia" ? pd.incidentId : null,
        receiptNumber: data.receipt_number || "",
        description: data.description || "",
        shortDescription: detalleCortoParaGuardar(data.description_short),
        maturity: maturityDate,
        invoiceDate: invoiceDate || null,
        money: data.currency === "dolar" ? "dolar" : "peso",
        subtotal: Number(data.subtotal) || 0,
        iva: Number(data.iva_amount) || 0,
        amount: Number(data.total),
        paid: false,
        bill: billUrl,
        criteria: effCri,
        sourceFund: (effCat === "obra" || effCat === "incidencia") ? effFnd : null,
        classifyMethod: cls.method || null,
        classifyAutoApplied: !!(sgCat || sgCri || sgFnd),
        dividePorSuperficie: false,
        isDuplicate: markAsDuplicate, // Marcar como duplicado si corresponde
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.email || "desconocido",
      };
      const expensesid = await crearGastoNumerado(payload);
      await addDoc(collection(db, "audit"), {
        action: "create_expense_building",
        buildingId,
        expenseId: expensesid,
        createdBy: auth.currentUser?.email || "desconocido",
        createdAt: serverTimestamp(),
      });
      await fetchPagos();
      const totalCount = aiResultsBatch.length;
      const isLast = currentBatchIndex >= totalCount - 1;
      if (isLast) {
        setShowSupplierDecisionModal(false);
        setPendingInvoiceData(null);
        setAIResultsBatch([]);
        setCurrentBatchIndex(0);
        setFieldConfidence({});
        setShowNew(false);
        setAIFilesBatch([]);
        fetchPagos();
        showToast(`Se guardaron ${totalCount} gastos correctamente.`, "success");
      } else {
        const nextIdx = currentBatchIndex + 1;
        setCurrentBatchIndex(nextIdx);
        const nextItem = aiResultsBatch[nextIdx];
        if (nextItem) {
          const nd = nextItem.pendingData.invoiceData;
          if (nd.confidence) {
            setFieldConfidence({
              supplier_name: nd.confidence.supplier_name,
              supplier_rut: nd.confidence.supplier_rut,
              supplier_address: nd.confidence.supplier_address,
              receiptNumber: nd.confidence.receipt_number,
              invoiceDate: nd.confidence.invoice_date,
              maturity: nd.confidence.maturity_date,
              subtotal: nd.confidence.subtotal,
              iva: nd.confidence.iva_amount,
              amount: nd.confidence.total,
              description: nd.confidence.description,
            });
          }
          setPendingInvoiceData(nextItem.pendingData);
        }
      }
    } catch (e) {
      console.error("Error guardando gasto batch:", e);
      showToast(mensajeErrorNumeracion(e, "gasto") || "Error al guardar el gasto.", "error");
    } finally {
      setSaving(false);
    }
  };

  // Guardar gasto directo desde el modal de IA (single-file)
  const saveExpenseFromAI = async (pd, supplierId) => {
    if (!buildingFilter || !pd) return;
    const data = pd.invoiceData;
    if (!supplierId) return showToast("Selecciona o crea un proveedor.", "warning");
    if (!data.description) return showToast("Completa la descripción.", "warning");
    if (!data.total) return showToast("Completa el total.", "warning");
    const amountNum = Number(data.total);
    if (amountNum <= 0 || !isFinite(amountNum)) return showToast("El monto debe ser un número positivo.", "warning");
    // El proyecto/obra es OPCIONAL: hay gastos de fondo de reserva que no son de una obra puntual.
    if (pd.expenseCategory === "incidencia" && !pd.incidentId) return showToast("Selecciona una incidencia.", "warning");

    let maturityDate = data.maturity_date || "";
    const invoiceDate = data.invoice_date || "";
    if (maturityDate && invoiceDate && maturityDate < invoiceDate) maturityDate = invoiceDate;
    if (invoiceDate && maturityDate && invoiceDate > maturityDate) {
      return showToast("La Fecha Factura no puede ser posterior a la Fecha Vencimiento", "warning");
    }

    // Detección de duplicados
    const dupResult = await checkDuplicateInvoice(data.receipt_number, supplierId, buildingFilter);
    if (dupResult === null) return;
    const markAsDuplicate = dupResult.isDuplicate;

    setSaving(true);
    try {
      let billUrl = null;
      if (aiFile) {
        const fileRef = ref(storage, `bills/${buildingFilter}/${Date.now()}_${aiFile.name}`);
        await uploadBytes(fileRef, aiFile);
        billUrl = await getDownloadURL(fileRef);
      }

      // A1 — Aplicar sugerencias del clasificador SOLO si confidence >= 0.8.
      // Para confidence menor, dejamos los defaults actuales (gastos_comunes,
      // criteria=null) — el staff puede editar el gasto después si quiere.
      const cls = pd.classifySuggestion || {};
      const AUTO_APPLY_THRESHOLD = 0.8;
      const suggestedCategory =
        cls.expense_category?.confidence >= AUTO_APPLY_THRESHOLD
          ? cls.expense_category.value
          : null;
      const suggestedCriteria =
        cls.criteria_expenses?.confidence >= AUTO_APPLY_THRESHOLD
          ? cls.criteria_expenses.value
          : null;
      const suggestedFund =
        cls.source_fund?.confidence >= AUTO_APPLY_THRESHOLD
          ? cls.source_fund.value
          : null;

      const effectiveCategory =
        pd.expenseCategory || suggestedCategory || "gastos_comunes";
      const effectiveCriteria = pd.criteria || suggestedCriteria || null;
      // Obras/incidencias sin fondo elegido → Reserva (lo que muestra el selector por defecto).
      const effectiveFund = pd.sourceFund || suggestedFund || "reserva";

      const payload = {
        buildingid: Number(buildingFilter),
        supplierid: supplierId,
        documentType: pd.documentType || "factura",
        expenseCategory: effectiveCategory,
        hasIncident: effectiveCategory === "incidencia",
        incidents: effectiveCategory === "incidencia" ? pd.incidentId : null,
        projectId: effectiveCategory === "obra" ? pd.projectId : null,
        incidentId: effectiveCategory === "incidencia" ? pd.incidentId : null,
        receiptNumber: data.receipt_number || "",
        description: data.description || "",
        shortDescription: detalleCortoParaGuardar(data.description_short),
        maturity: maturityDate,
        invoiceDate: invoiceDate || null,
        money: data.currency === "dolar" ? "dolar" : "peso",
        subtotal: Number(data.subtotal) || 0,
        iva: Number(data.iva_amount) || 0,
        amount: Number(data.total),
        paid: false,
        bill: billUrl,
        criteria: effectiveCriteria,
        overrideDistribution: effectiveCategory !== "obra" ? (pd.overrideDistribution || null) : null,
        sourceFund: (effectiveCategory === "obra" || effectiveCategory === "incidencia") ? effectiveFund : null,
        dividePorSuperficie: false,
        isDuplicate: markAsDuplicate,
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.email || "desconocido",
        // Trail: si el clasificador sugirió, guardamos el método y método para auditar.
        classifyMethod: cls.method || null,
        classifyAutoApplied: !!(suggestedCategory || suggestedCriteria || suggestedFund),
      };
      const expensesid = await crearGastoNumerado(payload);
      await addDoc(collection(db, "audit"), {
        action: "create_expense_building",
        buildingId,
        expenseId: expensesid,
        createdBy: auth.currentUser?.email || "desconocido",
        createdAt: serverTimestamp(),
      });

      setShowSupplierDecisionModal(false);
      setPendingInvoiceData(null);
      setFieldConfidence({});
      resetAIUpload();
      resetForm();
      fetchPagos();
      showToast("Gasto creado correctamente.", "success");
    } catch (e) {
      console.error("Error guardando gasto desde IA:", e);
      showToast(mensajeErrorNumeracion(e, "gasto") || "Error al guardar el gasto.", "error");
    } finally {
      setSaving(false);
    }
  };

  // Función para crear proveedor nuevo desde el modal
  const handleCreateSupplierFromAI = async () => {
    if (!pendingInvoiceData) return;
    
    if (!buildingId) {
      showToast("No hay edificio seleccionado. Selecciona un edificio primero.", "warning");
      return;
    }

    const data = pendingInvoiceData.invoiceData;
    if (data.supplier_rut) {
      const rutValidation = validateRutUY(data.supplier_rut);
      if (!rutValidation.valid) {
        showToast(rutValidation.message, "warning");
        return;
      }
    }
    
    setCreatingSupplier(true);
    try {
      const requestBody = {
        name: data.supplier_name || "Proveedor sin nombre",
        rut: data.supplier_rut || "",
        type: data.supplier_type || "Otro",
        address: data.supplier_address || "",
        phone: data.supplier_phone || "",
        email: data.supplier_email || "",
        building_id: parseInt(buildingId, 10),
        show_to_client: showSupplierToClient
      };
      
      // Solo agregar lat/lng si están disponibles
      if (data.supplier_lat && data.supplier_lng) {
        requestBody.lat = data.supplier_lat;
        requestBody.lng = data.supplier_lng;
      }
      
      devLog("Creando proveedor con datos:", requestBody);
      
      const supplierAuthHeaders = await getAuthHeaders();
      const createResponse = await fetch(`${Habi_API_URL}/api/create-supplier`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...supplierAuthHeaders },
        body: JSON.stringify(requestBody)
      });
      
      if (createResponse.ok) {
        const createResult = await createResponse.json();
        devLog("Proveedor creado:", createResult);
        await fetchSuppliers();
        if (aiResultsBatch.length > 0) {
          await saveExpenseFromBatch(pendingInvoiceData, createResult.supplier_id);
        } else {
          await saveExpenseFromAI(pendingInvoiceData, createResult.supplier_id);
        }
        setShowSupplierToClient(true);
      } else {
        const errorText = await createResponse.text();
        console.error("Error response:", createResponse.status, errorText);
        showToast(`Error al crear el proveedor (${createResponse.status})`, "error");
      }
    } catch (e) {
      console.error("Error creando proveedor:", e);
      showToast("Error al crear el proveedor: " + e.message, "error");
    } finally {
      setCreatingSupplier(false);
    }
  };

  // Función para elegir proveedor manualmente
  const handleChooseSupplierManually = () => {
    if (!pendingInvoiceData) return;
    // Cambiar a modo selección manual dentro del modal
    setPendingInvoiceData({
      ...pendingInvoiceData,
      supplierExists: true,
      supplierId: "",
      manualSupplierSelection: true,
    });
  };

  const resetAIUpload = () => {
    setAIFile(null);
    setAIPreviewUrl(null);
    setAIResult(null);
    setAIError(null);
    setAIProcessing(false);
    setAIFilesBatch([]);
    setAIResultsBatch([]);
    setCurrentBatchIndex(0);
    setBatchProcessingProgress(null);
  };

  const isBatchMode = aiResultsBatch.length > 0;

  return (
    <div style={styles.page}>
      {/* shimmer skeleton styles */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: -400px 0; }
          100% { background-position: 400px 0; }
        }
        .skeleton {
          position: relative;
          overflow: hidden;
          background: #e5e7eb;
        }
        .skeleton::after {
          content: '';
          position: absolute;
          inset: 0;
          background-image: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,.6) 50%, rgba(255,255,255,0) 100%);
          background-size: 400px 100%;
          animation: shimmer 1.2s infinite;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>

      {/* Header con título y acciones */}
      <div style={styles.pageHeader}>
        <ScreenHeader icon={FaCreditCard} title="Gastos" subtitle="Gestión de gastos del edificio" />

        <div style={styles.headerRight} data-tutorial="gastos-filtros">
          {/* Botón Nuevo Gasto - oculto cuando se ven eliminados */}
          {buildingFilter && viewMode === "active" && (
            <button
              style={styles.addBtn}
              onClick={() => setShowNew(true)}
              data-tutorial="gastos-nuevo"
            >
              <FaPlus size={16} /> Nuevo Gasto
            </button>
          )}
          {/* Selector de Año */}
          <div style={styles.filterSelect}>
            <FaCalendarAlt size={12} color={colors.primary} />
            <select
              style={styles.selectInput}
              value={yearFilter}
              onChange={(e) => setYearFilter(Number(e.target.value))}
            >
              {Array.from({ length: 5 }, (_, i) => {
                const year = new Date().getFullYear() - 2 + i;
                return (
                  <option key={year} value={year}>
                    {year}
                  </option>
                );
              })}
            </select>
          </div>

          {/* Selector de Mes */}
          <div style={styles.filterSelect}>
            <FaCalendarAlt size={12} color={colors.primary} />
            <select
              style={styles.selectInput}
              value={monthFilter}
              onChange={(e) => setMonthFilter(Number(e.target.value))}
            >
              {[
                { value: 1, label: "Enero" },
                { value: 2, label: "Febrero" },
                { value: 3, label: "Marzo" },
                { value: 4, label: "Abril" },
                { value: 5, label: "Mayo" },
                { value: 6, label: "Junio" },
                { value: 7, label: "Julio" },
                { value: 8, label: "Agosto" },
                { value: 9, label: "Septiembre" },
                { value: 10, label: "Octubre" },
                { value: 11, label: "Noviembre" },
                { value: 12, label: "Diciembre" }
              ].map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </div>

          {/* Toggle: Activos / Eliminados — Pattern C */}
          <div style={{ display: "flex", gap: 4, background: colors.gray100, borderRadius: radius.sm, padding: 4, border: `1px solid ${colors.gray200}` }}>
            <button
              type="button"
              onClick={() => setViewMode("active")}
              style={{
                padding: "6px 12px",
                border: "none",
                borderRadius: 6,
                background: viewMode === "active" ? colors.primary : "transparent",
                color: viewMode === "active" ? colors.white : colors.gray600,
                boxShadow: viewMode === "active" ? "0 2px 4px rgba(0,128,255,0.3)" : "none",
                fontSize: 13,
                fontWeight: 400,
                cursor: "pointer",
                transition: `all ${transitions.normal}`,
                fontFamily: "inherit",
              }}
            >
              Activos
            </button>
            <button
              type="button"
              onClick={() => setViewMode("deleted")}
              style={{
                padding: "6px 12px",
                border: "none",
                borderRadius: 6,
                background: viewMode === "deleted" ? colors.primary : "transparent",
                color: viewMode === "deleted" ? colors.white : colors.gray600,
                boxShadow: viewMode === "deleted" ? "0 2px 4px rgba(0,128,255,0.3)" : "none",
                fontSize: 13,
                fontWeight: 400,
                cursor: "pointer",
                transition: `all ${transitions.normal}`,
                fontFamily: "inherit",
              }}
            >
              Eliminados
            </button>
          </div>
        </div>
      </div>

      {/* Selector de fondo: caja / reserva / fondos propios, con lo pendiente de cada uno */}
      {viewMode === "active" && (
        <div style={styles.fundTabs} role="tablist" aria-label="Fondo que paga el gasto">
          {fundTabs.filter((t) => t.key === "todos" || t.key === "caja" || t.key === "reserva" || t.count > 0).map((t) => {
            const active = fundFilter === t.key;
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setFundFilter(t.key)}
                style={{ ...styles.fundTab, ...(active ? styles.fundTabActive : null) }}
                title={t.pending > 0 ? `Pendiente: ${formatCurrencyUY(t.pending)}` : "Sin pendientes"}
              >
                <span>{t.label}</span>
                <span style={{ ...styles.fundTabMeta, color: active ? "rgba(255,255,255,0.85)" : colors.gray600 }}>
                  {t.count} · {t.pending > 0 ? `${formatCurrencyUY(t.pending)} pend.` : "al día"}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* KPIs - ocultos cuando se ven eliminados */}
      {viewMode === "active" && (
        <div style={styles.kpis}>
          <div 
            style={{...styles.kpiCard, cursor: 'pointer', border: statusFilter === 'all' ? `2px solid ${colors.primary}` : `1px solid ${colors.gray200}`}}
            onClick={() => setStatusFilter('all')}
            title="Ver todos los gastos"
          >
            <div style={styles.kpiIconWrapper}>
              <FaChartBar size={16} color={colors.primary} />
            </div>
            <div style={styles.kpiContent}>
              <span style={styles.kpiLabel}>Total Gastos</span>
              <span style={styles.kpiValue}>{formatCurrencyUY(totals.total)}</span>
            </div>
          </div>
          <div 
            style={{...styles.kpiCard, cursor: 'pointer', border: statusFilter === 'paid' ? `2px solid ${colors.success}` : `1px solid ${colors.gray200}`}}
            onClick={() => setStatusFilter('paid')}
            title="Filtrar gastos pagados"
          >
            <div style={{ ...styles.kpiIconWrapper, background: `${colors.success}1a` }}>
              <FaCheckCircle size={16} color={colors.success} />
            </div>
            <div style={styles.kpiContent}>
              <span style={styles.kpiLabel}>Pagado en el mes</span>
              <span style={{ ...styles.kpiValue, color: colors.success }}>{formatCurrencyUY(totals.paid)}</span>
            </div>
          </div>
          <div 
            style={{...styles.kpiCard, cursor: 'pointer', border: statusFilter === 'pending' ? `2px solid ${colors.warning}` : `1px solid ${colors.gray200}`}}
            onClick={() => setStatusFilter('pending')}
            title="Filtrar gastos pendientes"
          >
            <div style={styles.kpiContent}>
              <span style={styles.kpiLabel}>Pendiente (todos)</span>
              <span style={{ ...styles.kpiValue, color: colors.warning }}>{formatCurrencyUY(totals.pending)}</span>
            </div>
          </div>
        </div>
      )}
      {/* Gastos sin detalle corto (cargados antes): la IA los resume en una línea */}
      {viewMode === "active" && gastosSinDetalleCorto.length > 0 && (
        <div style={styles.resumirBar}>
          <span>
            {gastosSinDetalleCorto.length === 1 ? "1 gasto no tiene" : `${gastosSinDetalleCorto.length} gastos no tienen`} detalle corto: en la tabla se ve la primera oración de la descripción.
          </span>
          <button type="button" style={{ ...styles.btnSecondary, opacity: resumiendoDetalles ? 0.7 : 1 }} className="habitia-btn-secondary" onClick={resumirDetallesConIA} disabled={resumiendoDetalles}>
            <FaRobot size={13} /> {resumiendoDetalles ? "Resumiendo…" : "Resumir detalles con IA"}
          </button>
        </div>
      )}
      {viewMode === "deleted" && (
        <div style={{ padding: '8px 0', fontSize: 13, color: colors.gray600 }}>
          Gastos eliminados — {items.length} en este período
        </div>
      )}

      {/* Tabla */}
      {!buildingFilter ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyStateIcon}>
            <FaBuilding size={48} color={colors.primary} />
          </div>
          <h3 style={styles.emptyStateTitle}>Selecciona un edificio</h3>
          <p style={styles.emptyStateText}>
            Utiliza el filtro en la barra superior para seleccionar un edificio y ver sus gastos.
          </p>
        </div>
      ) : (
        <div style={styles.table} data-tutorial="gastos-tabla">
          {/* Header - columnas según vista activa o eliminados */}
          <div style={viewMode === "deleted" ? styles.theadDeleted : styles.thead}>
            {activeColumns.map((col) => {
              const showActions = hoveredColumn === col.key || openMenuColumn === col.key || openFilterColumn === col.key || sortKey === col.key;
              const isSortCol = sortKey === col.key;
              const SortIcon = !isSortCol ? FaSort : sortDirection === "asc" ? FaSortUp : FaSortDown;
              return (
                <div
                  key={col.key}
                  data-column-header
                  style={styles.headerCellWithMenu}
                  onMouseEnter={() => setHoveredColumn(col.key)}
                  onMouseLeave={() => setHoveredColumn(null)}
                >
                  {col.label ? <span style={styles.headerLabel}>{col.label}</span> : null}
                  <div style={{...styles.headerActions, opacity: showActions ? 1 : 0}}>
                    <button
                      type="button"
                      style={{...styles.headerIconBtn, opacity: isSortCol ? 1 : 0.85}}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (sortKey === col.key) setSortDirection(d => d === "asc" ? "desc" : "asc");
                        else { setSortKey(col.key); setSortDirection("asc"); }
                      }}
                      title="Ordenar"
                    >
                      <SortIcon size={10} />
                    </button>
                    <button
                      type="button"
                      style={styles.headerIconBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenMenuColumn(openMenuColumn === col.key ? null : col.key);
                        setOpenFilterColumn(null);
                      }}
                      title="Opciones"
                    >
                      <FaEllipsisV size={10} />
                    </button>
                    {openMenuColumn === col.key && (
                      <div data-column-menu style={styles.headerMenu}>
                        <button
                          type="button"
                          style={styles.headerMenuItem}
                          onClick={() => {
                            setOpenMenuColumn(null);
                            setOpenFilterColumn(col.key);
                          }}
                        >
                          Filtrar
                        </button>
                      </div>
                    )}
                    {openFilterColumn === col.key && (
                      <div data-filter-popover style={styles.filterPopover}>
                        <input
                          type="text"
                          placeholder="Buscar..."
                          value={columnFilters[col.key] || ""}
                          onChange={(e) => setColumnFilters((prev) => ({ ...prev, [col.key]: e.target.value }))}
                          style={styles.filterPopoverInput}
                          autoFocus
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            <div style={styles.headerCellSimple} />
          </div>

          {loading ? (
            <>
              {Array.from({ length: 5 }).map((_, idx) => (
                <div key={idx} style={viewMode === "deleted" ? styles.trowDeleted : styles.trow}>
                  {viewMode === "deleted" ? (
                    <>
                      <div className="skeleton" style={{ height: 14, borderRadius: 6, width: 80 }} />
                      <div className="skeleton" style={{ height: 14, borderRadius: 6, width: 100 }} />
                      <div className="skeleton" style={{ height: 14, borderRadius: 6, width: 70 }} />
                      <div className="skeleton" style={{ height: 14, borderRadius: 6, width: 120 }} />
                      <div className="skeleton" style={{ height: 14, borderRadius: 6, width: 90 }} />
                      <div className="skeleton" style={{ height: 20, borderRadius: 999, width: 80 }} />
                      <div className="skeleton" style={{ height: 14, borderRadius: 6, width: 100 }} />
                      <div className="skeleton" style={{ height: 28, borderRadius: 6, width: 64 }} />
                    </>
                  ) : (
                    <>
                      <div className="skeleton" style={{ height: 14, borderRadius: 6, width: 100 }} />
                      <div className="skeleton" style={{ height: 14, borderRadius: 6, width: 90 }} />
                      <div className="skeleton" style={{ height: 14, borderRadius: 6, width: 70 }} />
                      <div className="skeleton" style={{ height: 14, borderRadius: 6, width: 80 }} />
                      <div className="skeleton" style={{ height: 14, borderRadius: 6, width: 80 }} />
                      <div className="skeleton" style={{ height: 14, borderRadius: 6, width: 60 }} />
                      <div className="skeleton" style={{ height: 20, borderRadius: 999, width: 70 }} />
                      <div className="skeleton" style={{ height: 14, borderRadius: 6, width: 50 }} />
                      <div className="skeleton" style={{ height: 14, borderRadius: 6, width: 50 }} />
                      <div className="skeleton" style={{ height: 14, borderRadius: 6, width: 100 }} />
                      <div className="skeleton" style={{ height: 28, borderRadius: 6, width: 80 }} />
                    </>
                  )}
                </div>
              ))}
            </>
          ) : sortedFilteredItems.length === 0 ? (
            <div style={styles.emptyRow}>
              <FaCreditCard size={18} />
              <span>{items.length === 0 ? "No hay pagos ni gastos registrados para este edificio." : "No hay resultados para los filtros aplicados."}</span>
            </div>
          ) : (
            sortedFilteredItems.map((p) => {
              const supplierName = suppliers.find(s => String(s.supplierId) === String(p.supplierid))?.name || "-";
              const incidentInfo = (p.expenseCategory === "incidencia" || p.hasIncident) && p.incidents
                ? incidents.find(i => i.id === p.incidents)
                : null;
              
              const getCellStyle = (col) => {
                const base = { ...styles.tcell };
                if (col.key === "status") {
                  if (p.isDeleted) Object.assign(base, { background: "rgba(100, 116, 139, 0.12)", color: colors.gray700, padding: "4px 10px", borderRadius: 4, whiteSpace: "nowrap", minWidth: 0 });
                  else if (p.isDuplicate) Object.assign(base, { background: "rgba(249, 115, 22, 0.12)", color: colors.warningDark, padding: "4px 10px", borderRadius: 4, whiteSpace: "nowrap", minWidth: 0, fontWeight: 400 });
                  else Object.assign(base, p.paid ? styles.badgeSuccess : styles.badgeWarn);
                }
                if (["receiptNumber", "supplier", "description"].includes(col.key)) Object.assign(base, styles.tcellWrap);
                if (col.key === "supplier") base.fontSize = 13;
                if (col.key === "money") base.textAlign = "center";
                if (col.key === "deletedBy") Object.assign(base, { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 });
                return base;
              };

              const getCellContent = (col) => {
                const val = col.getValue(p);
                if (col.key === "amount") return val;
                if (col.key === "incidents" && val) return <span style={styles.incidentBadge}>{val}</span>;
                // La descripción completa queda a mano al pasar el mouse.
                if (col.key === "description" && val && val !== String(p.description || "").trim()) return <span title={p.description}>{val}</span>;
                return val;
              };
              
              return (
                <div key={p.id} style={{
                  ...(viewMode === "deleted" ? styles.trowDeleted : styles.trow),
                  ...(p.isDuplicate ? { opacity: 0.6, background: "repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(249,115,22,0.04) 10px, rgba(249,115,22,0.04) 20px)" } : {})
                }}>
                  {activeColumns.map((col) => (
                    <div key={col.key} style={getCellStyle(col)}>
                      {getCellContent(col)}
                    </div>
                  ))}
                  <div style={{ display: 'flex', flexDirection: 'row', gap: '8px', justifyContent: 'center' }}>
                    {!p.paid && p.type === "expense" && !p.isDeleted && (
                      <button
                        style={{
                          backgroundColor: colors.success,
                          color: colors.white,
                          border: 'none',
                          padding: '8px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onClick={() => openPaymentModal(p)}
                        title="Pagar"
                      >
                        <FaMoneyBillWave size={14} />
                      </button>
                    )}
                    <button
                      style={{
                        backgroundColor: p.type === "expense" ? colors.primary : colors.gray600,
                        color: colors.white,
                        border: 'none',
                        padding: '8px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      onClick={() => openDetailModal(p)}
                      title="Ver Detalle"
                    >
                      <FaEye size={14} />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}


      {/* Modal nuevo gasto */}
      <AdminModal
        open={showNew}
        onClose={() => { setShowNew(false); resetAIUpload(); }}
        title={`Nuevo Gasto${buildingFilter ? ` - ${buildings.find(b => Number(b.buildingid) === Number(buildingFilter))?.name || ""}` : ""}`}
        icon={<FaPlus />}
        size="wide"
        footer={
          <>
            <button style={styles.btnSecondary} className="habitia-btn-secondary" onClick={() => setShowNew(false)} disabled={saving}>
              Cancelar
            </button>
            <button
              style={{ ...styles.btnPrimary, opacity: saving ? 0.7 : 1 }}
              className="habitia-btn-primary"
              onClick={() => saveExpense()}
              disabled={saving || !buildingFilter}
            >
              {saving ? "Creando..." : "Crear"}
            </button>
          </>
        }
      >

            {!buildingFilter && (
              <div style={styles.alert}>
                ⚠️ Selecciona un edificio primero para crear un gasto
              </div>
            )}

            {/* Sección de carga con IA */}
            <div style={styles.aiSection}>
              <div style={styles.aiSectionHeader}>
                <div style={styles.aiSectionIcon}>
                  <FaRobot size={18} color={colors.primary} />
                </div>
                <div>
                  <h4 style={styles.aiSectionTitle}>Cargar con IA</h4>
                  <p style={styles.aiSectionSubtitle}>Sube una factura y la IA extraerá los datos automáticamente</p>
                </div>
              </div>
              
              <div style={styles.aiSectionBody}>
                <input
                  ref={aiFileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  style={{ display: 'none' }}
                  onChange={handleAIFileSelect}
                />
                <input
                  ref={aiFileInputBatchRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleAIFileSelectBatch}
                />
                
                {!aiFile && !aiProcessing && aiFilesBatch.length === 0 && !batchProcessingProgress && (
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button
                      style={styles.aiUploadBtn}
                      onClick={() => aiFileInputRef.current?.click()}
                      disabled={!buildingId}
                    >
                      <FaCloudUploadAlt size={18} />
                      <span>Una factura</span>
                    </button>
                    <button
                      style={{ ...styles.aiUploadBtn, background: colors.primarySubtle, color: colors.primaryDark, border: `1px dashed ${colors.primaryDark}` }}
                      onClick={() => aiFileInputBatchRef.current?.click()}
                      disabled={!buildingId}
                    >
                      <FaCloudUploadAlt size={18} />
                      <span>Varias facturas</span>
                    </button>
                  </div>
                )}
                
                {aiFile && !aiResult && (
                  <div style={styles.aiFileRow}>
                    <div style={styles.aiFileInfo}>
                      <FaFileInvoice size={20} color={colors.primary} />
                      <span style={styles.aiFileNameSmall}>{aiFile.name}</span>
                    </div>
                    <div style={styles.aiFileActions}>
                      <button
                        style={{
                          ...styles.aiProcessBtn,
                          opacity: aiProcessing ? 0.7 : 1,
                          cursor: aiProcessing ? "not-allowed" : "pointer",
                        }}
                        onClick={processInvoiceWithAI}
                        disabled={aiProcessing}
                      >
                        {aiProcessing ? (
                          <>
                            <FaSpinner size={14} className="spin" />
                            Procesando...
                          </>
                        ) : (
                          <>
                            <FaRobot size={14} />
                            Procesar
                          </>
                        )}
                      </button>
                      <button
                        style={styles.aiCancelBtn}
                        onClick={() => { setAIFile(null); setAIPreviewUrl(null); }}
                        disabled={aiProcessing}
                        aria-label="Quitar archivo"
                      >
                        <FaTimes size={14} />
                      </button>
                    </div>
                  </div>
                )}
                
                {aiFilesBatch.length > 0 && aiResultsBatch.length === 0 && (
                  <div style={styles.aiFileRow}>
                    <div style={styles.aiFileInfo}>
                      <FaFileInvoice size={20} color={colors.primary} />
                      <span style={styles.aiFileNameSmall}>
                        {batchProcessingProgress
                          ? `Procesando factura ${batchProcessingProgress.current} de ${batchProcessingProgress.total}...`
                          : `${aiFilesBatch.length} factura${aiFilesBatch.length > 1 ? "s" : ""} seleccionada${aiFilesBatch.length > 1 ? "s" : ""}`
                        }
                      </span>
                    </div>
                    <div style={styles.aiFileActions}>
                      <button
                        style={{
                          ...styles.aiProcessBtn,
                          opacity: batchProcessingProgress ? 0.7 : 1,
                          cursor: batchProcessingProgress ? "not-allowed" : "pointer",
                        }}
                        onClick={processBatchInvoices}
                        disabled={!!batchProcessingProgress}
                      >
                        {batchProcessingProgress ? (
                          <>
                            <FaSpinner size={14} className="spin" />
                            Procesando...
                          </>
                        ) : (
                          <>
                            <FaRobot size={14} />
                            Procesar todas
                          </>
                        )}
                      </button>
                      <button
                        style={styles.aiCancelBtn}
                        onClick={() => setAIFilesBatch([])}
                        disabled={!!batchProcessingProgress}
                        aria-label="Quitar archivos"
                      >
                        <FaTimes size={14} />
                      </button>
                    </div>
                  </div>
                )}
                
                {aiResult && (
                  <div style={styles.aiSuccessRow}>
                    <FaCheckCircle size={18} color={colors.success} />
                    <span>
                      Datos extraídos correctamente
                      {aiResult.invoice_data?.supplier_validated && (
                        <span style={{ marginLeft: 8, color: colors.success, fontSize: 12 }}>✓ Proveedor validado</span>
                      )}
                    </span>
                    <button
                      style={styles.aiNewFileBtn}
                      onClick={() => { resetAIUpload(); setFieldConfidence({}); }}
                    >
                      Cargar otra
                    </button>
                  </div>
                )}
                
                {/* Leyenda de confianza si hay campos con confianza */}
                {Object.keys(fieldConfidence).length > 0 && (
                  <div style={{
                    display: 'flex',
                    gap: 16,
                    fontSize: 11,
                    padding: '8px 12px',
                    background: colors.gray50,
                    borderRadius: 6,
                    marginTop: 8
                  }}>
                    <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: colors.success, marginRight: 4 }}></span>Alta ≥85%</span>
                    <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: colors.warning, marginRight: 4 }}></span>Media 60-84%</span>
                    <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: colors.error, marginRight: 4 }}></span>Baja &lt;60%</span>
                  </div>
                )}

                {aiError && (
                  <div style={aiErrorType === "warning" ? styles.aiWarningRow : styles.aiErrorRow}>
                    <FaExclamationCircle size={16} color={aiErrorType === "warning" ? colors.warningDark : colors.errorDark} />
                    <span>{aiError}</span>
                    <button
                      style={aiErrorType === "warning" ? styles.aiWarningRetryBtn : styles.aiRetryBtn}
                      onClick={() => { setAIError(null); setAIErrorType("error"); setAIFile(null); }}
                    >
                      Reintentar
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div style={styles.modalBody}>
              {/* Tipo de documento */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Tipo de Documento</label>
                <select style={styles.input} value={form.documentType}
                  onChange={(e) => setForm({ ...form, documentType: e.target.value })}>
                  <option value="factura">Factura</option>
                  <option value="recibo">Recibo</option>
                  <option value="ticket">Ticket</option>
                  <option value="otro">Otro</option>
                </select>
              </div>

              {/* Categoría de gasto */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Categoría</label>
                <select style={styles.input} value={form.expenseCategory}
                  onChange={(e) => {
                    const nextCat = e.target.value;
                    // Default de fondo según categoría: obras e incidencias salen de Reserva por defecto.
                    const defaultSource = (nextCat === "obra" || nextCat === "incidencia") ? "reserva" : "";
                    setForm({ ...form, expenseCategory: nextCat, projectId: "", incidentId: "", sourceFund: defaultSource });
                  }}>
                  <option value="gastos_comunes">Gastos Comunes</option>
                  <option value="obra">Obras y Proyectos</option>
                  <option value="incidencia">Incidencias</option>
                </select>
              </div>

              {/* Fondo que paga (solo para obra e incidencia) — FIN-019 */}
              {(form.expenseCategory === "obra" || form.expenseCategory === "incidencia") && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Fondo que paga *</label>
                  <select style={styles.input} value={form.sourceFund}
                    onChange={(e) => setForm({ ...form, sourceFund: e.target.value })}>
                    <option value="caja">Caja (Gastos Comunes)</option>
                    <option value="reserva">Fondo de Reserva</option>
                    {buildingCustomFunds
                      .filter(f => f.active !== false)
                      .map(f => (
                        <option key={f.id} value={f.id}>{f.name || f.id}</option>
                      ))}
                  </select>
                </div>
              )}

              {/* Proyecto (solo para categoría obra) */}
              {form.expenseCategory === "obra" && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Proyecto / Obra (opcional)</label>
                  <select style={styles.input} value={form.projectId}
                    onChange={(e) => setForm({ ...form, projectId: e.target.value })}>
                    <option value="">Sin proyecto</option>
                    {projects.map(p => (
                      <option key={p.id} value={p.id}>{p.name || p.title || `Proyecto ${p.id}`}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Incidencia (solo para categoría incidencia) */}
              {form.expenseCategory === "incidencia" && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Incidencia *</label>
                  <select style={styles.input} value={form.incidentId}
                    onChange={(e) => setForm({ ...form, incidentId: e.target.value })}>
                    <option value="">Selecciona una incidencia</option>
                    {incidents.map(incident => (
                      <option key={incident.id} value={incident.id}>
                        {incident.incidentid || incident.id} - {incident.title}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Proveedor */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Proveedor {form.documentType === "factura" ? "*" : "(opcional)"}</label>
                <select
                  style={styles.input}
                  value={form.supplierid}
                  onChange={(e) => e.target.value === NEW_SUPPLIER
                    ? setNewSupplier({ target: "form", name: "", rut: "", type: "", telephone: "" })
                    : setForm({ ...form, supplierid: e.target.value })}
                  disabled={!buildingId}
                >
                  <option value="">Selecciona un proveedor</option>
                  {suppliers.map(supplier => (
                    <option key={supplier.supplierId} value={supplier.supplierId}>
                      {supplier.name} - {supplier.type}
                    </option>
                  ))}
                  <option value={NEW_SUPPLIER}>+ Crear proveedor nuevo</option>
                </select>
              </div>

              {/* Número de comprobante */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Número de Comprobante {form.documentType !== "factura" && <span style={{ fontWeight: 400, color: colors.gray400 }}>(opcional)</span>}
                  {fieldConfidence.receiptNumber !== undefined && (
                    <span style={{ 
                      marginLeft: 8, 
                      fontSize: 11, 
                      color: getConfidenceColor('receiptNumber'),
                      fontWeight: 400
                    }}>
                      ({Math.round((fieldConfidence.receiptNumber || 0) * 100)}%)
                    </span>
                  )}
                </label>
                <input
                  style={getInputStyleWithConfidence('receiptNumber')}
                  value={form.receiptNumber}
                  onChange={(e) => setForm({ ...form, receiptNumber: e.target.value })}
                  placeholder="Ej: 001-00012345"
                />
              </div>

              {/* Descripción */}
              <div style={{ gridColumn: "1 / -1", ...styles.formGroup }}>
                <label style={styles.label}>Descripción *</label>
                <textarea
                  style={{...styles.input, minHeight: 80, resize: 'vertical'}}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Descripción del gasto..."
                />
              </div>

              {/* Fecha Alta */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Fecha Alta</label>
                <input
                  type="date"
                  style={{...styles.input, backgroundColor: colors.gray50}}
                  value={new Date().toISOString().split('T')[0]}
                  disabled
                />
              </div>

              {/* Fecha Factura */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Fecha Factura
                  {fieldConfidence.invoiceDate !== undefined && (
                    <span style={{ marginLeft: 8, fontSize: 11, color: getConfidenceColor('invoiceDate'), fontWeight: 400 }}>
                      ({Math.round((fieldConfidence.invoiceDate || 0) * 100)}%)
                    </span>
                  )}
                </label>
                <input
                  type="date"
                  style={getInputStyleWithConfidence('invoiceDate')}
                  value={form.invoiceDate}
                  onChange={(e) => {
                    const newInvoiceDate = e.target.value;
                    // Asegurar que vencimiento >= factura
                    let newMaturity = form.maturity;
                    if (newMaturity && newMaturity < newInvoiceDate) {
                      newMaturity = newInvoiceDate;
                    }
                    setForm({ ...form, invoiceDate: newInvoiceDate, maturity: newMaturity });
                  }}
                />
              </div>

              {/* Fecha Vencimiento */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Fecha Vencimiento
                  {fieldConfidence.maturity !== undefined && (
                    <span style={{ marginLeft: 8, fontSize: 11, color: getConfidenceColor('maturity'), fontWeight: 400 }}>
                      ({Math.round((fieldConfidence.maturity || 0) * 100)}%)
                    </span>
                  )}
                </label>
                <input
                  type="date"
                  style={getInputStyleWithConfidence('maturity')}
                  value={form.maturity}
                  min={form.invoiceDate || undefined}
                  onChange={(e) => setForm({ ...form, maturity: e.target.value })}
                />
              </div>

              {/* RUT */}
              <div style={styles.formGroup}>
                <label style={styles.label}>RUT</label>
                <input
                  style={{
                    ...styles.input, 
                    backgroundColor: colors.gray50,
                    color: colors.gray600,
                    cursor: 'not-allowed'
                  }}
                  value={form.supplierid ? (suppliers.find(s => String(s.supplierId) === String(form.supplierid))?.rut || "") : ""}
                  disabled
                  readOnly
                  placeholder="Selecciona un proveedor"
                />
              </div>

              {/* Moneda */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Moneda</label>
                <select
                  style={styles.input}
                  value={form.money}
                  onChange={(e) => setForm({ ...form, money: e.target.value })}
                >
                  <option value="peso">Peso</option>
                  <option value="dolar">Dólar</option>
                </select>
              </div>

              {/* Subtotal */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Subtotal
                  {fieldConfidence.subtotal !== undefined && (
                    <span style={{ marginLeft: 8, fontSize: 11, color: getConfidenceColor('subtotal'), fontWeight: 400 }}>
                      ({Math.round((fieldConfidence.subtotal || 0) * 100)}%)
                    </span>
                  )}
                </label>
                <input
                  type="number"
                  style={getInputStyleWithConfidence('subtotal', descNetos(form) ? { ...styles.input, backgroundColor: colors.gray50 } : undefined)}
                  value={descNetos(form)?.subtotal ?? form.subtotal}
                  readOnly={Boolean(descNetos(form))}
                  onChange={(e) => {
                    const subtotal = e.target.value;
                    const next = { ...form, subtotal };
                    next.amount = tieneDescuento(next.descuentoTipo, next.descuentoValor)
                      ? computeExpenseTotals({ subtotalBruto: subtotal, ivaBruto: next.iva, descuentoTipo: next.descuentoTipo, descuentoValor: next.descuentoValor }).amount
                      : (Number(subtotal) || 0) + (Number(next.iva) || 0);
                    setForm(next);
                  }}
                  placeholder="0"
                  min={0}
                  step="0.01"
                />
                {descNetos(form) && (
                  <span style={{ fontSize: 11, color: colors.gray500, marginTop: 2 }}>Bruto: {formatCurrencyUY(Number(form.subtotal) || 0)} · neto con descuento</span>
                )}
              </div>

              {/* IVA (valor absoluto) */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  IVA ($)
                  {fieldConfidence.iva !== undefined && (
                    <span style={{ marginLeft: 8, fontSize: 11, color: getConfidenceColor('iva'), fontWeight: 400 }}>
                      ({Math.round((fieldConfidence.iva || 0) * 100)}%)
                    </span>
                  )}
                </label>
                <input
                  type="number"
                  style={getInputStyleWithConfidence('iva', descNetos(form) ? { ...styles.input, backgroundColor: colors.gray50 } : undefined)}
                  value={descNetos(form)?.iva ?? form.iva}
                  readOnly={Boolean(descNetos(form))}
                  onChange={(e) => {
                    const iva = e.target.value;
                    const next = { ...form, iva };
                    next.amount = tieneDescuento(next.descuentoTipo, next.descuentoValor)
                      ? computeExpenseTotals({ subtotalBruto: next.subtotal, ivaBruto: iva, descuentoTipo: next.descuentoTipo, descuentoValor: next.descuentoValor }).amount
                      : (Number(next.subtotal) || 0) + (Number(iva) || 0);
                    setForm(next);
                  }}
                  placeholder="0"
                  min={0}
                  step="0.01"
                />
                {descNetos(form) && (
                  <span style={{ fontSize: 11, color: colors.gray500, marginTop: 2 }}>Bruto: {formatCurrencyUY(Number(form.iva) || 0)} · IVA recalculado sobre el neto</span>
                )}
              </div>

              {/* Descuento sobre la factura (se aplica al neto, el IVA se recalcula) */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Descuento (opcional)</label>
                <div style={{ display: "flex", gap: 8 }}>
                  <select
                    style={{ ...styles.input, flex: "0 0 130px" }}
                    value={form.descuentoTipo}
                    onChange={(e) => {
                      const descuentoTipo = e.target.value;
                      const next = { ...form, descuentoTipo };
                      if (!descuentoTipo) next.descuentoValor = "";
                      next.amount = tieneDescuento(descuentoTipo, next.descuentoValor)
                        ? computeExpenseTotals({ subtotalBruto: next.subtotal, ivaBruto: next.iva, descuentoTipo, descuentoValor: next.descuentoValor }).amount
                        : (Number(next.subtotal) || 0) + (Number(next.iva) || 0);
                      setForm(next);
                    }}
                  >
                    <option value="">Sin descuento</option>
                    <option value="porcentaje">% del subtotal</option>
                    <option value="monto">Monto fijo ($)</option>
                  </select>
                  <input
                    type="number"
                    style={{ ...styles.input, flex: 1 }}
                    value={form.descuentoValor}
                    disabled={!form.descuentoTipo}
                    onChange={(e) => {
                      const descuentoValor = e.target.value;
                      const next = { ...form, descuentoValor };
                      next.amount = tieneDescuento(next.descuentoTipo, descuentoValor)
                        ? computeExpenseTotals({ subtotalBruto: next.subtotal, ivaBruto: next.iva, descuentoTipo: next.descuentoTipo, descuentoValor }).amount
                        : (Number(next.subtotal) || 0) + (Number(next.iva) || 0);
                      setForm(next);
                    }}
                    placeholder={form.descuentoTipo === "porcentaje" ? "Ej. 10" : "Ej. 100"}
                    min={0}
                    step="0.01"
                  />
                </div>
                {tieneDescuento(form.descuentoTipo, form.descuentoValor) && (() => {
                  const r = computeExpenseTotals({ subtotalBruto: form.subtotal, ivaBruto: form.iva, descuentoTipo: form.descuentoTipo, descuentoValor: form.descuentoValor });
                  return (
                    <div style={{ marginTop: 6, fontSize: 12, color: colors.gray600 }}>
                      Descuento: −{formatCurrencyUY(r.descuentoMonto)} · Neto {formatCurrencyUY(r.subtotal)} + IVA {formatCurrencyUY(r.iva)} = <b style={{ color: colors.primary }}>{formatCurrencyUY(r.amount)}</b>
                    </div>
                  );
                })()}
              </div>

              {/* Total */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Total *
                  {fieldConfidence.amount !== undefined && (
                    <span style={{ marginLeft: 8, fontSize: 11, color: getConfidenceColor('amount'), fontWeight: 400 }}>
                      ({Math.round((fieldConfidence.amount || 0) * 100)}%)
                    </span>
                  )}
                </label>
                <input
                  type="number"
                  style={getInputStyleWithConfidence('amount', {...styles.input, backgroundColor: colors.gray50})}
                  value={descNetos(form)?.amount ?? form.amount}
                  readOnly={Boolean(descNetos(form))}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="0"
                  min={0}
                  step="0.01"
                />
              </div>

              {/* Criterio (no aplica para obras, no se distribuyen) */}
              {form.expenseCategory !== "obra" && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Criterio</label>
                  <select
                    style={styles.input}
                    value={form.criteria}
                    onChange={(e) => setForm({ ...form, criteria: e.target.value })}
                    disabled={!buildingFilter}
                  >
                    <option value="">Selecciona un criterio</option>
                    {criteriaExpenses.map(criteria => (
                      <option key={criteria.id} value={criteria.id}>
                        {criteria.criteria || criteria.name || criteria.title || `Criterio ${criteria.id}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Distribución override por gasto (solo para categorías que se distribuyen) */}
              {form.expenseCategory !== "obra" && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Distribución (override)</label>
                  <select style={styles.input} value={form.overrideDistribution}
                    onChange={(e) => setForm({ ...form, overrideDistribution: e.target.value })}>
                    <option value="">Default del edificio</option>
                    <option value="partes_iguales">Partes iguales</option>
                    <option value="alicuotas">Por alícuotas</option>
                    <option value="superficie">Por superficie</option>
                  </select>
                </div>
              )}

              {/* Factura - Adjunto */}
              <div style={{ gridColumn: "1 / -1", ...styles.formGroup }}>
                <label style={styles.label}>Factura - Adjunto</label>
                <div style={styles.fileUpload}>
                  <input
                    type="file"
                    id="bill-upload"
                    style={{ display: 'none' }}
                    onChange={handleFileUpload}
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                  <label htmlFor="bill-upload" style={styles.fileUploadLabel}>
                    <FaUpload size={16} />
                    {form.bill ? form.bill.name : 'Seleccionar archivo'}
                  </label>
                  {form.bill && (
                    <button
                      type="button"
                      style={styles.removeFile}
                      onClick={() => setForm({ ...form, bill: null })}
                      aria-label="Quitar archivo"
                    >
                      <FaTimes size={14} />
                    </button>
                  )}
                </div>
              </div>
            </div>

      </AdminModal>

      {/* Modal Detalle/Edicion de Gasto */}
      <AdminModal
        open={showDetailModal && !!selectedDetailExpense && !!editingExpense}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedDetailExpense(null);
          setEditingExpense(null);
          setPaymentDetail(null);
        }}
        title={selectedDetailExpense?.paid ? "Detalle del Gasto" : "Detalle y Edicion del Gasto"}
        icon={<FaFileInvoice />}
        size="wide"
        hideClose
        footer={
          selectedDetailExpense && editingExpense ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, width: '100%' }}>
                {/* Boton Eliminar - solo si no esta pagado ni eliminado */}
                {!selectedDetailExpense.paid && !selectedDetailExpense.isDeleted ? (
                  <button
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '8px 16px', borderRadius: 8, border: `1px solid ${colors.errorLight}`,
                      background: colors.errorLight, color: colors.errorDark, fontWeight: 400, fontSize: 13,
                      cursor: deletingExpense ? 'not-allowed' : 'pointer',
                      opacity: deletingExpense ? 0.6 : 1,
                      transition: 'all 0.2s',
                    }}
                    onClick={handleDeleteExpense}
                    disabled={deletingExpense || savingEdit}
                    title="Eliminar gasto"
                  >
                    <FaTrash size={13} />
                    {deletingExpense ? "Eliminando..." : "Eliminar"}
                  </button>
                ) : <div />}

                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    style={styles.btnSecondary}
                    className="habitia-btn-secondary"
                    onClick={() => {
                      setShowDetailModal(false);
                      setSelectedDetailExpense(null);
                      setEditingExpense(null);
                      setPaymentDetail(null);
                    }}
                    disabled={savingEdit}
                  >
                    {selectedDetailExpense.paid ? "Cerrar" : "Cancelar"}
                  </button>
                  {!selectedDetailExpense.isDeleted && (
                    <button
                      style={{ ...styles.btnPrimary, opacity: savingEdit ? 0.7 : 1 }}
                      className="habitia-btn-primary"
                      onClick={handleSaveEdit}
                      disabled={savingEdit || !buildingFilter}
                    >
                      {savingEdit ? "Guardando..." : "Guardar"}
                    </button>
                  )}
                </div>
            </div>
          ) : null
        }
      >
        {selectedDetailExpense && editingExpense && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing[8] }}>
              {/* Sección Factura y Gasto */}
              <div>
                <h2 style={{ fontSize: typography.sizes.lg, fontWeight: 400, color: colors.black, margin: `0 0 ${spacing[4]}px 0` }}>Factura y Gasto</h2>
                <div style={{ ...cardBase, padding: spacing[5] }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    {/* Tipo de Documento */}
                    <FormField label="Tipo de Documento" htmlFor="edit-documentType">
                      <select id="edit-documentType" className="habitia-input" style={{padding: '8px 10px', fontSize: 13}} value={editingExpense.documentType} onChange={(e) => setEditingExpense({ ...editingExpense, documentType: e.target.value })} disabled={selectedDetailExpense.paid}>
                        <option value="factura">Factura</option>
                        <option value="recibo">Recibo</option>
                        <option value="ticket">Ticket</option>
                        <option value="otro">Otro</option>
                      </select>
                    </FormField>
                    {/* Categoría */}
                    <FormField label="Categoría" htmlFor="edit-category">
                      <select id="edit-category" className="habitia-input" style={{padding: '8px 10px', fontSize: 13}} value={editingExpense.expenseCategory || "gastos_comunes"} onChange={(e) => {
                        const nextCat = e.target.value;
                        const defaultSource = (nextCat === "obra" || nextCat === "incidencia") ? "reserva" : "";
                        setEditingExpense({ ...editingExpense, expenseCategory: nextCat, projectId: "", incidentId: "", sourceFund: defaultSource });
                      }} disabled={selectedDetailExpense.paid}>
                        <option value="gastos_comunes">Gastos Comunes</option>
                        <option value="obra">Obras y Proyectos</option>
                        <option value="incidencia">Incidencias</option>
                      </select>
                    </FormField>
                    {/* Proveedor - obligatorio solo para facturas */}
                    <FormField label="Proveedor" htmlFor="edit-supplier" required={editingExpense.documentType === "factura"} hint={editingExpense.documentType !== "factura" ? "(opcional)" : undefined}>
                      <select id="edit-supplier" className="habitia-input" style={{padding: '8px 10px', fontSize: 13}} value={editingExpense.supplierid} onChange={(e) => e.target.value === NEW_SUPPLIER ? setNewSupplier({ target: "edit", name: "", rut: "", type: "", telephone: "" }) : setEditingExpense({ ...editingExpense, supplierid: e.target.value })} disabled={selectedDetailExpense.paid || !buildingId}>
                        <option value="">Selecciona un proveedor</option>
                        {suppliers.map(supplier => (
                          <option key={supplier.supplierId} value={supplier.supplierId}>{supplier.name} - {supplier.type}</option>
                        ))}
                        <option value={NEW_SUPPLIER}>+ Crear proveedor nuevo</option>
                      </select>
                    </FormField>
                    {/* RUT - autocompletado desde proveedor */}
                    <FormField label="RUT" htmlFor="edit-rut">
                      <input id="edit-rut" className="habitia-input" style={{padding: '8px 10px', fontSize: 13, backgroundColor: colors.gray50, color: colors.gray600}} value={editingExpense.supplierid ? (suppliers.find(s => String(s.supplierId) === String(editingExpense.supplierid))?.rut || "") : ""} disabled readOnly placeholder="Selecciona un proveedor" />
                    </FormField>
                    {/* Número de Comprobante */}
                    <FormField label="Número de Comprobante" htmlFor="edit-receiptNumber" fullWidth hint={editingExpense.documentType !== "factura" ? "(opcional)" : undefined}>
                      <input id="edit-receiptNumber" className="habitia-input" style={{padding: '8px 10px', fontSize: 13}} value={editingExpense.receiptNumber} onChange={(e) => setEditingExpense({ ...editingExpense, receiptNumber: e.target.value })} placeholder="Ej: 001-00012345" disabled={selectedDetailExpense.paid} />
                    </FormField>
                    {/* Descripción */}
                    <FormField label="Descripción" htmlFor="edit-description" required fullWidth>
                      <textarea id="edit-description" className="habitia-input" style={{padding: '8px 10px', fontSize: 13, minHeight: 60, resize: 'vertical'}} value={editingExpense.description} onChange={(e) => setEditingExpense({ ...editingExpense, description: e.target.value })} placeholder="Descripción del gasto..." disabled={selectedDetailExpense.paid} />
                    </FormField>
                    {/* Detalle corto - lo que se ve en la tabla; editable incluso pagado (no toca montos) */}
                    <FormField
                      label="Detalle corto (tabla)"
                      htmlFor="edit-shortDescription"
                      fullWidth
                      hint={`${(editingExpense.shortDescription || "").length}/${MAX_DETALLE_CORTO} · Es lo que se ve en la tabla. Vacío: se usa la descripción recortada.`}
                    >
                      <input id="edit-shortDescription" className="habitia-input" style={{padding: '8px 10px', fontSize: 13}} value={editingExpense.shortDescription || ""} maxLength={MAX_DETALLE_CORTO} onChange={(e) => setEditingExpense({ ...editingExpense, shortDescription: e.target.value })} placeholder="Ej: OSE agua ago–set 2026 (96 m3)" disabled={selectedDetailExpense.isDeleted} />
                    </FormField>
                    {/* Criterio - siempre editable, incluso en gastos pagados */}
                    <FormField label="Criterio" htmlFor="edit-criteria">
                      <select id="edit-criteria" className="habitia-input" style={{padding: '8px 10px', fontSize: 13}} value={editingExpense.criteria} onChange={(e) => setEditingExpense({ ...editingExpense, criteria: e.target.value })} disabled={!buildingFilter}>
                        <option value="">Selecciona un criterio</option>
                        {criteriaExpenses.map(cr => (
                          <option key={cr.id} value={cr.id}>{cr.criteria || cr.name || cr.title || `Criterio ${cr.id}`}</option>
                        ))}
                      </select>
                    </FormField>
                    {/* Fondo que paga - solo para obra/incidencia */}
                    {(editingExpense.expenseCategory === "obra" || editingExpense.expenseCategory === "incidencia") && (
                      <FormField label="Fondo que paga" htmlFor="edit-sourceFund" required>
                        <select id="edit-sourceFund" className="habitia-input" style={{padding: '8px 10px', fontSize: 13}} value={editingExpense.sourceFund || ""} onChange={(e) => setEditingExpense({ ...editingExpense, sourceFund: e.target.value })} disabled={selectedDetailExpense.paid}>
                          <option value="caja">Caja (Gastos Comunes)</option>
                          <option value="reserva">Fondo de Reserva</option>
                          {buildingCustomFunds
                            .filter(f => f.active !== false)
                            .map(f => (
                              <option key={f.id} value={f.id}>{f.name || f.id}</option>
                            ))}
                        </select>
                      </FormField>
                    )}
                    {/* Proyecto - solo para categoría obra */}
                    {editingExpense.expenseCategory === "obra" && (
                      <FormField label="Proyecto (opcional)" htmlFor="edit-project">
                        <select id="edit-project" className="habitia-input" style={{padding: '8px 10px', fontSize: 13}} value={editingExpense.projectId || ""} onChange={(e) => setEditingExpense({ ...editingExpense, projectId: e.target.value })} disabled={selectedDetailExpense.paid}>
                          <option value="">Sin proyecto</option>
                          {projects.map(p => (
                            <option key={p.id} value={p.id}>{p.name || p.title || `Proyecto ${p.id}`}</option>
                          ))}
                        </select>
                      </FormField>
                    )}
                    {/* Incidencia - solo para categoría incidencia */}
                    {editingExpense.expenseCategory === "incidencia" && (
                      <FormField label="Incidencia" htmlFor="edit-incident" required>
                        <select id="edit-incident" className="habitia-input" style={{padding: '8px 10px', fontSize: 13}} value={editingExpense.incidentId || ""} onChange={(e) => setEditingExpense({ ...editingExpense, incidentId: e.target.value })} disabled={selectedDetailExpense.paid}>
                          <option value="">Selecciona incidencia</option>
                          {incidents.map(inc => (
                            <option key={inc.id} value={inc.id}>#{inc.incidentid || inc.id} - {inc.title}</option>
                          ))}
                        </select>
                      </FormField>
                    )}
                    {/* Fecha Factura */}
                    <FormField label="Fecha Factura" htmlFor="edit-invoiceDate">
                      <input type="date" id="edit-invoiceDate" className="habitia-input" style={{padding: '8px 10px', fontSize: 13}} value={editingExpense.invoiceDate} onChange={(e) => setEditingExpense({ ...editingExpense, invoiceDate: e.target.value })} disabled={selectedDetailExpense.paid} />
                    </FormField>
                    {/* Fecha Vencimiento */}
                    <FormField label="Fecha Vencimiento" htmlFor="edit-maturity">
                      <input type="date" id="edit-maturity" className="habitia-input" style={{padding: '8px 10px', fontSize: 13}} value={editingExpense.maturity} onChange={(e) => setEditingExpense({ ...editingExpense, maturity: e.target.value })} disabled={selectedDetailExpense.paid} />
                    </FormField>
                    {/* Moneda */}
                    <FormField label="Moneda" htmlFor="edit-money">
                      <select id="edit-money" className="habitia-input" style={{padding: '8px 10px', fontSize: 13}} value={editingExpense.money} onChange={(e) => setEditingExpense({ ...editingExpense, money: e.target.value })} disabled={selectedDetailExpense.paid}>
                        <option value="peso">Peso</option>
                        <option value="dolar">Dólar</option>
                      </select>
                    </FormField>
                    {/* Subtotal */}
                    <FormField label="Subtotal" htmlFor="edit-subtotal">
                      <input type="number" id="edit-subtotal" className="habitia-input" style={{padding: '8px 10px', fontSize: 13}} value={descNetos(editingExpense)?.subtotal ?? editingExpense.subtotal} readOnly={Boolean(descNetos(editingExpense))} onChange={(e) => { const subtotal = e.target.value; const next = { ...editingExpense, subtotal }; next.amount = tieneDescuento(next.descuentoTipo, next.descuentoValor) ? computeExpenseTotals({ subtotalBruto: subtotal, ivaBruto: next.iva, descuentoTipo: next.descuentoTipo, descuentoValor: next.descuentoValor }).amount : (Number(subtotal) || 0) + (Number(next.iva) || 0); setEditingExpense(next); }} placeholder="0" min={0} step="0.01" disabled={selectedDetailExpense.paid} />
                    </FormField>
                    {/* IVA (valor absoluto) */}
                    <FormField label="IVA ($)" htmlFor="edit-iva">
                      <input type="number" id="edit-iva" className="habitia-input" style={{padding: '8px 10px', fontSize: 13}} value={descNetos(editingExpense)?.iva ?? editingExpense.iva} readOnly={Boolean(descNetos(editingExpense))} onChange={(e) => { const iva = e.target.value; const next = { ...editingExpense, iva }; next.amount = tieneDescuento(next.descuentoTipo, next.descuentoValor) ? computeExpenseTotals({ subtotalBruto: next.subtotal, ivaBruto: iva, descuentoTipo: next.descuentoTipo, descuentoValor: next.descuentoValor }).amount : (Number(next.subtotal) || 0) + (Number(iva) || 0); setEditingExpense(next); }} placeholder="0" min={0} step="0.01" disabled={selectedDetailExpense.paid} />
                    </FormField>
                    {/* Descuento */}
                    <FormField label="Descuento (opcional)" htmlFor="edit-descuento">
                      <div style={{ display: "flex", gap: 8 }}>
                        <select id="edit-descuento" className="habitia-input" style={{ padding: '8px 10px', fontSize: 13, flex: "0 0 120px" }} value={editingExpense.descuentoTipo || ""} disabled={selectedDetailExpense.paid} onChange={(e) => { const descuentoTipo = e.target.value; const next = { ...editingExpense, descuentoTipo }; if (!descuentoTipo) next.descuentoValor = ""; next.amount = tieneDescuento(descuentoTipo, next.descuentoValor) ? computeExpenseTotals({ subtotalBruto: next.subtotal, ivaBruto: next.iva, descuentoTipo, descuentoValor: next.descuentoValor }).amount : (Number(next.subtotal) || 0) + (Number(next.iva) || 0); setEditingExpense(next); }}>
                          <option value="">Sin descuento</option>
                          <option value="porcentaje">%</option>
                          <option value="monto">$</option>
                        </select>
                        <input type="number" className="habitia-input" style={{ padding: '8px 10px', fontSize: 13, flex: 1 }} value={editingExpense.descuentoValor || ""} disabled={selectedDetailExpense.paid || !editingExpense.descuentoTipo} onChange={(e) => { const descuentoValor = e.target.value; const next = { ...editingExpense, descuentoValor }; next.amount = tieneDescuento(next.descuentoTipo, descuentoValor) ? computeExpenseTotals({ subtotalBruto: next.subtotal, ivaBruto: next.iva, descuentoTipo: next.descuentoTipo, descuentoValor }).amount : (Number(next.subtotal) || 0) + (Number(next.iva) || 0); setEditingExpense(next); }} placeholder={editingExpense.descuentoTipo === "porcentaje" ? "10" : "100"} min={0} step="0.01" />
                      </div>
                    </FormField>
                    {/* Total */}
                    <FormField label="Total" htmlFor="edit-amount" required>
                      <input type="number" id="edit-amount" className="habitia-input" style={{padding: '8px 10px', fontSize: 13, backgroundColor: colors.gray50}} value={descNetos(editingExpense)?.amount ?? editingExpense.amount} readOnly={Boolean(descNetos(editingExpense))} onChange={(e) => setEditingExpense({ ...editingExpense, amount: e.target.value })} placeholder="0" min={0} step="0.01" disabled={selectedDetailExpense.paid} />
                    </FormField>
                    {/* Factura - Adjunto */}
                    <FormField label="Factura - Adjunto" htmlFor="bill-upload-edit" fullWidth>
                      {editingExpense.billUrl ? (
                        <a href={editingExpense.billUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px', background: colors.successLight, border: `1px solid ${colors.success}`, borderRadius: 8, color: colors.successDark, textDecoration: 'none', fontWeight: 400, fontSize: 13 }}>
                          <FaFileInvoice size={16} /> Ver factura
                        </a>
                      ) : (
                        <span style={{ color: colors.gray400, fontSize: 13 }}>Sin factura adjunta</span>
                      )}
                      {!selectedDetailExpense.paid && (
                        <div style={styles.fileUpload}>
                          <input type="file" id="bill-upload-edit" style={{ display: 'none' }} onChange={(e) => { const file = e.target.files[0]; if (file) { if (!validateUploadFile(file)) { e.target.value = ""; return; } setEditingExpense({ ...editingExpense, bill: file }); } }} accept=".pdf,.jpg,.jpeg,.png" />
                          <label htmlFor="bill-upload-edit" style={styles.fileUploadLabel}>
                            <FaUpload size={16} /> {editingExpense.bill ? editingExpense.bill.name : 'Subir nueva factura'}
                          </label>
                          {editingExpense.bill && (
                            <button type="button" style={styles.removeFile} onClick={() => setEditingExpense({ ...editingExpense, bill: null })} aria-label="Quitar archivo"><FaTimes size={14} /></button>
                          )}
                        </div>
                      )}
                    </FormField>
                    {/* Estado */}
                    <FormField label="Estado" htmlFor="edit-status" fullWidth>
                      <div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
                        <StatusBadge status={selectedDetailExpense.paid ? "pagado" : "pendiente"} />
                        {selectedDetailExpense.isDuplicate && (
                          <StatusBadge status="pendiente_verificacion" label="Duplicado (no se cuenta en totales)" />
                        )}
                      </div>
                    </FormField>
                  </div>
                </div>
              </div>

              {/* Sección Pago y Comprobante (solo cuando está pagado) */}
              {selectedDetailExpense.paid && (
                <div>
                  <h2 style={{ fontSize: typography.sizes.lg, fontWeight: 400, color: colors.black, margin: `0 0 ${spacing[4]}px 0` }}>Pago y Comprobante</h2>
                  <div style={{ ...cardBase, padding: spacing[5] }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      {paymentDetail && pagoEdit ? (
                        <>
                          <FormField label="Fecha de pago" htmlFor="detail-paymentDate" required>
                            <input type="date" id="detail-paymentDate" className="habitia-input" style={{ padding: '8px 10px', fontSize: 13 }} value={pagoEdit.paymentDate} onChange={(e) => setPagoEdit({ ...pagoEdit, paymentDate: e.target.value })} disabled={savingEdit || selectedDetailExpense.isDeleted} />
                          </FormField>
                          <FormField label="Método de pago" htmlFor="detail-method" required>
                            <select id="detail-method" className="habitia-input" style={{ padding: '8px 10px', fontSize: 13 }} value={pagoEdit.method} onChange={(e) => setPagoEdit({ ...pagoEdit, method: e.target.value })} disabled={savingEdit || selectedDetailExpense.isDeleted}>
                              {!pagoEdit.method && <option value="">Elegí un método</option>}
                              {[...METODOS_PAGO_GASTO, ...(paymentDetail.method && !METODOS_PAGO_GASTO.includes(paymentDetail.method) ? [paymentDetail.method] : [])].map((m) => (
                                <option key={m} value={m}>{m}</option>
                              ))}
                            </select>
                          </FormField>
                          <FormField
                            label="Monto pagado ($)"
                            htmlFor="detail-amount"
                            required
                            fullWidth
                            hint={pagoEdit.amount !== "" && Math.abs((Number(pagoEdit.amount) || 0) - (Number(selectedDetailExpense.amount) || 0)) >= 0.01
                              ? `No coincide con el total del gasto (${formatCurrencyUY(selectedDetailExpense.amount)}). El estado de cuenta del fondo descuenta el monto pagado.`
                              : undefined}
                          >
                            <input type="number" id="detail-amount" className="habitia-input" style={{ padding: '8px 10px', fontSize: 13 }} value={pagoEdit.amount} onChange={(e) => setPagoEdit({ ...pagoEdit, amount: e.target.value })} min={0} step="0.01" disabled={savingEdit || selectedDetailExpense.isDeleted} />
                          </FormField>
                          {selectedDetailExpense.convertedToUYU && (
                            <FormField label="Conversión de moneda" htmlFor="detail-conversion" fullWidth>
                              <div style={{ backgroundColor: colors.primaryLighter, borderRadius: radius.sm, padding: '10px 12px', fontSize: 13, color: colors.textSecondary }}>
                                <div style={{ marginBottom: 4 }}>
                                  <strong>Monto original:</strong> US$ {selectedDetailExpense.originalAmountUSD?.toLocaleString("es-UY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </div>
                                <div style={{ marginBottom: 4 }}>
                                  <strong>Tipo de cambio:</strong> {selectedDetailExpense.exchangeRateUsed?.toFixed(2)} (venta)
                                </div>
                                <div>
                                  <strong>Fecha del TC:</strong> {selectedDetailExpense.exchangeRateDate || "—"}
                                </div>
                              </div>
                            </FormField>
                          )}
                          <FormField
                            label="Comprobante de pago"
                            htmlFor="detail-proof-upload"
                            fullWidth
                            hint={pagoEdit.proof && paymentDetail.proof ? "Al guardar reemplaza al actual; el anterior queda en el historial del pago." : undefined}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              {paymentDetail.proof ? (
                                <a
                                  href={paymentDetail.proof}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    padding: '10px 16px',
                                    background: colors.primaryLighter,
                                    borderRadius: radius.sm,
                                    color: colors.primary,
                                    textDecoration: 'none',
                                    fontWeight: 400,
                                    fontSize: 13
                                  }}
                                >
                                  <FaEye size={16} />
                                  Ver comprobante
                                </a>
                              ) : (
                                <span style={{ color: colors.gray400, fontSize: 13 }}>Sin comprobante</span>
                              )}
                              {!selectedDetailExpense.isDeleted && (
                                <div style={styles.fileUpload}>
                                  <input
                                    type="file"
                                    id="detail-proof-upload"
                                    style={{ display: 'none' }}
                                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                                    disabled={savingEdit}
                                    onChange={(e) => {
                                      const file = e.target.files[0];
                                      e.target.value = "";
                                      if (!file || !validateUploadFile(file)) return;
                                      setPagoEdit({ ...pagoEdit, proof: file });
                                    }}
                                  />
                                  <label htmlFor="detail-proof-upload" style={styles.fileUploadLabel}>
                                    <FaUpload size={14} />
                                    {pagoEdit.proof ? pagoEdit.proof.name : (paymentDetail.proof ? "Reemplazar comprobante" : "Subir comprobante")}
                                  </label>
                                  {pagoEdit.proof && (
                                    <button type="button" style={styles.removeFile} onClick={() => setPagoEdit({ ...pagoEdit, proof: null })} aria-label="Quitar el comprobante nuevo" disabled={savingEdit}>
                                      <FaTimes size={14} />
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </FormField>
                          {!selectedDetailExpense.isDeleted && (
                            <div style={{ gridColumn: '1 / -1', color: colors.gray600, fontSize: 12 }}>
                              Si corregís algún dato del pago, al guardar te mostramos qué cambia y te pedimos confirmación.
                            </div>
                          )}
                        </>
                      ) : (
                        <div style={{ gridColumn: '1 / -1', color: colors.gray600, fontSize: 13 }}>
                          {paymentDetailEstado === "sin_registro"
                            ? "Este gasto se marcó como pagado sin un registro de pago, así que no hay fecha, método ni comprobante para editar."
                            : paymentDetailEstado === "error"
                              ? "No se pudieron cargar los datos del pago. Cerrá y volvé a abrir el gasto."
                              : "Cargando datos del pago..."}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
        )}
      </AdminModal>

      {/* Modal Pago */}
      <AdminModal
        open={showPaymentModal && !!selectedExpense}
        onClose={() => {
          setShowPaymentModal(false);
          setSelectedExpense(null);
          setPaymentForm({ method: "Transferencia", proof: null, paymentDate: new Date().toISOString().split('T')[0] });
        }}
        title="Registrar Pago"
        icon={<FaCreditCard />}
        size="wide"
        footer={
          <>
            <button
              style={styles.btnSecondary}
              className="habitia-btn-secondary"
              onClick={() => {
                setShowPaymentModal(false);
                setSelectedExpense(null);
                setPaymentForm({ method: "Transferencia", proof: null, paymentDate: new Date().toISOString().split('T')[0] });
              }}
              disabled={savingPayment}
            >
              Cancelar
            </button>
            <button
              style={{ ...styles.btnPrimary, opacity: savingPayment ? 0.7 : 1 }}
              className="habitia-btn-primary"
              onClick={savePayment}
              disabled={savingPayment}
            >
              {savingPayment ? "Guardando..." : "Pagar"}
            </button>
          </>
        }
      >
        {selectedExpense && (
            <div style={styles.modalBody}>
              {/* Descripcion del pago */}
              <div style={{ gridColumn: "1 / -1", ...styles.formGroup }}>
                <label style={styles.label}>Descripcion del pago</label>
                <input
                  style={{...styles.input, backgroundColor: colors.gray50, cursor: 'not-allowed'}}
                  value={selectedExpense.description || ""}
                  disabled
                  readOnly
                />
              </div>

              {/* Importe a pagar */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Importe a pagar</label>
                <input
                  style={{...styles.input, backgroundColor: colors.gray50, cursor: 'not-allowed'}}
                  value={formatCurrencyUY(selectedExpense.amount)}
                  disabled
                  readOnly
                />
              </div>

              {/* Payment date — editable por el usuario, permite fechas pasadas y futuras */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Fecha de pago *</label>
                <input
                  type="date"
                  style={styles.input}
                  value={paymentForm.paymentDate}
                  onChange={(e) => setPaymentForm({ ...paymentForm, paymentDate: e.target.value })}
                />
              </div>

              {/* Payment user */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Usuario</label>
                <input
                  style={{...styles.input, backgroundColor: colors.gray50, cursor: 'not-allowed'}}
                  value={auth.currentUser?.email || "desconocido"}
                  disabled
                  readOnly
                />
              </div>

              {/* Metodo de pago */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Metodo de pago *</label>
                <select
                  style={styles.input}
                  value={paymentForm.method}
                  onChange={(e) => setPaymentForm({ ...paymentForm, method: e.target.value })}
                >
                  <option value="Transferencia">Transferencia</option>
                  <option value="Tarjeta">Tarjeta</option>
                  <option value="Efectivo">Efectivo</option>
                </select>
              </div>

              {/* Vencimiento */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Vencimiento</label>
                <input
                  type="date"
                  style={{...styles.input, backgroundColor: colors.gray50, cursor: 'not-allowed'}}
                  value={toDateStr(selectedExpense.maturity) || ""}
                  disabled
                  readOnly
                />
              </div>

              {/* Comprobante de pago */}
              <div style={{ gridColumn: "1 / -1", ...styles.formGroup }}>
                <label style={styles.label}>Comprobante de pago</label>
                <div style={styles.fileUpload}>
                  <input
                    type="file"
                    id="payment-proof-upload"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        if (!validateUploadFile(file)) { e.target.value = ""; return; }
                        setPaymentForm({ ...paymentForm, proof: file });
                      }
                    }}
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                  <label htmlFor="payment-proof-upload" style={styles.fileUploadLabel}>
                    <FaUpload size={16} />
                    {paymentForm.proof ? paymentForm.proof.name : 'Seleccionar archivo'}
                  </label>
                  {paymentForm.proof && (
                    <button
                      type="button"
                      style={styles.removeFile}
                      onClick={() => setPaymentForm({ ...paymentForm, proof: null })}
                      aria-label="Quitar archivo"
                    >
                      <FaTimes size={14} />
                    </button>
                  )}
                </div>
              </div>
            </div>
        )}
      </AdminModal>

      {/* Modal de Validación de Datos Extraídos por IA */}
      {pendingInvoiceData && (
      <AdminModal
        open={showSupplierDecisionModal}
        onClose={() => {
          setShowSupplierDecisionModal(false);
          setPendingInvoiceData(null);
          setFieldConfidence({});
          if (isBatchMode) {
            setAIResultsBatch([]);
            setCurrentBatchIndex(0);
          }
        }}
        title="Validar Datos Extraídos por IA"
        subtitle={isBatchMode ? `Factura ${currentBatchIndex + 1} de ${aiResultsBatch.length}` : undefined}
        icon={<FaRobot />}
        size="wide"
        footer={
          <>
            <button
              className="habitia-btn-secondary"
              style={styles.btnSecondary}
              onClick={() => {
                setShowSupplierDecisionModal(false);
                setPendingInvoiceData(null);
                setFieldConfidence({});
                if (isBatchMode) {
                  setAIResultsBatch([]);
                  setCurrentBatchIndex(0);
                }
              }}
            >
              Cancelar
            </button>

            {pendingInvoiceData && !pendingInvoiceData.supplierExists && !isBatchMode && (
              <button
                className="habitia-btn-secondary"
                style={{...styles.btnSecondary, borderColor: colors.primary, color: colors.primary}}
                onClick={handleChooseSupplierManually}
                disabled={creatingSupplier}
              >
                Usar proveedor existente
              </button>
            )}

            <button
              className="habitia-btn-primary"
              style={{...styles.btnPrimary, opacity: (creatingSupplier || saving) ? 0.7 : 1}}
              onClick={() => {
                if (pendingInvoiceData?.supplierExists && pendingInvoiceData?.supplierId) {
                  if (isBatchMode) {
                    saveExpenseFromBatch(pendingInvoiceData, pendingInvoiceData.supplierId);
                  } else {
                    saveExpenseFromAI(pendingInvoiceData, pendingInvoiceData.supplierId);
                  }
                } else {
                  handleCreateSupplierFromAI();
                }
              }}
              disabled={creatingSupplier || saving || !pendingInvoiceData?.invoiceData?.supplier_name || (pendingInvoiceData?.manualSupplierSelection && !pendingInvoiceData?.supplierId)}
            >
              {(creatingSupplier || saving) ? (
                <><FaSpinner className="spin" size={14} style={{marginRight: 6}} />Procesando...</>
              ) : isBatchMode ? (
                currentBatchIndex >= aiResultsBatch.length - 1 ? (
                  <>✓ Confirmar y Finalizar</>
                ) : (
                  <>Confirmar y Siguiente</>
                )
              ) : pendingInvoiceData?.supplierExists ? (
                <>✓ Confirmar y Crear Gasto</>
              ) : (
                <><FaPlus size={14} style={{marginRight: 6}} />Crear Proveedor y Crear Gasto</>
              )}
            </button>
          </>
        }
      >
              {/* A1 — Banner de sugerencias del clasificador (si aplica) */}
              {pendingInvoiceData?.classifySuggestion && (
                (pendingInvoiceData.classifySuggestion.expense_category?.value ||
                 pendingInvoiceData.classifySuggestion.criteria_expenses?.value ||
                 pendingInvoiceData.classifySuggestion.source_fund?.value) && (
                  <div style={{
                    padding: '12px 14px',
                    marginBottom: 12,
                    background: colors.primaryLighter,
                    border: `1px solid ${colors.primaryLight}`,
                    borderRadius: 8,
                    fontSize: 13,
                    color: colors.gray900,
                  }}>
                    <div style={{ marginBottom: 6, color: colors.primary }}>
                      💡 Sugerencia IA · método: {pendingInvoiceData.classifySuggestion.method}
                      {pendingInvoiceData.classifySuggestion.samples_evaluated > 0 &&
                        ` · ${pendingInvoiceData.classifySuggestion.samples_evaluated} factura(s) similares en histórico`}
                    </div>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: colors.gray700 }}>
                      {pendingInvoiceData.classifySuggestion.expense_category?.value && (
                        <span>
                          <strong>Categoría:</strong> {pendingInvoiceData.classifySuggestion.expense_category.value}
                          {" "}
                          <span style={{
                            color: pendingInvoiceData.classifySuggestion.expense_category.confidence >= 0.8 ? colors.success : colors.warning,
                          }}>
                            ({Math.round(pendingInvoiceData.classifySuggestion.expense_category.confidence * 100)}%)
                          </span>
                        </span>
                      )}
                      {pendingInvoiceData.classifySuggestion.criteria_expenses?.value && (
                        <span>
                          <strong>Criterio:</strong> {String(pendingInvoiceData.classifySuggestion.criteria_expenses.value).slice(0, 24)}
                          {" "}
                          <span style={{
                            color: pendingInvoiceData.classifySuggestion.criteria_expenses.confidence >= 0.8 ? colors.success : colors.warning,
                          }}>
                            ({Math.round(pendingInvoiceData.classifySuggestion.criteria_expenses.confidence * 100)}%)
                          </span>
                        </span>
                      )}
                      {pendingInvoiceData.classifySuggestion.source_fund?.value && (
                        <span>
                          <strong>Fondo:</strong> {pendingInvoiceData.classifySuggestion.source_fund.value}
                          {" "}
                          <span style={{
                            color: pendingInvoiceData.classifySuggestion.source_fund.confidence >= 0.8 ? colors.success : colors.warning,
                          }}>
                            ({Math.round(pendingInvoiceData.classifySuggestion.source_fund.confidence * 100)}%)
                          </span>
                        </span>
                      )}
                    </div>
                    <div style={{ marginTop: 6, fontSize: 11, color: colors.gray600 }}>
                      Las sugerencias con confianza ≥80% se aplican automáticamente al guardar. Podés editar el gasto después si necesitás cambiar algo.
                    </div>
                  </div>
                )
              )}

              {/* Leyenda de confianza + Estado proveedor en una fila */}
              <div style={{
                display: 'flex',
                gap: 12,
                marginBottom: 16,
                flexWrap: 'wrap',
                alignItems: 'stretch'
              }}>
                {/* Leyenda */}
                <div style={{ 
                  flex: 1,
                  minWidth: 280,
                  padding: '10px 14px', 
                  background: colors.gray50,
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  fontSize: 11
                }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: colors.success }}></span>
                    Alta (≥85%)
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: colors.warning }}></span>
                    Media (60-84%)
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: colors.error }}></span>
                    Baja (&lt;60%)
                  </span>
                </div>
                {/* Estado */}
                <div style={{ 
                  padding: '8px 14px', 
                  background: pendingInvoiceData.supplierExists ? colors.successLight : colors.warningLight,
                  borderRadius: 8,
                  border: `1px solid ${pendingInvoiceData.supplierExists ? colors.success : colors.warning}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 13,
                  fontWeight: 400,
                  color: pendingInvoiceData.supplierExists ? colors.successDark : colors.warningDark
                }}>
                  {pendingInvoiceData.manualSupplierSelection
                  ? '🔍 Selección manual'
                  : pendingInvoiceData.supplierExists ? '✓ Proveedor encontrado' : '⚠ Proveedor nuevo'}
                </div>
              </div>

              {/* Selector manual de proveedor */}
              {pendingInvoiceData.manualSupplierSelection && (
                <div style={{
                  padding: '12px 16px',
                  background: colors.primarySubtle,
                  borderRadius: 8,
                  border: `1px solid ${colors.primaryLight}`,
                  marginBottom: 16,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12
                }}>
                  <label style={{ fontSize: 13, fontWeight: 400, color: colors.primaryDark, whiteSpace: 'nowrap' }}>Proveedor *</label>
                  <select
                    style={{...styles.input, padding: '8px 10px', fontSize: 13, flex: 1}}
                    value={pendingInvoiceData.supplierId || ""}
                    onChange={(e) => setPendingInvoiceData({
                      ...pendingInvoiceData,
                      supplierId: e.target.value
                    })}
                  >
                    <option value="">Selecciona un proveedor</option>
                    {suppliers.map(s => (
                      <option key={s.supplierId} value={s.supplierId}>
                        {s.name} - {s.type}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Contenedor Proveedor */}
              <div style={{
                background: colors.white,
                borderRadius: radius.md,
                border: `1px solid ${colors.gray200}`,
                marginBottom: 16,
                overflow: 'hidden'
              }}>
                <div style={{
                  background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
                  padding: '10px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}>
                  <FaBuilding color={colors.white} size={14} />
                  <span style={{ color: colors.white, fontWeight: 400, fontSize: 13 }}>Datos del Proveedor</span>
                  {pendingInvoiceData.invoiceData.supplier_validated && (
                    <span style={{ marginLeft: 'auto', fontSize: 11, color: colors.successLight }}>✓ Validado</span>
                  )}
                </div>
                <div style={{ padding: 16 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div style={{...styles.formGroup, margin: 0}}>
                      <label style={{...styles.label, fontSize: 11, marginBottom: 4}}>
                        Nombre *
                        {fieldConfidence.supplier_name !== undefined && (
                          <span style={{ marginLeft: 6, fontSize: 10, color: getConfidenceColor('supplier_name') }}>
                            ({Math.round((fieldConfidence.supplier_name || 0) * 100)}%)
                          </span>
                        )}
                      </label>
                      <input
                        style={{...getInputStyleWithConfidence('supplier_name'), padding: '8px 10px', fontSize: 13}}
                        value={pendingInvoiceData.invoiceData.supplier_name || ""}
                        onChange={(e) => setPendingInvoiceData({
                          ...pendingInvoiceData,
                          invoiceData: { ...pendingInvoiceData.invoiceData, supplier_name: e.target.value }
                        })}
                      />
                    </div>
                    <div style={{...styles.formGroup, margin: 0}}>
                      <label style={{...styles.label, fontSize: 11, marginBottom: 4}}>
                        RUT
                        {fieldConfidence.supplier_rut !== undefined && (
                          <span style={{ marginLeft: 6, fontSize: 10, color: getConfidenceColor('supplier_rut') }}>
                            ({Math.round((fieldConfidence.supplier_rut || 0) * 100)}%)
                          </span>
                        )}
                      </label>
                      <input
                        style={{
                          ...getInputStyleWithConfidence('supplier_rut'),
                          padding: '8px 10px',
                          fontSize: 13,
                          ...(pendingInvoiceData.invoiceData.supplier_rut && validateRutUY(pendingInvoiceData.invoiceData.supplier_rut).valid ? { borderColor: colors.success, backgroundColor: colors.successLight } : {})
                        }}
                        value={pendingInvoiceData.invoiceData.supplier_rut || ""}
                        onChange={(e) => setPendingInvoiceData({
                          ...pendingInvoiceData,
                          invoiceData: { ...pendingInvoiceData.invoiceData, supplier_rut: e.target.value }
                        })}
                      />
                      {pendingInvoiceData.invoiceData.supplier_rut && validateRutUY(pendingInvoiceData.invoiceData.supplier_rut).valid && (
                        <span style={{ fontSize: 11, color: colors.success, marginTop: 4, fontWeight: 400, display: 'block' }}>RUT validado</span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                    <div style={{...styles.formGroup, margin: 0}}>
                      <label style={{...styles.label, fontSize: 11, marginBottom: 4}}>
                        Tipo
                        {fieldConfidence.supplier_type !== undefined && (
                          <span style={{ marginLeft: 6, fontSize: 10, color: getConfidenceColor('supplier_type') }}>
                            ({Math.round((fieldConfidence.supplier_type || 0) * 100)}%)
                          </span>
                        )}
                      </label>
                      <select
                        style={{...getInputStyleWithConfidence('supplier_type'), padding: '8px 10px', fontSize: 13}}
                        value={pendingInvoiceData.invoiceData.supplier_type || "Otro"}
                        onChange={(e) => setPendingInvoiceData({
                          ...pendingInvoiceData,
                          invoiceData: { ...pendingInvoiceData.invoiceData, supplier_type: e.target.value }
                        })}
                      >
                        <option value="Servicios Públicos">Servicios Públicos</option>
                        <option value="Telecomunicaciones">Telecomunicaciones</option>
                        <option value="Seguros">Seguros</option>
                        <option value="Mantenimiento">Mantenimiento</option>
                        <option value="Jardinería">Jardinería</option>
                        <option value="Limpieza">Limpieza</option>
                        <option value="Seguridad">Seguridad</option>
                        <option value="Administración">Administración</option>
                        <option value="Construcción">Construcción</option>
                        <option value="Otro">Otro</option>
                      </select>
                    </div>
                    <div style={{...styles.formGroup, margin: 0}}>
                      <label style={{...styles.label, fontSize: 11, marginBottom: 4}}>
                        Dirección (escribí para buscar)
                        {fieldConfidence.supplier_address !== undefined && (
                          <span style={{ marginLeft: 6, fontSize: 10, color: getConfidenceColor('supplier_address') }}>
                            ({Math.round((fieldConfidence.supplier_address || 0) * 100)}%)
                          </span>
                        )}
                      </label>
                      <input
                        ref={supplierAddressInputRef}
                        style={{...getInputStyleWithConfidence('supplier_address'), padding: '8px 10px', fontSize: 13}}
                        value={pendingInvoiceData.invoiceData.supplier_address || ""}
                        onChange={(e) => {
                          setPendingInvoiceData({
                            ...pendingInvoiceData,
                            invoiceData: { 
                              ...pendingInvoiceData.invoiceData, 
                              supplier_address: e.target.value,
                              supplier_lat: null,
                              supplier_lng: null
                            }
                          });
                        }}
                        placeholder="Escribí para buscar dirección..."
                      />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 12 }}>
                    <div style={{...styles.formGroup, margin: 0}}>
                      <label style={{...styles.label, fontSize: 11, marginBottom: 4}}>
                        Teléfono
                        {fieldConfidence.supplier_phone !== undefined && (
                          <span style={{ marginLeft: 6, fontSize: 10, color: getConfidenceColor('supplier_phone') }}>
                            ({Math.round((fieldConfidence.supplier_phone || 0) * 100)}%)
                          </span>
                        )}
                      </label>
                      <input
                        style={{...getInputStyleWithConfidence('supplier_phone'), padding: '8px 10px', fontSize: 13}}
                        value={pendingInvoiceData.invoiceData.supplier_phone || ""}
                        onChange={(e) => {
                          const onlyNumbers = e.target.value.replace(/[^0-9]/g, '');
                          setPendingInvoiceData({
                            ...pendingInvoiceData,
                            invoiceData: { ...pendingInvoiceData.invoiceData, supplier_phone: onlyNumbers }
                          });
                        }}
                        placeholder="Solo números"
                      />
                    </div>
                    <div style={{...styles.formGroup, margin: 0}}>
                      <label style={{...styles.label, fontSize: 11, marginBottom: 4}}>
                        Email
                        {fieldConfidence.supplier_email !== undefined && (
                          <span style={{ marginLeft: 6, fontSize: 10, color: getConfidenceColor('supplier_email') }}>
                            ({Math.round((fieldConfidence.supplier_email || 0) * 100)}%)
                          </span>
                        )}
                      </label>
                      <input
                        style={{...getInputStyleWithConfidence('supplier_email'), padding: '8px 10px', fontSize: 13}}
                        type="email"
                        value={pendingInvoiceData.invoiceData.supplier_email || ""}
                        onChange={(e) => setPendingInvoiceData({
                          ...pendingInvoiceData,
                          invoiceData: { ...pendingInvoiceData.invoiceData, supplier_email: e.target.value }
                        })}
                      />
                    </div>
                    <div style={{...styles.formGroup, margin: 0, display: 'flex', alignItems: 'flex-end'}}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer', padding: '8px 0' }}>
                        <input
                          type="checkbox"
                          checked={showSupplierToClient}
                          onChange={(e) => setShowSupplierToClient(e.target.checked)}
                          style={{ width: 14, height: 14 }}
                        />
                        Visible a residentes
                      </label>
                    </div>
                  </div>
                  
                  {/* Geolocalización del proveedor */}
                  <div style={{ 
                    marginTop: 12, 
                    padding: '10px 12px', 
                    background: pendingInvoiceData.invoiceData.supplier_lat ? colors.successLight : colors.warningLight,
                    borderRadius: 8,
                    border: `1px solid ${pendingInvoiceData.invoiceData.supplier_lat ? colors.success : colors.warning}`
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 11, fontWeight: 400, color: pendingInvoiceData.invoiceData.supplier_lat ? colors.successDark : colors.warningDark, display: 'flex', alignItems: 'center', gap: 6 }}>
                        📍 Geolocalización
                      </span>
                      {pendingInvoiceData.invoiceData.supplier_lat && pendingInvoiceData.invoiceData.supplier_lng ? (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <span style={{ fontSize: 10, color: colors.success }}>✓ Ubicación encontrada</span>
                          <a 
                            href={`https://www.google.com/maps?q=${pendingInvoiceData.invoiceData.supplier_lat},${pendingInvoiceData.invoiceData.supplier_lng}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: 10, color: colors.primary, textDecoration: 'none', fontWeight: 400 }}
                          >
                            Ver en mapa ↗
                          </a>
                        </div>
                      ) : (
                        <span style={{ fontSize: 10, color: colors.warningDark }}>
                          ⚠ Editá el campo Dirección para buscar ubicación
                        </span>
                      )}
                    </div>
                    {pendingInvoiceData.invoiceData.supplier_lat && pendingInvoiceData.invoiceData.supplier_lng && (
                      <div style={{ marginTop: 6, fontSize: 11, color: colors.gray700 }}>
                        Coordenadas: {pendingInvoiceData.invoiceData.supplier_lat.toFixed(6)}, {pendingInvoiceData.invoiceData.supplier_lng.toFixed(6)}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Contenedor Factura */}
              <div style={{
                background: colors.white,
                borderRadius: radius.md,
                border: `1px solid ${colors.gray200}`,
                overflow: 'hidden'
              }}>
                <div style={{
                  background: `linear-gradient(135deg, ${colors.success} 0%, ${colors.successDark} 100%)`,
                  padding: '10px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}>
                  <FaFileInvoice color={colors.white} size={14} />
                  <span style={{ color: colors.white, fontWeight: 400, fontSize: 13 }}>Datos de la Factura</span>
                </div>
                <div style={{ padding: 16 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                    <div style={{...styles.formGroup, margin: 0}}>
                      <label style={{...styles.label, fontSize: 11, marginBottom: 4}}>
                        Nº Comprobante
                        {fieldConfidence.receiptNumber !== undefined && (
                          <span style={{ marginLeft: 6, fontSize: 10, color: getConfidenceColor('receiptNumber') }}>
                            ({Math.round((fieldConfidence.receiptNumber || 0) * 100)}%)
                          </span>
                        )}
                      </label>
                      <input
                        style={{...getInputStyleWithConfidence('receiptNumber'), padding: '8px 10px', fontSize: 13}}
                        value={pendingInvoiceData.invoiceData.receipt_number || ""}
                        onChange={(e) => setPendingInvoiceData({
                          ...pendingInvoiceData,
                          invoiceData: { ...pendingInvoiceData.invoiceData, receipt_number: e.target.value }
                        })}
                      />
                    </div>
                    <div style={{...styles.formGroup, margin: 0}}>
                      <label style={{...styles.label, fontSize: 11, marginBottom: 4}}>
                        Fecha Factura
                        {fieldConfidence.invoiceDate !== undefined && (
                          <span style={{ marginLeft: 4, fontSize: 10, color: getConfidenceColor('invoiceDate') }}>
                            ({Math.round((fieldConfidence.invoiceDate || 0) * 100)}%)
                          </span>
                        )}
                      </label>
                      <input
                        type="date"
                        style={{...getInputStyleWithConfidence('invoiceDate'), padding: '8px 10px', fontSize: 13}}
                        value={pendingInvoiceData.invoiceData.invoice_date || ""}
                        onChange={(e) => {
                          const newDate = e.target.value;
                          let newMaturity = pendingInvoiceData.invoiceData.maturity_date;
                          if (newMaturity && newMaturity < newDate) newMaturity = newDate;
                          setPendingInvoiceData({
                            ...pendingInvoiceData,
                            invoiceData: { ...pendingInvoiceData.invoiceData, invoice_date: newDate, maturity_date: newMaturity }
                          });
                        }}
                      />
                    </div>
                    <div style={{...styles.formGroup, margin: 0}}>
                      <label style={{...styles.label, fontSize: 11, marginBottom: 4}}>
                        Vencimiento
                        {fieldConfidence.maturity !== undefined && (
                          <span style={{ marginLeft: 4, fontSize: 10, color: getConfidenceColor('maturity') }}>
                            ({Math.round((fieldConfidence.maturity || 0) * 100)}%)
                          </span>
                        )}
                      </label>
                      <input
                        type="date"
                        style={{...getInputStyleWithConfidence('maturity'), padding: '8px 10px', fontSize: 13}}
                        value={pendingInvoiceData.invoiceData.maturity_date || ""}
                        min={pendingInvoiceData.invoiceData.invoice_date || undefined}
                        onChange={(e) => setPendingInvoiceData({
                          ...pendingInvoiceData,
                          invoiceData: { ...pendingInvoiceData.invoiceData, maturity_date: e.target.value }
                        })}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginTop: 12 }}>
                    <div style={{...styles.formGroup, margin: 0}}>
                      <label style={{...styles.label, fontSize: 11, marginBottom: 4}}>
                        Moneda
                        {fieldConfidence.currency !== undefined && (
                          <span style={{ marginLeft: 4, fontSize: 10, color: getConfidenceColor('currency') }}>
                            ({Math.round((fieldConfidence.currency || 0) * 100)}%)
                          </span>
                        )}
                      </label>
                      <select
                        style={{...getInputStyleWithConfidence('currency'), padding: '8px 10px', fontSize: 13}}
                        value={pendingInvoiceData.invoiceData.currency || "peso"}
                        onChange={(e) => setPendingInvoiceData({
                          ...pendingInvoiceData,
                          invoiceData: { ...pendingInvoiceData.invoiceData, currency: e.target.value }
                        })}
                      >
                        <option value="peso">UYU</option>
                        <option value="dolar">USD</option>
                      </select>
                    </div>
                    <div style={{...styles.formGroup, margin: 0}}>
                      <label style={{...styles.label, fontSize: 11, marginBottom: 4}}>
                        Subtotal
                        {fieldConfidence.subtotal !== undefined && (
                          <span style={{ marginLeft: 4, fontSize: 10, color: getConfidenceColor('subtotal') }}>
                            ({Math.round((fieldConfidence.subtotal || 0) * 100)}%)
                          </span>
                        )}
                      </label>
                      <input
                        type="number"
                        style={{...getInputStyleWithConfidence('subtotal'), padding: '8px 10px', fontSize: 13}}
                        value={pendingInvoiceData.invoiceData.subtotal || ""}
                        onChange={(e) => setPendingInvoiceData({
                          ...pendingInvoiceData,
                          invoiceData: { ...pendingInvoiceData.invoiceData, subtotal: Number(e.target.value) }
                        })}
                      />
                    </div>
                    <div style={{...styles.formGroup, margin: 0}}>
                      <label style={{...styles.label, fontSize: 11, marginBottom: 4}}>
                        IVA ($)
                        {fieldConfidence.iva !== undefined && (
                          <span style={{ marginLeft: 4, fontSize: 10, color: getConfidenceColor('iva') }}>
                            ({Math.round((fieldConfidence.iva || 0) * 100)}%)
                          </span>
                        )}
                      </label>
                      <input
                        type="number"
                        style={{...getInputStyleWithConfidence('iva'), padding: '8px 10px', fontSize: 13}}
                        value={pendingInvoiceData.invoiceData.iva_amount || ""}
                        onChange={(e) => setPendingInvoiceData({
                          ...pendingInvoiceData,
                          invoiceData: { ...pendingInvoiceData.invoiceData, iva_amount: Number(e.target.value) }
                        })}
                      />
                    </div>
                    <div style={{...styles.formGroup, margin: 0}}>
                      <label style={{...styles.label, fontSize: 11, marginBottom: 4}}>
                        Total
                        {fieldConfidence.amount !== undefined && (
                          <span style={{ marginLeft: 4, fontSize: 10, color: getConfidenceColor('amount') }}>
                            ({Math.round((fieldConfidence.amount || 0) * 100)}%)
                          </span>
                        )}
                      </label>
                      <input
                        type="number"
                        style={{...getInputStyleWithConfidence('amount'), padding: '8px 10px', fontSize: 13}}
                        value={pendingInvoiceData.invoiceData.total || ""}
                        onChange={(e) => setPendingInvoiceData({
                          ...pendingInvoiceData,
                          invoiceData: { ...pendingInvoiceData.invoiceData, total: Number(e.target.value) }
                        })}
                      />
                    </div>
                  </div>
                  <div style={{...styles.formGroup, margin: '12px 0 0 0'}}>
                    <label style={{...styles.label, fontSize: 11, marginBottom: 4}}>
                      Descripción
                      {fieldConfidence.description !== undefined && (
                        <span style={{ marginLeft: 6, fontSize: 10, color: getConfidenceColor('description') }}>
                          ({Math.round((fieldConfidence.description || 0) * 100)}%)
                        </span>
                      )}
                    </label>
                    <textarea
                      style={{...getInputStyleWithConfidence('description'), padding: '8px 10px', fontSize: 13, minHeight: 50, resize: 'vertical'}}
                      value={pendingInvoiceData.invoiceData.description || ""}
                      onChange={(e) => setPendingInvoiceData({
                        ...pendingInvoiceData,
                        invoiceData: { ...pendingInvoiceData.invoiceData, description: e.target.value }
                      })}
                    />
                  </div>
                  <div style={{...styles.formGroup, margin: '12px 0 0 0'}}>
                    <label htmlFor="ai-description-short" style={{...styles.label, fontSize: 11, marginBottom: 4}}>
                      Detalle corto para la tabla ({(pendingInvoiceData.invoiceData.description_short || "").length}/{MAX_DETALLE_CORTO})
                    </label>
                    <input
                      id="ai-description-short"
                      style={{...styles.input, padding: '8px 10px', fontSize: 13}}
                      maxLength={MAX_DETALLE_CORTO}
                      value={pendingInvoiceData.invoiceData.description_short || ""}
                      placeholder="Si lo dejás vacío se usa la descripción recortada"
                      onChange={(e) => setPendingInvoiceData({
                        ...pendingInvoiceData,
                        invoiceData: { ...pendingInvoiceData.invoiceData, description_short: e.target.value }
                      })}
                    />
                  </div>
                </div>
              </div>

              {/* Opciones del Gasto */}
              <div style={{
                background: colors.white,
                borderRadius: radius.md,
                border: `1px solid ${colors.gray200}`,
                marginTop: 16,
                overflow: 'hidden'
              }}>
                <div style={{
                  background: `linear-gradient(135deg, ${colors.info} 0%, ${colors.infoDark} 100%)`,
                  padding: '10px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}>
                  <span style={{ color: colors.white, fontWeight: 400, fontSize: 13 }}>Opciones del Gasto</span>
                </div>
                <div style={{ padding: 16 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div style={{...styles.formGroup, margin: 0}}>
                      <label style={{...styles.label, fontSize: 11, marginBottom: 4}}>Tipo de Documento</label>
                      <select
                        style={{...styles.input, padding: '8px 10px', fontSize: 13}}
                        value={pendingInvoiceData.documentType || "factura"}
                        onChange={(e) => setPendingInvoiceData({
                          ...pendingInvoiceData,
                          documentType: e.target.value
                        })}
                      >
                        <option value="factura">Factura</option>
                        <option value="recibo">Recibo</option>
                        <option value="ticket">Ticket</option>
                        <option value="otro">Otro</option>
                      </select>
                    </div>
                    <div style={{...styles.formGroup, margin: 0}}>
                      <label style={{...styles.label, fontSize: 11, marginBottom: 4}}>Categoría</label>
                      <select
                        style={{...styles.input, padding: '8px 10px', fontSize: 13}}
                        value={pendingInvoiceData.expenseCategory || "gastos_comunes"}
                        onChange={(e) => {
                          const nextCat = e.target.value;
                          // Igual que el alta manual: obras e incidencias salen de Reserva por defecto.
                          setPendingInvoiceData({
                            ...pendingInvoiceData,
                            expenseCategory: nextCat,
                            projectId: "",
                            incidentId: "",
                            sourceFund: (nextCat === "obra" || nextCat === "incidencia") ? (pendingInvoiceData.sourceFund || "reserva") : "",
                          });
                        }}
                      >
                        <option value="gastos_comunes">Gastos Comunes</option>
                        <option value="obra">Obras y Proyectos</option>
                        <option value="incidencia">Incidencias</option>
                      </select>
                    </div>
                    <div style={{...styles.formGroup, margin: 0}}>
                      <label style={{...styles.label, fontSize: 11, marginBottom: 4}}>Criterio</label>
                      <select
                        style={{...styles.input, padding: '8px 10px', fontSize: 13}}
                        value={pendingInvoiceData.criteria || ""}
                        onChange={(e) => setPendingInvoiceData({
                          ...pendingInvoiceData,
                          criteria: e.target.value
                        })}
                        disabled={!buildingFilter}
                      >
                        <option value="">Selecciona un criterio</option>
                        {criteriaExpenses.map(cr => (
                          <option key={cr.id} value={cr.id}>
                            {cr.criteria || cr.name || cr.title || `Criterio ${cr.id}`}
                          </option>
                        ))}
                      </select>
                    </div>
                    {pendingInvoiceData.expenseCategory !== "obra" && (
                      <div style={{...styles.formGroup, margin: 0}}>
                        <label style={{...styles.label, fontSize: 11, marginBottom: 4}}>Distribución</label>
                        <select
                          style={{...styles.input, padding: '8px 10px', fontSize: 13}}
                          value={pendingInvoiceData.overrideDistribution || ""}
                          onChange={(e) => setPendingInvoiceData({
                            ...pendingInvoiceData,
                            overrideDistribution: e.target.value
                          })}
                        >
                          <option value="">Default del edificio</option>
                          <option value="partes_iguales">Partes iguales</option>
                          <option value="alicuotas">Por alícuotas</option>
                          <option value="superficie">Por superficie</option>
                        </select>
                      </div>
                    )}
                  </div>
                  {(pendingInvoiceData.expenseCategory === "obra" || pendingInvoiceData.expenseCategory === "incidencia") && (
                    <div style={{...styles.formGroup, margin: '12px 0 0 0'}}>
                      <label style={{...styles.label, fontSize: 11, marginBottom: 4}} htmlFor="ai-sourceFund">Fondo que paga *</label>
                      <select
                        id="ai-sourceFund"
                        style={{...styles.input, padding: '8px 10px', fontSize: 13}}
                        value={pendingInvoiceData.sourceFund || "reserva"}
                        onChange={(e) => setPendingInvoiceData({ ...pendingInvoiceData, sourceFund: e.target.value })}
                      >
                        <option value="caja">Caja (Gastos Comunes)</option>
                        <option value="reserva">Fondo de Reserva</option>
                        {buildingCustomFunds
                          .filter(f => f.active !== false)
                          .map(f => (
                            <option key={f.id} value={f.id}>{f.name || f.id}</option>
                          ))}
                      </select>
                    </div>
                  )}
                  {pendingInvoiceData.expenseCategory === "obra" && (
                    <div style={{...styles.formGroup, margin: '12px 0 0 0'}}>
                      <label style={{...styles.label, fontSize: 11, marginBottom: 4}}>Proyecto (opcional)</label>
                      <select
                        style={{...styles.input, padding: '8px 10px', fontSize: 13}}
                        value={pendingInvoiceData.projectId || ""}
                        onChange={(e) => setPendingInvoiceData({
                          ...pendingInvoiceData,
                          projectId: e.target.value
                        })}
                      >
                        <option value="">Sin proyecto</option>
                        {projects.map(p => (
                          <option key={p.id} value={p.id}>
                            {p.name || p.title || `Proyecto ${p.id}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {pendingInvoiceData.expenseCategory === "incidencia" && (
                    <div style={{...styles.formGroup, margin: '12px 0 0 0'}}>
                      <label style={{...styles.label, fontSize: 11, marginBottom: 4}}>Incidencia *</label>
                      <select
                        style={{...styles.input, padding: '8px 10px', fontSize: 13}}
                        value={pendingInvoiceData.incidentId || ""}
                        onChange={(e) => setPendingInvoiceData({
                          ...pendingInvoiceData,
                          incidentId: e.target.value
                        })}
                      >
                        <option value="">Selecciona incidencia</option>
                        {incidents.map(inc => (
                          <option key={inc.id} value={inc.id}>
                            #{inc.incidentid || inc.id} - {inc.title}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>

      </AdminModal>
      )}

      {/* Alta rápida de proveedor (desde Nuevo Gasto y edición; la IA tiene su propio flujo) */}
      <AdminModal
        open={!!newSupplier}
        onClose={() => !creatingQuickSupplier && setNewSupplier(null)}
        title="Nuevo proveedor"
        size="narrow"
        footer={
          <>
            <button style={styles.btnSecondary} className="habitia-btn-secondary" onClick={() => setNewSupplier(null)} disabled={creatingQuickSupplier}>Cancelar</button>
            <button style={{ ...styles.btnPrimary, opacity: creatingQuickSupplier ? 0.7 : 1 }} onClick={handleCreateSupplier} disabled={creatingQuickSupplier}>
              {creatingQuickSupplier ? "Creando..." : "Crear y usar"}
            </button>
          </>
        }
      >
        {newSupplier && (
          <div>
            <FormField label="Nombre" htmlFor="ns-name" required>
              <input id="ns-name" className="habitia-input" autoFocus value={newSupplier.name}
                onChange={(e) => setNewSupplier({ ...newSupplier, name: e.target.value })}
                placeholder="Ej: Cerrajería Gauna" />
            </FormField>
            <FormField label="RUT" htmlFor="ns-rut" hint="(opcional; si ya existe, se reutiliza ese proveedor)">
              <input id="ns-rut" className="habitia-input" inputMode="numeric" value={newSupplier.rut}
                onChange={(e) => setNewSupplier({ ...newSupplier, rut: e.target.value })}
                placeholder="12 dígitos" />
            </FormField>
            <FormField label="Rubro" htmlFor="ns-type" hint="(opcional)">
              <input id="ns-type" className="habitia-input" value={newSupplier.type}
                onChange={(e) => setNewSupplier({ ...newSupplier, type: e.target.value })}
                placeholder="Ej: Cerrajería, Limpieza, Electricidad" />
            </FormField>
            <FormField label="Teléfono" htmlFor="ns-tel" hint="(opcional)">
              <input id="ns-tel" type="tel" className="habitia-input" value={newSupplier.telephone}
                onChange={(e) => setNewSupplier({ ...newSupplier, telephone: e.target.value })} />
            </FormField>
          </div>
        )}
      </AdminModal>
    </div>
  );
};

// Contenido del diálogo de confirmación al corregir el pago de un gasto.
const CambiosDelPago = ({ cambios, yaRepartido }) => (
  <div>
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: typography.sizes.sm }}>
      <thead>
        <tr>
          {["Dato", "Antes", "Después"].map((t) => (
            <th key={t} scope="col" style={{ textAlign: "left", fontWeight: 400, color: colors.gray600, padding: "6px 8px", borderBottom: `1px solid ${colors.gray200}` }}>{t}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {cambios.map((c) => (
          <tr key={c.campo}>
            <th scope="row" style={{ textAlign: "left", fontWeight: 400, color: colors.gray900, padding: "6px 8px", borderBottom: `1px solid ${colors.gray100}` }}>{c.etiqueta}</th>
            <td style={{ padding: "6px 8px", color: colors.gray600, borderBottom: `1px solid ${colors.gray100}` }}>{c.antes}</td>
            <td style={{ padding: "6px 8px", color: colors.primary, borderBottom: `1px solid ${colors.gray100}` }}>{c.despues}</td>
          </tr>
        ))}
      </tbody>
    </table>
    {yaRepartido && (
      <p style={{ margin: `${spacing[3]}px 0 0`, fontSize: typography.sizes.sm, color: colors.warningDark }}>
        Este gasto ya se repartió en un cierre: el cambio se ve en el estado de cuenta del fondo, pero no cambia lo que se les cobró a las unidades.
      </p>
    )}
  </div>
);

// Columnas de la tabla de gastos activos (mismo orden que TABLE_COLUMNS + acciones).
const GRILLA_GASTOS =
  "minmax(95px, 0.7fr) minmax(95px, 1.3fr) minmax(40px, 0.3fr) minmax(80px, 0.6fr) minmax(88px, 0.62fr) minmax(88px, 0.62fr) minmax(88px, 0.62fr) minmax(85px, 0.6fr) minmax(60px, 0.44fr) minmax(52px, 0.4fr) minmax(110px, 1.4fr) minmax(64px, 0.45fr)";

const toDateStr = (d) => {
  if (!d) return "";
  if (typeof d === "string") return d;
  if (typeof d === "number") return new Date(d).toISOString().slice(0, 10);
  if (d?.toDate) return d.toDate().toISOString().slice(0, 10);
  return "";
};

const toTimestamp = (d) => {
  if (!d) return 0;
  if (typeof d === "number") return d;
  if (d?.toDate) return d.toDate().getTime();
  try { return new Date(d).getTime(); } catch { return 0; }
};

const mapMethod = (m) =>
  m === "card" ? "Tarjeta" : m === "cash" ? "Efectivo" : "Transferencia";

const styles = {
  fundTabs: { display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 },
  resumirBar: {
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap",
    padding: "10px 14px", marginBottom: 12, borderRadius: radius.sm,
    background: colors.primaryLighter, color: colors.gray700, fontSize: typography.sizes.sm,
  },
  fundTab: {
    display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2,
    padding: "8px 14px", borderRadius: 10, border: `1px solid ${colors.gray200}`,
    background: colors.white, color: colors.gray900, cursor: "pointer", fontFamily: "inherit",
    fontSize: 13, fontWeight: 400, lineHeight: 1.2,
  },
  fundTabActive: { background: colors.primary, borderColor: colors.primary, color: colors.white },
  fundTabMeta: { fontSize: 11 },
  // Contenedor principal de la página
  page: {
    padding: spacing[6],
    background: colors.bgMain,
    minHeight: "100vh",
    position: "relative",
    fontFamily: typography.fontFamily,
  },
  pageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: spacing[3],
  },
  headerTitleSection: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  pageTitle: {
    fontFamily: typography.fontFamily,
    fontSize: typography.sizes.xl,
    fontWeight: 400,
    color: colors.primary,
    margin: 0,
    letterSpacing: "-0.01em",
  },
  pageSubtitle: {
    fontSize: typography.sizes.base,
    color: colors.gray600,
    margin: 0,
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: spacing[3],
  },
  addBtn: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    background: colors.primary,
    border: "none",
    borderRadius: radius.sm,
    padding: "10px 18px",
    cursor: "pointer",
    fontSize: typography.sizes.md,
    fontWeight: 400,
    color: colors.white,
    boxShadow: "0 2px 4px rgba(0,128,255,0.2)",
    transition: `all ${transitions.normal}`,
  },
  backBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: colors.white,
    border: `1px solid ${colors.gray200}`,
    borderRadius: radius.sm,
    padding: 10,
    cursor: "pointer",
    color: colors.black,
    transition: `all ${transitions.normal}`,
  },
  title: {
    fontSize: 20,
    fontWeight: 400,
    margin: 0,
    color: colors.black,
    fontFamily: typography.fontFamily,
  },
  subtitle: {
    fontSize: typography.sizes.base,
    color: colors.gray600,
    margin: "4px 0 0 0",
    fontFamily: typography.fontFamily,
  },
  filterSelect: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    background: colors.gray50,
    border: `1px solid ${colors.gray200}`,
    borderRadius: radius.sm,
    padding: "8px 12px",
  },
  selectInput: {
    border: "none",
    outline: "none",
    background: "transparent",
    fontSize: typography.sizes.base,
    fontWeight: 400,
    color: colors.black,
    cursor: "pointer",
    fontFamily: typography.fontFamily,
  },

  kpis: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: spacing[4],
    marginBottom: spacing[6],
  },
  kpiCard: {
    background: colors.bgCard,
    borderRadius: radius.md,
    padding: spacing[5],
    display: "flex",
    alignItems: "center",
    gap: spacing[4],
    boxShadow: shadows.sm,
  },
  kpiIconWrapper: {
    width: 44,
    height: 44,
    borderRadius: 10,
    background: `${colors.primary}1a`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  kpiContent: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  kpiLabel: {
    fontSize: typography.sizes.sm,
    color: colors.gray600,
    fontWeight: 400,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  kpiValue: {
    fontSize: 20,
    fontWeight: 400,
    color: colors.black,
  },

  table: {
    background: colors.bgCard,
    borderRadius: radius.md,
    boxShadow: shadows.sm,
    overflowX: "auto",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
  },
  thead: {
    display: "grid",
    gridTemplateColumns: GRILLA_GASTOS,
    gap: 0,
    padding: "10px 14px",
    background: colors.primary,
    color: colors.white,
    fontWeight: 400,
    fontSize: typography.sizes.xs,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    alignItems: "center",
  },
  theadDeleted: {
    display: "grid",
    gridTemplateColumns: "minmax(70px, 0.5fr) minmax(90px, 1fr) minmax(90px, 0.6fr) minmax(115px, 0.95fr) minmax(88px, 0.6fr) minmax(105px, 0.8fr) minmax(100px, 1.2fr) minmax(64px, 0.45fr)",
    gap: 0,
    padding: "10px 14px",
    background: colors.primary,
    color: colors.white,
    fontWeight: 400,
    fontSize: typography.sizes.xs,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    alignItems: "center",
  },
  headerCellWithMenu: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    position: "relative",
  },
  headerCellSimple: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  headerLabel: {
    textAlign: "center",
    whiteSpace: "nowrap",
  },
  headerActions: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: 2,
    transition: "opacity 0.15s ease",
  },
  headerIconBtn: {
    background: "transparent",
    border: "none",
    color: "rgba(255,255,255,0.95)",
    cursor: "pointer",
    padding: 2,
    display: "flex",
    alignItems: "center",
    borderRadius: 4,
  },
  headerMenu: {
    position: "absolute",
    top: "100%",
    left: 0,
    marginTop: 4,
    background: colors.white,
    color: colors.primary,
    border: `1px solid ${colors.primaryLighter}`,
    borderRadius: 6,
    boxShadow: "0 2px 8px rgba(0,128,255,0.12)",
    padding: 4,
    minWidth: 72,
    zIndex: 100,
  },
  headerMenuItem: {
    display: "block",
    width: "100%",
    textAlign: "center",
    padding: "6px 10px",
    background: "none",
    border: "none",
    color: colors.primary,
    cursor: "pointer",
    fontSize: typography.sizes.sm,
    fontWeight: 400,
    borderRadius: 4,
  },
  filterPopover: {
    position: "absolute",
    top: "100%",
    left: 0,
    marginTop: 4,
    background: colors.white,
    borderRadius: radius.sm,
    boxShadow: shadows.md,
    border: `1px solid ${colors.primaryLighter}`,
    padding: spacing[2],
    minWidth: 160,
    zIndex: 101,
  },
  filterPopoverInput: {
    width: "100%",
    padding: "6px 10px",
    fontSize: typography.sizes.sm,
    border: `1px solid ${colors.gray200}`,
    borderRadius: 6,
    outline: "none",
    color: colors.black,
  },
  trow: {
    display: "grid",
    gridTemplateColumns: GRILLA_GASTOS,
    padding: "10px 12px",
    borderTop: `1px solid ${colors.gray100}`,
    fontSize: typography.sizes.sm,
    alignItems: "center",
    background: colors.white,
    transition: `background ${transitions.normal}`,
  },
  trowDeleted: {
    display: "grid",
    gridTemplateColumns: "minmax(70px, 0.5fr) minmax(90px, 1fr) minmax(90px, 0.6fr) minmax(115px, 0.95fr) minmax(88px, 0.6fr) minmax(105px, 0.8fr) minmax(100px, 1.2fr) minmax(64px, 0.45fr)",
    padding: "10px 12px",
    borderTop: `1px solid ${colors.gray100}`,
    fontSize: typography.sizes.sm,
    alignItems: "center",
    background: colors.white,
    transition: `background ${transitions.normal}`,
  },
  tcell: {
    paddingRight: 6,
    minWidth: 0,
  },
  tcellWrap: {
    overflowWrap: "break-word",
    wordBreak: "break-word",
  },
  incidentBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "3px 8px",
    background: `${colors.warning}1f`,
    color: colors.warningDark,
    borderRadius: 4,
    fontSize: typography.sizes.xs,
    fontWeight: 400,
  },

  badge: {
    display: "inline-block",
    padding: "4px 8px",
    borderRadius: radius.full,
    background: colors.gray200,
    fontSize: typography.sizes.sm,
    color: colors.black,
    textAlign: "center",
  },
  badgeSuccess: {
    display: "inline-block",
    padding: "4px 8px",
    borderRadius: radius.full,
    background: colors.successLight,
    color: colors.successDark,
    fontSize: typography.sizes.sm,
    textAlign: "center",
  },
  badgeWarn: {
    display: "inline-block",
    padding: "4px 8px",
    borderRadius: radius.full,
    background: colors.errorLight,
    color: colors.errorDark,
    fontSize: typography.sizes.sm,
    textAlign: "center",
  },

  fabContainer: {
    position: "fixed",
    right: spacing[6],
    bottom: spacing[6],
    display: "flex",
    flexDirection: "column",
    gap: spacing[3],
  },
  fab: {
    width: 52,
    height: 52,
    display: "grid",
    placeItems: "center",
    borderRadius: "50%",
    background: colors.primary,
    color: colors.white,
    border: "none",
    boxShadow: "0 8px 20px rgba(0,128,255,0.35)",
    cursor: "pointer",
    transition: `all ${transitions.normal}`,
  },
  fabAI: {
    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
    boxShadow: "0 8px 20px rgba(0,128,255,0.35)",
  },

  // Estilos para carga con IA
  aiUploadZone: {
    gridColumn: "1 / -1",
    border: `2px dashed ${colors.gray300}`,
    borderRadius: radius.md,
    padding: 40,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: `all ${transitions.normal}`,
    background: colors.gray50,
  },
  aiFilePreview: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 12,
  },
  aiPreviewImage: {
    maxWidth: 200,
    maxHeight: 200,
    borderRadius: radius.sm,
    boxShadow: shadows.md,
  },
  aiPdfIcon: {
    width: 80,
    height: 80,
    borderRadius: radius.md,
    background: `${colors.primary}1a`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  aiFileName: {
    fontSize: typography.sizes.md,
    fontWeight: 400,
    color: colors.black,
    margin: 0,
  },
  aiChangeFile: {
    background: "transparent",
    border: `1px solid ${colors.gray300}`,
    borderRadius: 6,
    padding: "6px 12px",
    fontSize: typography.sizes.sm,
    color: colors.gray700,
    cursor: "pointer",
  },
  aiUploadText: {
    fontSize: typography.sizes.lg,
    color: colors.black,
    margin: "16px 0 4px 0",
  },
  aiUploadSubtext: {
    fontSize: typography.sizes.md,
    color: colors.gray600,
    margin: 0,
  },
  aiUploadFormats: {
    fontSize: typography.sizes.sm,
    color: colors.gray400,
    margin: "8px 0 0 0",
  },
  aiError: {
    gridColumn: "1 / -1",
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    padding: "12px 16px",
    background: colors.errorLight,
    borderRadius: radius.sm,
    color: colors.errorDark,
    fontSize: typography.sizes.md,
  },
  aiInfo: {
    gridColumn: "1 / -1",
    background: colors.primarySubtle,
    borderRadius: radius.sm,
    padding: spacing[4],
    marginTop: spacing[2],
  },
  aiInfoTitle: {
    fontSize: typography.sizes.md,
    fontWeight: 400,
    color: colors.black,
    margin: "0 0 12px 0",
  },
  aiInfoList: {
    margin: 0,
    paddingLeft: 20,
    fontSize: typography.sizes.base,
    color: colors.gray700,
    lineHeight: 1.8,
  },
  aiResultHeader: {
    gridColumn: "1 / -1",
    display: "flex",
    alignItems: "center",
    gap: spacing[3],
    padding: spacing[4],
    background: colors.successLight,
    borderRadius: radius.sm,
    marginBottom: spacing[2],
  },
  aiResultTitle: {
    fontSize: typography.sizes.lg,
    fontWeight: 400,
    color: colors.success,
  },
  aiResultGrid: {
    gridColumn: "1 / -1",
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: 16,
  },
  aiResultItem: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  aiResultLabel: {
    fontSize: typography.sizes.xs,
    fontWeight: 400,
    color: colors.gray600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  aiResultValue: {
    fontSize: typography.sizes.md,
    fontWeight: 400,
    color: colors.black,
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    flexWrap: "wrap",
  },
  aiBadgeSuccess: {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 8px",
    background: colors.successLight,
    color: colors.success,
    borderRadius: 4,
    fontSize: typography.sizes.xs,
    fontWeight: 400,
  },
  aiBadgeWarning: {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 8px",
    background: colors.warningLight,
    color: colors.warning,
    borderRadius: 4,
    fontSize: typography.sizes.xs,
    fontWeight: 400,
  },
  aiBadgeNew: {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 8px",
    background: `${colors.primary}1a`,
    color: colors.primary,
    borderRadius: 4,
    fontSize: typography.sizes.xs,
    fontWeight: 400,
  },

  // Estilos para sección de IA integrada
  aiSection: {
    margin: `0 ${spacing[4]}px`,
    padding: spacing[4],
    background: `linear-gradient(135deg, ${colors.primary}0d 0%, ${colors.primaryLight}0d 100%)`,
    borderRadius: radius.md,
    border: `1px solid ${colors.primary}26`,
  },
  aiSectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: spacing[3],
    marginBottom: spacing[3],
  },
  aiSectionIcon: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: `${colors.primary}1a`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  aiSectionTitle: {
    margin: 0,
    fontSize: typography.sizes.md,
    fontWeight: 400,
    color: colors.black,
  },
  aiSectionSubtitle: {
    margin: "2px 0 0 0",
    fontSize: typography.sizes.sm,
    color: colors.gray600,
  },
  aiSectionBody: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  aiUploadBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: spacing[2],
    padding: "12px 16px",
    background: colors.white,
    border: `2px dashed ${colors.gray300}`,
    borderRadius: radius.sm,
    cursor: "pointer",
    fontSize: typography.sizes.base,
    fontWeight: 400,
    color: colors.gray700,
    transition: `all ${transitions.normal}`,
  },
  aiFileRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 14px",
    background: colors.white,
    borderRadius: radius.sm,
    border: `1px solid ${colors.gray200}`,
  },
  aiFileInfo: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  aiFileNameSmall: {
    fontSize: typography.sizes.base,
    fontWeight: 400,
    color: colors.black,
  },
  aiFileActions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  aiProcessBtn: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 12px",
    background: colors.primary,
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: typography.sizes.sm,
    fontWeight: 400,
    color: colors.white,
  },
  aiCancelBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 28,
    height: 28,
    background: colors.gray100,
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    color: colors.gray600,
  },
  aiProcessingRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 14px",
    background: colors.white,
    borderRadius: radius.sm,
    border: `1px solid ${colors.gray200}`,
    fontSize: typography.sizes.base,
    color: colors.gray700,
  },
  aiSuccessRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 14px",
    background: colors.successLight,
    borderRadius: radius.sm,
    border: `1px solid ${colors.success}33`,
    fontSize: typography.sizes.base,
    fontWeight: 400,
    color: colors.success,
  },
  aiNewFileBtn: {
    marginLeft: "auto",
    padding: "4px 10px",
    background: "transparent",
    border: `1px solid ${colors.success}4d`,
    borderRadius: 4,
    cursor: "pointer",
    fontSize: typography.sizes.xs,
    fontWeight: 400,
    color: colors.success,
  },
  aiErrorRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 14px",
    background: colors.errorLight,
    borderRadius: radius.sm,
    border: `1px solid ${colors.error}33`,
    fontSize: typography.sizes.base,
    color: colors.errorDark,
  },
  aiRetryBtn: {
    marginLeft: "auto",
    padding: "4px 10px",
    background: "transparent",
    border: `1px solid ${colors.error}4d`,
    borderRadius: 4,
    cursor: "pointer",
    fontSize: typography.sizes.xs,
    fontWeight: 400,
    color: colors.errorDark,
  },
  aiWarningRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 14px",
    background: colors.warningLight,
    borderRadius: radius.sm,
    border: `1px solid ${colors.warning}`,
    fontSize: typography.sizes.base,
    color: colors.warningDark,
  },
  aiWarningRetryBtn: {
    marginLeft: "auto",
    padding: "4px 10px",
    background: "transparent",
    border: `1px solid ${colors.warningDark}`,
    borderRadius: 4,
    cursor: "pointer",
    fontSize: typography.sizes.xs,
    fontWeight: 400,
    color: colors.warningDark,
  },

  modalBody: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 12,
    padding: 16,
    overflowY: "auto",
    flex: 1,
    minHeight: 0,
  },
  formGroup: { display: "flex", flexDirection: "column", gap: 6 },
  label: { fontSize: typography.sizes.sm, color: colors.gray700, fontWeight: 400 },
  input: {
    height: 36,
    border: `1px solid ${colors.gray200}`,
    borderRadius: radius.sm,
    padding: "6px 10px",
    fontSize: typography.sizes.md,
    outline: "none",
  },
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    padding: spacing[4],
    borderTop: `1px solid ${colors.gray200}`,
    background: colors.gray50,
  },
  btnSecondary: {
    background: colors.white,
    border: `1px solid ${colors.gray300}`,
    borderRadius: radius.sm,
    padding: "8px 14px",
    cursor: "pointer",
    fontSize: typography.sizes.md,
    fontWeight: 400,
    color: colors.gray900,
  },
  btnPrimary: {
    background: colors.primary,
    border: "none",
    borderRadius: radius.sm,
    padding: "8px 16px",
    cursor: "pointer",
    fontSize: typography.sizes.md,
    fontWeight: 400,
    color: colors.white,
    boxShadow: "0 6px 16px rgba(0,128,255,0.35)",
  },

  emptyRow: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    padding: spacing[4],
    borderTop: `1px solid ${colors.gray200}`,
    color: colors.gray700,
    background: colors.white,
  },
  alert: {
    background: colors.warningLight,
    border: `1px solid ${colors.warning}`,
    color: colors.warningDark,
    padding: "12px 16px",
    borderRadius: radius.sm,
    marginBottom: spacing[4],
    fontSize: typography.sizes.md,
  },
  fileUpload: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  fileUploadLabel: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    padding: "8px 12px",
    background: colors.gray50,
    border: `1px solid ${colors.gray300}`,
    borderRadius: radius.sm,
    cursor: "pointer",
    fontSize: typography.sizes.md,
    transition: `all ${transitions.normal}`,
  },
  removeFile: {
    background: colors.errorLight,
    border: `1px solid ${colors.errorLight}`,
    color: colors.errorDark,
    borderRadius: 6,
    padding: 4,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  emptyState: {
    background: colors.bgCard,
    borderRadius: radius.md,
    padding: "64px 32px",
    textAlign: "center",
    boxShadow: shadows.sm,
  },
  emptyStateIcon: {
    marginBottom: spacing[5],
    opacity: 0.8,
  },
  emptyStateTitle: {
    fontSize: typography.sizes.xl,
    fontWeight: 400,
    color: colors.black,
    margin: "0 0 8px 0",
    fontFamily: typography.fontFamily,
  },
  emptyStateText: {
    fontSize: typography.sizes.md,
    color: colors.gray600,
    margin: 0,
    lineHeight: 1.6,
    maxWidth: 320,
    marginLeft: "auto",
    marginRight: "auto",
  },
  btnSmall: {
    padding: "4px 8px",
    borderRadius: "4px",
    border: "none",
    cursor: "pointer",
    fontSize: typography.sizes.sm,
    fontWeight: 400,
    transition: `all ${transitions.normal}`,
  },
  
  // Estilos para modal de decisión de proveedor
  supplierDecisionInfo: {
    display: "flex",
    alignItems: "flex-start",
    gap: 16,
    padding: 16,
    background: "rgba(245, 158, 11, 0.08)",
    borderRadius: 12,
    marginBottom: 16,
  },
  supplierDecisionIcon: {
    flexShrink: 0,
    marginTop: 2,
  },
  supplierDecisionText: {
    margin: 0,
    fontSize: typography.sizes.md,
    color: colors.black,
    lineHeight: 1.5,
  },
  supplierDecisionSubtext: {
    margin: "6px 0 0 0",
    fontSize: typography.sizes.base,
    color: colors.gray600,
  },
  supplierExtractedData: {
    padding: 14,
    background: colors.gray50,
    borderRadius: radius.sm,
    marginBottom: spacing[4],
  },
  supplierExtractedTitle: {
    margin: "0 0 8px 0",
    fontSize: typography.sizes.sm,
    fontWeight: 400,
    color: colors.gray700,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  supplierExtractedItem: {
    margin: "4px 0",
    fontSize: typography.sizes.base,
    color: colors.black,
  },
  supplierDecisionOptions: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    marginBottom: 16,
  },
  supplierDecisionBtnPrimary: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: spacing[2],
    width: "100%",
    padding: "14px 20px",
    background: colors.primary,
    border: "none",
    borderRadius: radius.sm,
    cursor: "pointer",
    fontSize: typography.sizes.md,
    fontWeight: 400,
    color: colors.white,
    transition: `all ${transitions.normal}`,
  },
  supplierDecisionBtnSecondary: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: spacing[2],
    width: "100%",
    padding: "14px 20px",
    background: colors.white,
    border: `1px solid ${colors.gray300}`,
    borderRadius: radius.sm,
    cursor: "pointer",
    fontSize: typography.sizes.md,
    fontWeight: 400,
    color: colors.gray900,
    transition: `all ${transitions.normal}`,
  },
  supplierDecisionHint: {
    margin: 0,
    fontSize: typography.sizes.sm,
    color: colors.gray600,
    textAlign: "center",
    lineHeight: 1.5,
  },
  showToClientOption: {
    padding: "14px",
    background: colors.gray50,
    borderRadius: radius.sm,
    marginBottom: spacing[4],
  },
  showToClientLabel: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
    fontSize: typography.sizes.md,
    fontWeight: 400,
    color: colors.black,
  },
  showToClientCheckbox: {
    width: 18,
    height: 18,
    cursor: "pointer",
    accentColor: colors.primary,
  },
  showToClientHint: {
    margin: "8px 0 0 28px",
    fontSize: typography.sizes.sm,
    color: colors.gray600,
    lineHeight: 1.4,
  },
};

export default PagosEdificio;
